import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { saveAs } from 'file-saver';
// @mui
import {
  ThemeProvider,
  createTheme,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Container,
  Autocomplete,
  IconButton,
  FormControlLabel,
  FormControl,
  Checkbox,
  ListItem,
  ListItemText,
  List,
  Divider,
  Tooltip,
  CircularProgress,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptLocale from 'date-fns/locale/pt-BR';

// Icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SaveIcon from '@mui/icons-material/Save';
import BeenhereOutlinedIcon from '@mui/icons-material/BeenhereOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import SpeedIcon from '@mui/icons-material/Speed';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TuneIcon from '@mui/icons-material/Tune';
import WidgetsIcon from '@mui/icons-material/Widgets';
import GridOnIcon from '@mui/icons-material/GridOn';
import StoreIcon from '@mui/icons-material/Store';
import MemoryIcon from '@mui/icons-material/Memory';
import PersonIcon from '@mui/icons-material/Person';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import DescriptionIcon from '@mui/icons-material/Description';
import TerrainIcon from '@mui/icons-material/Terrain';
import PowerIcon from '@mui/icons-material/Power';
import OpacityIcon from '@mui/icons-material/Opacity';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Brightness3Icon from '@mui/icons-material/Brightness3';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExploreIcon from '@mui/icons-material/Explore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FlagIcon from '@mui/icons-material/Flag';
import BuildIcon from '@mui/icons-material/Build';

// Stepper
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Check from '@mui/icons-material/Check';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { AutoCompleteMedium, AutoComplete, labelCampoEscuro, cores, coresHex } from '../styles/styleUtils';

import { inicializarIndicesSeguro, calcularValoresSeguro, formatarMoeda } from '../utils/seguroUtils';

// api
import {
  getProdutos,
  getClientes,
  getFluxogramas,
  getKits,
  getOrientacaoTelhados,
  getTipoDisjuntores,
  getPotenciaDisjuntores,
  getModulos,
  getAnexos,
  getUsuarios,
  getFornecedores,
  getUnoAnexo,
  getSuperficie,
  getIndicadores,
  getGenericData,
  getFormasPagamento,
  getIndices,
  getHsp,
  getHspIbge,
  getEstados,
  getMunicipios,
  getKitsOffGrid,
  sugerirKitOffGrid,
  updateKit,
} from './api';
import { enviaMensagem } from './apiWpp';

// PDF
import generatePdf from './EditPdf';
import generatePdfBombeamento from './EditPdfBombeamento';
import generatePdfOffGrid from './EditPdfOffGrid';
import PreviewProducaoBombeamento from './PreviewProducaoBombeamento';
import PreviewProducao from './PreviewProducao';
import generatePdfLimpeza from './EditPdfLimpeza';
import generatePdfServicos from './EditPdfServicos';
import generatePdfSeguro from './EditPdfSeguro';

import ModalAddCliente from './ModalAddCliente';
import ModalMensagem from './ModalMensagem';
import ModalLoading from './ModalLoading';
import ModalAddIndicador from './ModalAddIndicador';
import ModalAtividadade from './ModalAtividade';
import ModalAddAgenda from './ModalAddAgenda';
import ModalPropostaPersonalizada from './ModalPropostaPersonalizada';
import DimensionamentoBombeamento from './DimensionamentoBombeamento';
import DimensionamentoOffGrid from './DimensionamentoOffGrid';
import AddKit from './AddKit';
import AddKitOffGrid from './AddKitOffGrid';
import AddKitBombeamento from './AddKitBombeamento';
import AlertaAdequacaoKit from './AlertaAdequacaoKit';
import { classificarAdequacaoKit, potenciaReferenciaBombeamento, buscarAlternativasKit } from '../utils/adequacaoKit';
import { identificarKitOnGrid } from './DimensionamentoOnGrid';
import { prDaProposta } from '../utils/performanceRatio';
import { ICONE_POR_PRODUTO, ICONE_PADRAO } from './IconesProduto';
import CamposInstalacao from './CamposInstalacao';

import { useUnidades, isSameUnidade } from '../hooks/useUnidades';


// Modal ---------------------------------
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  height: '98vh',
  '@media screen and (max-width: 600px)': {
    width: '100vw', // Width de 100% para telas de celulares
    height: '95vh',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`,
  borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg,${cores.painelTopo} 0%,${cores.painelBase} 100%)`,
};

const styleTextField = {
  ...labelCampoEscuro,
  backgroundColor: 'white',
  width: '30vw',
  '@media screen and (max-width: 600px)': {
    width: '70vw', // Width de 100% para telas de celulares
  },
};

const styleTextField2 = {
  ...labelCampoEscuro,
  backgroundColor: 'white',
  width: '14vw',
  '@media screen and (max-width: 600px)': {
    width: '34vw', // Width de 100% para telas de celulares
  },
};

const styleTextField3 = {
  ...labelCampoEscuro,
  backgroundColor: 'white',
  // Preenche a caixa do input de branco (inclusive desabilitado) + texto escuro legivel.
  // Corrige o efeito bicolor (branco no topo, escuro embaixo) em outlined disabled.
  '& .MuiInputBase-root': { backgroundColor: 'white' },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' },
  width: '15vw',
  '@media screen and (max-width: 600px)': {
    width: '30vw', // Width de 100% para telas de celulares
  },
};

const styleTextField4 = {
  ...labelCampoEscuro,
  backgroundColor: 'white',
  '& .MuiInputBase-root': { backgroundColor: 'white' },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' },
  width: '20vw',
  '@media screen and (max-width: 600px)': {
    width: '30vw', // Width de 100% para telas de celulares
    fontSize: '6px',
  },
};

const sxAddGrupoFatura = {
  width: 100,
};

const sxFontBotaoMobile = {
  '@media screen and (max-width: 600px)': {
    fontSize: 10,
  },
};

// Modal ---------------------------------

const IconButtonWrapper = styled('div')({
  marginLeft: '40px', // Adjust the margin as needed
  display: 'flex',
  alignItems: 'center',
});

// Stepper ------------------------------------

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--marca-primaria)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--marca-primaria)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: 'var(--marca-primaria)',
  }),
  '& .QontoStepIcon-completedIcon': {
    color: 'var(--marca-primaria)',
    zIndex: 1,
    fontSize: 18,
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? <Check className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
    </QontoStepIconRoot>
  );
}

QontoStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
};

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <SettingsIcon />,
    2: <GroupAddIcon />,
    3: <VideoLabelIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}
// Stepper ------------------------------------

ColorlibStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};
// Stepper ------------------------------------
const finalizado = [
  { nome: 'Aprovado', data: new Date() },
  { nome: 'Rejeitado', data: new Date() },
];

const finalizadoLead = [
  { nome: 'Lead Qualificado', data: new Date() },
  { nome: 'Lead Desqualificado', data: new Date() },
];
const origem = [{ nome: 'Prospecção' }, { nome: 'Agendamento' }, { nome: 'Indicação' }];
const tipoindicacao = [
  { nome: 'Indicação Simples', valor: 150 },
  { nome: 'Programa de Indicação', valor: 500 },
  { nome: 'Indicador PJ' },
];
const percentualindicacao = [1, 2, 3, 4];
const distribuidoras = [
  'CEMIG', 'CPFL Energia', 'Energisa', 'Enel (SP)', 'Enel (CE)', 'Enel (GO)',
  'Equatorial Energia', 'Coelba', 'Celpe', 'Cosern', 'Celg-D', 'Coelce',
  'Elektro', 'EDP São Paulo', 'EDP Espírito Santo', 'Celesc', 'RGE Sul',
  'Cooperativas / SICREDI', 'Roraima Energia', 'Amazonas Energia', 'Outra',
];

// const produtos = await getProdutos();
// const rowsClientes = await getClientes();
// const fluxogramas1 = await getFluxogramas();
// const steps = fluxogramas1[0].etapas
// const orientacaotelhado = await getOrientacaoTelhados()
// const tipodisjuntor = await getTipoDisjuntores()
// const potenciadisjuntor = await getPotenciaDisjuntores()
// const modulos = await getModulos()
// const kits = await getKits()

// usado somente para tela de finalizacao, quando animar coloca no resto, tela de revisao e pdf
const anos = [
  { id: 1, nome: '1 Ano' },
  { id: 2, nome: '2 Anos' },
  { id: 3, nome: '3 Anos' },
  { id: 4, nome: '4 Anos' },
  { id: 5, nome: '5 Anos' },
];
const parcelas = [
  { id: 1, nome: 'A Vista' },
  { id: 2, nome: '2 Vezes' },
  { id: 3, nome: '3 Vezes' },
  { id: 4, nome: '4 Vezes' },
];

const parcelasLimpeza = [
  { id: 1, nome: 'A Vista', indice: 0.9 },
  { id: 2, nome: '2 Vezes', indice: 1 },
  { id: 3, nome: '3 Vezes', indice: 1 },
];

const superficieServicos = ['Telhado', 'Solo', 'Não Aplicável'];

// ----------------

// ----------------------------------------------------------------------
// eslint-disable-next-line
const EditProposta = React.forwardRef((props, ref) => {
  const user = JSON.parse(localStorage.getItem('userData'));
  const { unidades } = useUnidades();
  const [dataFetched, setDataFetched] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [rowsClientes, setRowsClientes] = useState([]);
  const [rowsIndicador, setRowsIndicador] = useState([]);
  const [fluxogramas, setFluxogramas] = useState([]);
  const [steps, setSteps] = useState([]);
  const [orientacaotelhado, setOrientacaotelhado] = useState([]);
  const [tipodisjuntor, setTipodisjuntor] = useState([]);
  const [potenciadisjuntor, setPotenciadisjuntor] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Filtro comercial do inversor disponível para a potência de kit calculada.
  // Mantemos proposta.topologia como campo persistido por compatibilidade com
  // propostas antigas; o conteúdo continua sendo o objeto do inversor escolhido.
  const [inversoresDisponiveis, setInversoresDisponiveis] = useState([]);
  const [inversorSelecionado, setInversorSelecionado] = useState(
    props.proposta.topologia ? props.proposta.topologia : null
  );
  const [modalLoading, setModalLoading] = useState(false);
  const [superficies, setSuperficies] = useState([]);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [duracao, setDuracao] = useState(12);
  // CORRIGIDO (deployment Marcki — valor de serviço travado): o valor
  // cadastrado no catálogo (tela Serviços) deve ser só um ponto de partida,
  // não um valor fechado. Este estado guarda o valor efetivamente usado na
  // proposta, editável pelo usuário, pré-preenchido com o valor do catálogo
  // quando um serviço é selecionado.
  const [valorUnitario, setValorUnitario] = useState(0);
  const [servicos, setServicos] = useState([]);
  const [modalPersonalizado, setModalPersonalizado] = useState(null)
  const [modalPersonalizado2, setModalPersonalizado2] = useState(null)
  const [tipoDesconto, setTipoDesconto] = useState('%');
  const [tipoAcrescimo, setTipoAcrescimo] = useState('%');
  const [modalTermoAcrescimo, setModalTermoAcrescimo] = useState(false);
  const [termoAcrescimoAceito, setTermoAcrescimoAceito] = useState(props.proposta?.acrescimoAceito || false);

  const [motivos, setMotivos] = useState([]);
  useEffect(() => {
    const getMotivos = async () => {
      const result = await getGenericData('motivorejeicao', {});
      setMotivos(result);
    };
    getMotivos();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    setModalLoading(true);
    try {
      console.log('1 produtos');
      const produtosData = await getProdutos();
      console.log('2 clientes');
      const clientesData = await getClientes();
      console.log('3 fluxogramas');
      const fluxogramasData = await getFluxogramas();
      console.log('4 orientacao');
      const orientacaoData = await getOrientacaoTelhados();
      console.log('5 disjuntores');
      const tipodisjuntorData = await getTipoDisjuntores();
      console.log('6 potencia');
      const potenciaData = await getPotenciaDisjuntores();
      console.log('7 superficies');
      const superficiesData = await getSuperficie();
      console.log('8 indicadores');
      const indicadores = await getIndicadores();
      console.log('9 servicos');
      const servicosData = await getServicos();
      console.log('10 seguro');
      try { await inicializarIndicesSeguro(); } catch(e) { console.warn('seguro:', e); }
      console.log('11 setando dados');
      // Esconde produtos inativos (ex.: Grid-Zero) do seletor, mas ainda resolve
      // o produto de propostas antigas a partir da lista completa.
      const produtosAtivos = produtosData.filter((p) => p.ativo !== false);
      setProdutos(produtosAtivos);
      // Se a proposta já tem produto selecionado, usa ele; senão usa o primeiro da lista
      const produtoInicial = props.proposta?.produto?._id
        ? produtosData.find(p => String(p._id) === String(props.proposta.produto._id)) || produtosAtivos[0]
        : produtosAtivos[0];
      setProdutoSelecionado(produtoInicial);
      setRowsClientes(clientesData);
      setFluxogramas(fluxogramasData);
      // Personalizado e Serviços têm steppers próprios (useEffects dedicados). Não
      // sobrescrever aqui com os passos do fluxograma comercial — senão o fetchData
      // (assíncrono) ganha a corrida e o stepper volta pros 6 passos do On-Grid.
      if (
        proposta.produto?.nome !== 'Personalizado' &&
        proposta.produto?.nome !== 'Serviços' &&
        proposta.produto?.nome !== 'Proposta de Serviços'
      ) {
        setSteps(fluxogramasData.find((value) => value.id === (proposta.fluxo.id === 10 ? 10 : 1)).etapas);
      }
      setOrientacaotelhado(orientacaoData);
      setTipodisjuntor(tipodisjuntorData);
      setPotenciadisjuntor(potenciaData);
      setSuperficies(superficiesData);
      setRowsIndicador(indicadores);
      setServicos(servicosData);
      setDataFetched(true);
      console.log('12 concluido');
    } catch(e) {
      console.error('Erro:', e);
    } finally {
      console.log('13 finally - fechando loading');
      setModalLoading(false);
    }
  };

  const getServicos = async () => {
    const result = await getGenericData('servico', { ativo: true });
    return result;
  };

  useEffect(() => {
    fectchUsuarios();
  }, []);
  const fectchUsuarios = async () => {
    const resultado = await getUsuarios();
    setUsuarios(resultado);
  };

  const [kits, setKits] = useState([]);

  const [erro, setErro] = useState(false);
  const [erro2, setErro2] = useState(false);

  const [produtoSelecionado, setProdutoSelecionado] = useState(null);

  // const [rowsClientes, setRowsClientes] = useState(mock.clientes)

  const [proposta, setProposta] = useState(props.proposta);

  const [anexo, setAnexo] = useState([]);

  // const [steps, setSteps] = useState(mock.fluxogramas[0].etapas || null)

  // Estados e Cidades -----------------------
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [propostaGerada, setPropostaGerada] = useState(false);
  const [modalAddCliente, setModalAddCliente] = useState(false);
  const [modalAddIndicador, setModalAddIndicador] = useState(false);
  const [modalMensagem, setModalMensagem] = useState(false);
  const [mensagem, setMensagem] = useState(null);
  const [destino, setDestino] = useState(null);

  // ── Kits de bombeamento para a etapa de Revisão ───────────────────────────
  const [kitsBombeamento, setKitsBombeamento] = useState([]);
  const [loadingKitsBomb, setLoadingKitsBomb] = useState(false);

  // Cadastro contextual de kits a partir do dimensionamento
  const [modalKitContextual, setModalKitContextual] = useState(false);
  const [tipoKitContextual, setTipoKitContextual] = useState(null);
  const [contextoKitContextual, setContextoKitContextual] = useState(null);
  const [adequacaoOnGrid, setAdequacaoOnGrid] = useState(null);
  const [adequacaoOffGrid, setAdequacaoOffGrid] = useState(null);
  const [adequacaoBombeamento, setAdequacaoBombeamento] = useState(null);

  // ── Off-Grid: dados do dimensionamento, kits e gráficos ───────────────────
  const [dimOffGrid, setDimOffGrid] = useState(
    props.proposta?.dimensionamentoOffGrid?.mediaMensal
      ? props.proposta.dimensionamentoOffGrid
      : null
  );
  const [kitsOffGrid,        setKitsOffGrid]         = useState([]);
  const [loadingKitsOG,      setLoadingKitsOG]       = useState(false);
  const [kitSugeridoOG,      setKitSugeridoOG]       = useState(null);
  const [kitSugeridoOGParcial, setKitSugeridoOGParcial] = useState(false);
  const [kitManualOG,        setKitManualOG]         = useState(null);
  const [filtroFornecedorOG, setFiltroFornecedorOG]  = useState(null);
  const [filtroModuloOG,     setFiltroModuloOG]      = useState(null);
  const [filtroBateriaOG,    setFiltroBateriaOG]     = useState(null);
  const [descontoOG,         setDescontoOG]          = useState('');
  const [acrescimoOG,        setAcrescimoOG]         = useState('');
  const [tipoDescontoOG,     setTipoDescontoOG]      = useState('%');
  const [tipoAcrescimoOG,    setTipoAcrescimoOG]     = useState('%');
  const [termoAbertoOG,      setTermoAbertoOG]       = useState(false);
  const [acrescimoAceitoOG,  setAcrescimoAceitoOG]   = useState(false);
  const [producaoMesesOG,    setProducaoMesesOG]      = useState([]);
  const [producaoAnualOG,    setProducaoAnualOG]      = useState([]);
  const [depPrimeiroOG,      setDepPrimeiroOG]        = useState(0.007);
  const [depDemaisOG,        setDepDemaisOG]          = useState(0.005);

  useEffect(() => {
    setProposta(props.proposta);
    setTermoAcrescimoAceito(props.proposta?.acrescimoAceito || false);
  }, [props.proposta]);

  useEffect(() => {
    fetchKits();
  }, [props.proposta, proposta.fornecedor]);

  const fetchKits = async () => {
    const resultado = await getKits();
    const lista = Array.isArray(resultado) ? resultado : [];
    // Mantém todo o catálogo On-Grid disponível para a busca ampliada.
    // O DimensionamentoOnGrid continua filtrando fornecedor + módulo para a busca principal.
    setKits(lista.filter((kit) => kit.tipokat !== 'bombeamento' && kit.tipokat !== 'offgrid'));
  };

  // ── Off-Grid: carrega kits e sugere ao entrar na etapa 2 (Revisão) ──────────
  useEffect(() => {
    if (proposta.produto?.nome !== 'Gerador Off-Grid') return;
    if (proposta.etapa?.id !== 2) return;
    if (!dimOffGrid) return;
    const carregar = async () => {
      setLoadingKitsOG(true);
      try {
        const lista = await getKitsOffGrid();
        setKitsOffGrid(Array.isArray(lista) ? lista : []);
        const res = await sugerirKitOffGrid(
          dimOffGrid.potenciaCC,
          dimOffGrid.autonomia,
          dimOffGrid.dod,
          dimOffGrid.rte,
        );
        if (res?.resultado) {
          setKitSugeridoOG(res.resultado);
          setKitSugeridoOGParcial(Boolean(res.parcial));
        } else {
          setKitSugeridoOG(null);
          setKitSugeridoOGParcial(false);
        }
      } catch(e) { /* usa estado vazio */ }
      setLoadingKitsOG(false);
    };
    carregar();
  }, [proposta.produto?.nome, proposta.etapa?.id, dimOffGrid]);

  useEffect(() => {
    if (proposta.produto?.nome !== 'Gerador Off-Grid' || proposta.etapa?.id !== 2 || !dimOffGrid) {
      setAdequacaoOffGrid(null);
      return;
    }
    const kitEfetivo = kitManualOG || kitSugeridoOG;
    setAdequacaoOffGrid(classificarAdequacaoKit({
      potenciaNecessaria: dimOffGrid.potenciaCC,
      potenciaKit: kitEfetivo?.potenciacc,
      compatibilidadeParcial: !kitManualOG && kitSugeridoOGParcial,
      semKit: !kitEfetivo,
    }));
  }, [
    proposta.produto?.nome,
    proposta.etapa?.id,
    dimOffGrid,
    kitManualOG,
    kitSugeridoOG,
    kitSugeridoOGParcial,
  ]);

  // ── Off-Grid: calcula gráficos ao entrar na etapa 3 (Preview) ───────────────
  useEffect(() => {
    if (proposta.produto?.nome !== 'Gerador Off-Grid') return;
    if (proposta.etapa?.id !== 3) return;
    if (!dimOffGrid || !proposta.kit) return;
    const TARIFA = 1.12;
    const pr = prDaProposta({ ...proposta, dimensionamentoOffGrid: dimOffGrid }, 'offgrid');
    const DIAS_MES = 30;
    const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const CHAVES_HSP = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun','hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];
    const hsp = dimOffGrid.hsp;
    const potCC = proposta.kit.potenciacc || dimOffGrid.potenciaCC;
    const fatorOrient = dimOffGrid.fatorOrientacao;
    // Gráfico mensal
    const meses = CHAVES_HSP.map((chave, i) => {
      const hspMes   = hsp[chave] || 0;
      const producao = Number((potCC * hspMes * DIAS_MES * pr * fatorOrient).toFixed(2));
      const economia = Number((producao * TARIFA).toFixed(2));
      return { mes: MESES[i], producao, economia };
    });
    setProducaoMesesOG(meses);
    // Gráfico anual (25 anos)
    const anoBase = new Date().getFullYear();
    const anos = [];
    let prod = potCC * dimOffGrid.hspMedio * DIAS_MES * pr * fatorOrient * 12;
    for (let i = 0; i < 25; i += 1) {
      anos.push({ ano: anoBase + i, producao: Number(prod.toFixed(2)) });
      const dep = i === 0 ? (depPrimeiroOG || 0) : (depDemaisOG || 0);
      prod *= (1 - dep);
    }
    setProducaoAnualOG(anos);
  }, [proposta.produto?.nome, proposta.etapa?.id, proposta.kit, proposta.pr, dimOffGrid]);

  // ── Personalizado: stepper proprio de 5 etapas ────────────────────────────────
  // O conteudo das 5 etapas JA existe (etapa.id 0..4: Cadastro, Dimensionamento,
  // Revisao, Proposta, Finalizacao). O bug era so o rotulo: o stepper herdava os 6
  // passos do fluxograma "Proposta Comercial" (com Preview/Gerar Proposta). Aqui
  // forcamos os 5 rotulos corretos quando o produto e Personalizado.
  useEffect(() => {
    if (proposta.produto?.nome !== 'Personalizado') return;
    setSteps([
      { id: 0, nome: 'Cadastro' },
      { id: 1, nome: 'Dimensionamento' },
      { id: 2, nome: 'Revisão' },
      { id: 3, nome: 'Proposta' },
      { id: 4, nome: 'Finalização' },
    ]);
  }, [proposta.produto?.nome]);

  // ── Serviços: stepper próprio de 5 etapas ────────────────────────────────────
  useEffect(() => {
    if (proposta.produto?.nome !== 'Serviços' && proposta.produto?.nome !== 'Proposta de Serviços') return;
    setSteps([
      { id: 0, nome: 'Cadastro' },
      { id: 1, nome: 'Descrição dos Serviços' },
      { id: 2, nome: 'Revisão' },
      { id: 3, nome: 'Gerar Proposta' },
      { id: 4, nome: 'Finalização' },
    ]);
  }, [proposta.produto?.nome]);

  // ── Off-Grid: carregar depreciação dos índices ───────────────────────────────
  useEffect(() => {
    const carregar = async () => {
      try {
        const indices = await getIndices();
        const dp1 = indices.find(i => i.nome === 'Depreciação 1º Ano (Módulo)');
        const dpD = indices.find(i => i.nome === 'Depreciação Demais Anos (Módulo)');
        if (dp1?.valor) setDepPrimeiroOG(dp1.valor);
        if (dpD?.valor) setDepDemaisOG(dpD.valor);
      } catch(e) { /* usa padrões */ }
    };
    carregar();
  }, []);

  const alternativasOnGrid = buscarAlternativasKit({
    kits,
    potenciaNecessaria: adequacaoOnGrid?.potenciaNecessaria || proposta.potCCNecessaria,
    fornecedorAtual: proposta.fornecedor,
    moduloAtual: proposta.modulo,
    tipo: 'ongrid',
  }).filter((kit) => String(kit._id) !== String(proposta.kit?._id || ''));

  const alternativasBombeamento = buscarAlternativasKit({
    kits: kitsBombeamento,
    potenciaNecessaria: potenciaReferenciaBombeamento(proposta.dimensionamentoBomb),
    fornecedorAtual: proposta.fornecedor,
    moduloAtual: proposta.modulo,
    tipo: 'bombeamento',
    potenciaMotorCV: Number(proposta.dadosBombeamento?.potenciaMotorCV || 0),
  }).filter((kit) => String(kit._id) !== String(proposta.kit?._id || ''));

  const alternativasOffGrid = buscarAlternativasKit({
    kits: kitsOffGrid,
    potenciaNecessaria: Number(dimOffGrid?.potenciaCC || 0),
    fornecedorAtual: filtroFornecedorOG || (kitManualOG || kitSugeridoOG)?.fornecedor,
    moduloAtual: filtroModuloOG || (kitManualOG || kitSugeridoOG)?.modulo,
    tipo: 'offgrid',
    autonomiaNecessaria: Number(dimOffGrid?.autonomia || 0),
    dod: Number(dimOffGrid?.dod || 0.9),
    rte: Number(dimOffGrid?.rte || 0.9),
  }).filter((kit) => String(kit._id) !== String((kitManualOG || kitSugeridoOG)?._id || ''));

  const usarAlternativaOnGrid = (kit) => {
    setInversorSelecionado(kit.inversor || null);
    setProposta((prev) => ({
      ...prev,
      fornecedor: kit.fornecedor || prev.fornecedor,
      modulo: kit.modulo || prev.modulo,
      topologia: kit.inversor || prev.topologia,
      kit,
      valorkit: kit.precovenda || 0,
      valor: kit.precovenda || 0,
    }));
  };

  const usarAlternativaBombeamento = (kit) => {
    setProposta((prev) => ({
      ...prev,
      fornecedor: kit.fornecedor || prev.fornecedor,
      modulo: kit.modulo || prev.modulo,
      kit,
      valorkit: kit.precovenda || 0,
      valor: kit.precovenda || 0,
    }));
  };

  const usarAlternativaOffGrid = (kit) => {
    setFiltroFornecedorOG(kit.fornecedor || null);
    setFiltroModuloOG(kit.modulo || null);
    setFiltroBateriaOG(kit.bateria || null);
    setKitManualOG(kit);
    setKitSugeridoOG(null);
    setKitSugeridoOGParcial(false);
    setProposta((prev) => ({
      ...prev,
      fornecedor: kit.fornecedor || prev.fornecedor,
      modulo: kit.modulo || prev.modulo,
      kit,
      valorkit: kit.precovenda || 0,
      valor: kit.precovenda || 0,
    }));
  };

  const montarContextoCadastroKit = (tipo, adequacao) => {
    const base = {
      potenciaNecessaria: adequacao?.potenciaNecessaria || proposta.potCCNecessaria || 0,
      fornecedor: proposta.fornecedor || null,
      modulo: proposta.modulo || proposta.kit?.modulo || null,
      inversor: proposta.topologia || proposta.kit?.inversor || null,
      bateria: proposta.kit?.bateria || null,
      estadoGeracao: proposta.localinstalacao?.estado || null,
      cidadeGeracao: proposta.localinstalacao?.cidade || null,
      hspMedia:
        proposta.hsp?.hspMedia ||
        proposta.dimensionamentoOffGrid?.hspMedio ||
        proposta.dimensionamentoBomb?.hspMin ||
        0,
    };

    if (tipo === 'offgrid') {
      const referencia = kitManualOG || kitSugeridoOG;
      return {
        ...base,
        potenciaNecessaria: Number(dimOffGrid?.potenciaCC || 0),
        autonomiaNecessaria: Number(dimOffGrid?.autonomia || 0),
        fornecedor: filtroFornecedorOG || referencia?.fornecedor || base.fornecedor,
        modulo: filtroModuloOG || referencia?.modulo || base.modulo,
        inversor: referencia?.inversor || base.inversor,
        bateria: filtroBateriaOG || referencia?.bateria || base.bateria,
      };
    }

    if (tipo === 'bombeamento') {
      return {
        ...base,
        potenciaNecessaria: potenciaReferenciaBombeamento(proposta.dimensionamentoBomb),
        inversor: proposta.kit?.inversor || base.inversor,
      };
    }

    return base;
  };

  const abrirCadastroKitContextual = (tipo, adequacao) => {
    setTipoKitContextual(tipo);
    setContextoKitContextual(montarContextoCadastroKit(tipo, adequacao));
    setModalKitContextual(true);
  };

  const fecharCadastroKitContextual = () => {
    setModalKitContextual(false);
    setTipoKitContextual(null);
    setContextoKitContextual(null);
  };

  const recarregarKitsOffGrid = async () => {
    if (!dimOffGrid) return;
    setLoadingKitsOG(true);
    try {
      const lista = await getKitsOffGrid();
      setKitsOffGrid(Array.isArray(lista) ? lista : []);
      const res = await sugerirKitOffGrid(
        dimOffGrid.potenciaCC,
        dimOffGrid.autonomia,
        dimOffGrid.dod,
        dimOffGrid.rte,
      );
      if (res?.resultado) {
        setKitSugeridoOG(res.resultado);
        setKitSugeridoOGParcial(Boolean(res.parcial));
        setKitManualOG(null);
      } else {
        setKitSugeridoOG(null);
        setKitSugeridoOGParcial(false);
        setKitManualOG(null);
      }
    } finally {
      setLoadingKitsOG(false);
    }
  };

  const confirmarKitContextual = async (produto) => {
    const tipoAtual = tipoKitContextual;
    const payload = {
      ...produto,
      ...(tipoAtual === 'bombeamento' ? { tipokat: 'bombeamento' } : {}),
    };
    const result = await updateKit(payload);
    window.alert(result?.msg || (result?.ok ? 'Kit salvo com sucesso.' : 'Não foi possível salvar o kit.'));
    if (!result?.ok) return;

    fecharCadastroKitContextual();

    if (tipoAtual === 'bombeamento') {
      const lista = await getKits({ tipokat: 'bombeamento' });
      setKitsBombeamento(Array.isArray(lista) ? lista : []);
      setProposta((prev) => ({ ...prev, kit: null, valorkit: 0, valor: 0 }));
    } else {
      await fetchKits();
    }
  };

  const normalizarPotenciaKit = (valor) => {
    if (typeof valor === 'number') return valor;
    if (typeof valor === 'string') {
      const numero = Number(valor.replace(',', '.').trim());
      return Number.isFinite(numero) ? numero : NaN;
    }
    return NaN;
  };

  useEffect(() => {
    if (proposta.produto?.nome !== 'Sistema de Bombeamento') return;
    if (proposta.etapa?.id !== 2) return;
    const carregarKitsBomb = async () => {
      setLoadingKitsBomb(true);
      // Filtra direto no backend por tipokat para garantir apenas kits de bombeamento
      const filtrados = await getKits({ tipokat: 'bombeamento' });
      const lista = Array.isArray(filtrados) ? filtrados : [];
      setKitsBombeamento(lista);
      // Seleciona automaticamente o kit mais adequado se ainda não há kit
      if (!proposta.kit && proposta.dimensionamentoBomb?.potCC) {
        const fornId = proposta.fornecedor?._id;
        const modId  = proposta.modulo?._id;
        let candidatos = filtrados;
        if (fornId) candidatos = candidatos.filter((k) => k.fornecedor?._id === fornId);
        if (modId)  candidatos = candidatos.filter((k) => k.modulo?._id === modId);
        const kwpNec = potenciaReferenciaBombeamento(proposta.dimensionamentoBomb);
        const sorted = [...candidatos]
          .map((k) => ({ ...k, _potenciaNormalizada: normalizarPotenciaKit(k.potenciacc) }))
          .filter((k) => Number.isFinite(k._potenciaNormalizada))
          .sort((a, b) => a._potenciaNormalizada - b._potenciaNormalizada);
        const adequados = sorted.filter((k) => k._potenciaNormalizada >= kwpNec);
        const kitAuto = adequados.length > 0 ? adequados[0] : sorted[sorted.length - 1];
        if (kitAuto) {
          const { _potenciaNormalizada, ...kitLimpo } = kitAuto;
          setProposta((prev) => ({
            ...prev,
            kit:      kitLimpo,
            valorkit: kitLimpo.precovenda || 0,
            valor:    kitLimpo.precovenda || 0,
            modulo:   kitLimpo.modulo || prev.modulo,
          }));
        }
      }
      setLoadingKitsBomb(false);
    };
    carregarKitsBomb();
  }, [proposta.produto?.nome, proposta.etapa?.id, proposta.fornecedor?._id, proposta.modulo?._id]);

  // Re-seleciona kit quando fornecedor ou módulo mudam na Revisão de bombeamento
  useEffect(() => {
    if (proposta.produto?.nome !== 'Sistema de Bombeamento') return;
    if (proposta.etapa?.id !== 2) return;
    if (kitsBombeamento.length === 0) return;
    const fornId = proposta.fornecedor?._id;
    const modId  = proposta.modulo?._id;
    let candidatos = kitsBombeamento;
    if (fornId) candidatos = candidatos.filter((k) => k.fornecedor?._id === fornId);
    if (modId)  candidatos = candidatos.filter((k) => k.modulo?._id === modId);
    // Usa potCC como critério — potência CC necessária para acionar a bomba
    const potCCNec = potenciaReferenciaBombeamento(proposta.dimensionamentoBomb);
    const sorted = [...candidatos]
      .map((k) => ({ ...k, _potenciaNormalizada: normalizarPotenciaKit(k.potenciacc) }))
      .filter((k) => Number.isFinite(k._potenciaNormalizada))
      .sort((a, b) => a._potenciaNormalizada - b._potenciaNormalizada);
    const adequados = sorted.filter((k) => k._potenciaNormalizada >= potCCNec);
    const kitAuto = adequados.length > 0 ? adequados[0] : sorted[sorted.length - 1];
    if (kitAuto && kitAuto._id !== proposta.kit?._id) {
      const { _potenciaNormalizada, ...kitLimpo } = kitAuto;
      setProposta((prev) => ({
        ...prev,
        kit:      kitLimpo,
        valorkit: kitLimpo.precovenda || 0,
        valor:    kitLimpo.precovenda || 0,
        modulo:   kitLimpo.modulo || prev.modulo,
      }));
    }
  }, [proposta.fornecedor?._id, proposta.modulo?._id, kitsBombeamento]);

  useEffect(() => {
    if (proposta.produto?.nome !== 'Sistema de Bombeamento' || proposta.etapa?.id !== 2) {
      setAdequacaoBombeamento(null);
      return;
    }
    setAdequacaoBombeamento(classificarAdequacaoKit({
      potenciaNecessaria: potenciaReferenciaBombeamento(proposta.dimensionamentoBomb),
      potenciaKit: proposta.kit?.potenciacc,
      semKit: !proposta.kit,
    }));
  }, [
    proposta.produto?.nome,
    proposta.etapa?.id,
    proposta.dimensionamentoBomb,
    proposta.kit,
  ]);

  /*
   useEffect(() => {
    fetchFluxos();
  }, [props.proposta]);

  const fetchFluxos = async () => {
    setModalLoading(true);
    const resultado = await getFluxogramas();
    setFluxogramas(resultado);
    setModalLoading(false);
  };
  */

  useEffect(() => {
    const fetchAnexos = async () => {
      try {
        const resultado = await getAnexos(props.proposta);
        if (resultado) {
          setAnexo(resultado);
        }
      } catch (error) {
        console.log('Erro ao buscar anexos:', error);
      }
    };
    fetchAnexos();
  }, [props.proposta]);

  useEffect(() => {
    const newModulos = kits
      .map((kit) => kit.modulo)
      .reduce((unique, modulo) => {
        return unique.findIndex((m) => m._id === modulo._id) < 0 ? [...unique, modulo] : unique;
      }, []);

    if (newModulos.length === 1) {
      setProposta({ ...proposta, modulo: newModulos[0] });
    }
    setModulos(newModulos);
  }, [kits]);

  useEffect(() => {
    const fetchModulo = async () => {
      const modulosData = await getModulos();
      setModulos(modulosData);
    };
    if (proposta.produto._id === '65089a46326edddd6ff36607') {
      fetchModulo();
    }
  }, [proposta.faturas]);

  useEffect(() => {
    if (proposta.produto.nome === 'Seguro') {
      const newValor = proposta.valorprojeto * proposta.negociacao?.anos?.indice;
      setProposta({ ...proposta, valor: newValor });
    }
    /*
    if (proposta.produto.nome === 'Limpeza') {
      const newValor = proposta.quantidadepaineis * 15 * proposta.negociacao?.parcelas?.indice;
      setProposta({ ...proposta, valor: newValor });
    }
    */
  }, [proposta.negociacao?.anos, proposta.negociacao?.parcelas]);

  // Estados e Cidades (cache local, antes buscava direto do IBGE) -----------------------
  useEffect(() => {
    getEstados().then((data) => {
      const estadosOrdenados = (data || []).sort((a, b) => a.nome.localeCompare(b.nome));
      setEstados(estadosOrdenados);
    });
  }, [proposta.localinstalacao?.estado]);

  useEffect(() => {
    if (proposta.localinstalacao?.estado) {
      getMunicipios(proposta.localinstalacao.estado.sigla).then((data) => {
        const cidadesOrdenados = (data || []).sort((a, b) => a.nome.localeCompare(b.nome));
        setCidades(cidadesOrdenados);
      });
    }
  }, [proposta.localinstalacao?.estado]);

  useEffect(() => {
    if (props.visualiza) {
      setProposta({ ...proposta, fluxo: { id: 1 }, etapa: { id: 0 } });
    }
  }, []);

  const onClose = () => {
    setProdutoSelecionado(produtos[0]);
    setProposta(null);
    setErro(false);
    setErro2(false);
    props.onClose();
  };

  // Ao clicar no confirm executa a funcao da props onConfirm retornando produto para o objeto pai
  const handleConfirm = async () => {
    setModalLoading(true);
    if (!areFieldsFilled()) {
      window.alert('Preencha todos os dados para salvar');
      setModalLoading(false);
      return;
    }
    let newProposta = proposta;
    console.log('distribuidora ao salvar:', proposta.distribuidora);

    // Registrar acréscimo nas atividades se termo foi aceito
    if (termoAcrescimoAceito && proposta.acrescimo > 0) {
      const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
      const agora = new Date();
      const dataFormatada = `${agora.toLocaleDateString('pt-BR')} - ${agora.toLocaleTimeString('pt-BR')}`;
      const valorAcrescimo = tipoAcrescimo === 'R$'
        ? parseFloat(proposta.acrescimo).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : `${proposta.acrescimo}%`;
      const msgAtividade = `Acréscimo adicionado: ${valorAcrescimo} - Usuário: ${usuarioLogado?.nome || 'Sistema'} - Termo de ciência validado em: ${dataFormatada}`;
      const jaRegistrado = newProposta.atividades?.some((a) => a.msg?.includes('Acréscimo adicionado') && a.msg?.includes(valorAcrescimo));
      if (!jaRegistrado) {
        newProposta = { ...newProposta, atividades: [...(newProposta.atividades || []), { data: agora, msg: msgAtividade }] };
      }
    }

    /*
    if (proposta.fluxo.id === 10) {
      const newProposta = { ...proposta };
      if (newProposta.finalizadoLead.nome === 'Lead Qualificado') {
        newProposta.fluxo = fluxogramas.find((value) => value.id === 1);
        newProposta.etapa = fluxogramas.find((value) => value.id === 1).etapas[1];
        newProposta.finalizado = null;
        newProposta.idMotivoRejeicao = null;
      }
      if (newProposta.finalizadoLead.nome === 'Lead Desqualificado') {
        newProposta.finalizado = { ...newProposta.finalizado, nome: 'Rejeitado', data: new Date() };
      }
      props.onConfirm(newProposta, anexo);
      return;
    }
    */

    if (
      (proposta.produto?.nome === 'Proposta Comercial' ||
        proposta.produto?.nome === 'Gerador On-Grid' ||
        proposta.produto?.nome === 'Gerador Grid-Zero' ||
        proposta.produto?.nome === 'Personalizado') &&
      proposta.finalizado?.nome === 'Aprovado'
    ) {
      const resultado = fluxogramas.find((value) => value.nome === 'Vendas').etapas[0];
      if (resultado?.enviamensagem) {
        const { mensagem, destino } = resultado;
        setModalMensagem(true);
        await enviaMensagem(mensagem, destino, proposta, (enviadoComSucesso, mensagem) => {
          setModalMensagem(false);
          if (!enviadoComSucesso) {
            window.alert('Erro ao enviar mensagem');
          }
        });
      }
      newProposta = {
        ...newProposta,
        // On-Grid e Personalizado preservam o produto real (corrige o bug que os
        // gravava como 'Proposta Comercial'). O subtipoPersonalizado fica na proposta.
        // Demais casos legados mantem o mapeamento antigo.
        produto:
          (proposta.produto?.nome === 'Gerador On-Grid' ||
            proposta.produto?.nome === 'Personalizado')
            ? { ...proposta.produto }
            : {
                _id: '65089a46326edddd6ff36604',
                nome: 'Proposta Comercial',
              },
        fluxo: { id: 2, nome: 'Vendas' },
        etapa: { id: 0, nome: 'Orçamento Realizado', color: 'LightBlue' },
      };
    }

    // Sistema de Bombeamento aprovado — preserva produto, vai para Orçamento Realizado
    if (proposta.produto?.nome === 'Sistema de Bombeamento' && proposta.finalizado?.nome === 'Aprovado') {
      newProposta = {
        ...newProposta,
        fluxo: { id: 2, nome: 'Vendas' },
        etapa: { id: 0, nome: 'Orçamento Realizado', color: 'LightBlue' },
      };
    }

    // Gerador Off-Grid aprovado — preserva produto, vai para Orçamento Realizado
    if (proposta.produto?.nome === 'Gerador Off-Grid' && proposta.finalizado?.nome === 'Aprovado') {
      newProposta = {
        ...newProposta,
        fluxo: { id: 2, nome: 'Vendas' },
        etapa: { id: 0, nome: 'Orçamento Realizado', color: 'LightBlue' },
      };
    }
    if (proposta.produto?.nome === 'Seguro' && proposta.finalizado?.nome === 'Aprovado') {
      const resultado = fluxogramas.find((value) => value.nome === 'Seguro').etapas[0];
      if (resultado?.enviamensagem) {
        const { mensagem, destino } = resultado;
        setModalMensagem(true);
        await enviaMensagem(mensagem, destino, proposta, (enviadoComSucesso, mensagem) => {
          setModalMensagem(false);
          if (!enviadoComSucesso) {
            window.alert('Erro ao enviar mensagem');
          }
        });
      }
      newProposta = {
        ...newProposta,
        fluxo: { id: 80, nome: 'Seguro' },
        etapa: { id: 0, nome: 'Orçamento Realizado', color: 'LightBlue' },
      };
    }
    if ((proposta.produto?.nome === 'Serviços' || proposta.produto?.nome === 'Proposta de Serviços') && proposta.finalizado?.nome === 'Aprovado') {
      // Serviços usam o mesmo fluxo de Vendas dos produtos
      // (7 etapas: Orçamento Realizado, Agendar Entrega, Orçamento Entregue, Negociação, Fechamento, Assinar Contrato, Coleta de Dados)
      const resultado = fluxogramas.find((value) => value.nome === 'Vendas').etapas[0];
      if (resultado?.enviamensagem) {
        const { mensagem, destino } = resultado;
        setModalMensagem(true);
        await enviaMensagem(mensagem, destino, proposta, (enviadoComSucesso, mensagem) => {
          setModalMensagem(false);
          if (!enviadoComSucesso) {
            window.alert('Erro ao enviar mensagem');
          }
        });
      }
      newProposta = {
        ...newProposta,
        fluxo: { id: 2, nome: 'Vendas' },
        etapa: { id: 0, nome: 'Orçamento Realizado', color: 'LightBlue' },
      };
    }
    props.onConfirm(newProposta, anexo);
    // onClose()
  };

  const areFieldsFilled = () => {
    // Bombeamento: não exige orientação de telhado nem disjuntor
    if (proposta.produto.nome === 'Sistema de Bombeamento') {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade != null &&
        proposta.origem !== null &&
        proposta.unidade !== null
      );
    }
    // Off-Grid: não exige orientação de telhado nem disjuntor
    if (proposta.produto.nome === 'Gerador Off-Grid') {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade != null &&
        proposta.origem !== null &&
        proposta.unidade !== null
      );
    }
    if (
      proposta.produto.nome === 'Proposta Comercial' ||
      proposta.produto.nome === 'Gerador On-Grid' ||
      proposta.produto.nome === 'Gerador Grid-Zero' ||
      proposta.produto.nome === 'Gerador Off-Grid' ||
      proposta.produto.nome === 'Personalizado'
    ) {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade &&
        proposta.localinstalacao?.cidade !== null &&
        proposta.orientacaotelhado !== null &&
        proposta.disjuntorcliente?.tipodisjuntor !== null &&
        proposta.disjuntorcliente?.potenciadisjuntor !== null &&
        proposta.origem !== null &&
        proposta.unidade !== null
      );
    }
    if (proposta.produto.nome === 'Seguro') {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade &&
        proposta.localinstalacao?.cidade !== null &&
        proposta.valorprojeto !== 0 &&
        proposta.valorprojeto !== null &&
        proposta.datainstalacao !== null
      );
    }
    if (proposta.produto.nome === 'Limpeza') {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade &&
        proposta.localinstalacao?.cidade !== null
      );
    }
    if (proposta.produto.nome === 'Serviços' || proposta.produto.nome === 'Proposta de Serviços') {
      return (
        proposta.cliente !== null &&
        proposta.localinstalacao !== null &&
        proposta.localinstalacao?.estado !== null &&
        proposta.localinstalacao?.cidade &&
        proposta.localinstalacao?.cidade !== null &&
        proposta.superficie !== null &&
        proposta.origem !== null &&
        proposta.unidade !== null
      );
    }
    return false;
  };

  // Salva sem fechar o modal (usado quando termoAcrescimoAceito)
  const handleSave = async () => {
    console.log('handleSave chamado, props.onSave:', !!props.onSave);
  console.log('termoAcrescimoAceito:', termoAcrescimoAceito);
    if (props.onSave) {
      setModalLoading(true);
      // Registrar acréscimo no backlog se pendente
      let propostaParaSalvar = proposta;
      if (termoAcrescimoAceito && proposta.acrescimo > 0) {
        const usuarioLogado = JSON.parse(localStorage.getItem('userData'));
        const agora = new Date();
        const dataFormatada = `${agora.toLocaleDateString('pt-BR')} - ${agora.toLocaleTimeString('pt-BR')}`;
        const valorAcrescimo = tipoAcrescimo === 'R$'
          ? parseFloat(proposta.acrescimo).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : `${proposta.acrescimo}%`;
        const msgAtividade = `Acréscimo adicionado: ${valorAcrescimo} - Usuário: ${usuarioLogado?.nome || 'Sistema'} - Termo de ciência validado em: ${dataFormatada}`;
        const jaRegistrado = proposta.atividades?.some((a) => a.msg?.includes('Acréscimo adicionado') && a.msg?.includes(valorAcrescimo));
        if (!jaRegistrado) {
          propostaParaSalvar = {
            ...proposta,
            atividades: [...(proposta.atividades || []), { data: agora, msg: msgAtividade }],
          };
          setProposta(propostaParaSalvar);
        }
      }
      console.log('handleSave - backlog antes de salvar:', propostaParaSalvar.backlog?.slice(-2));
      await props.onSave(propostaParaSalvar);
      setModalLoading(false);
    } else {
      handleConfirm();
    }
  };

  const handleSaveDebug = () => {
    console.log('termo aceito:', termoAcrescimoAceito);
    console.log('props.onSave existe:', !!props.onSave);
    if (termoAcrescimoAceito) {
      handleSave();
    } else {
      handleConfirm();
    }
  };

  const handleNext = async () => {
    // No modo visualização, pula todas as validações de campos obrigatórios
    if (!props.visualiza && !areFieldsFilled()) {
      window.alert('Preencha todos os dados para avançar');
      return;
    }

    /*
    // muda fluxo para qualificacao
    if (
      (proposta.produto?.nome === 'Proposta Comercial' ||
        proposta.produto?.nome === 'Personalizado') &&
      proposta.origem.nome === 'Lead' &&
      proposta.fluxo.id === 1 &&
      proposta.etapa.id === 0 &&
      !props.visualiza &&
      !proposta.finalizadoLead
    ) {
      const newProposta = { ...proposta };
      newProposta.fluxo = fluxogramas.find((value) => value.id === 10);
      newProposta.etapa = fluxogramas.find((value) => value.id === 10).etapas[0];
      props.onConfirm(newProposta, anexo);

      return;
    }
    */

    // Identifica proposta de serviços (acréscimo é digitado/aceito já na etapa 1)
    const ehServicoNext =
      proposta.produto?.nome === 'Serviços' ||
      proposta.produto?.nome === 'Proposta de Serviços' ||
      proposta.produto?._id === '69fb36fba8a5817c8defc754' ||
      proposta.produto?._id === '65089a46326edddd6ff36606';

    // Se acréscimo foi digitado mas o termo não foi aceito (nem no estado transitório,
    // nem no flag persistido na proposta), bloqueia o avanço.
    // Não se aplica a serviços: lá o acréscimo e o termo são tratados na etapa 1.
    if (!ehServicoNext && proposta.etapa?.id === 2 && proposta.acrescimo > 0 && !termoAcrescimoAceito && !proposta.acrescimoAceito) {
      window.alert('Clique em "Habilitar Acréscimo" para atualizar o valor do equipamento.');
      return;
    }

    // Verificar desconto máximo ao avançar
    if (proposta.etapa?.id === 2 && proposta.kit?.descontomaximo != null && proposta.desconto > 0) {
      const descontoMaximo = proposta.kit.descontomaximo;
      let descontoAplicado = proposta.desconto;
      if (tipoDesconto === 'R$') {
        descontoAplicado = (proposta.desconto / proposta.valorkit) * 100;
      }
      if (descontoAplicado > descontoMaximo) {
        window.alert(`Desconto máximo permitido para este kit: ${descontoMaximo}%. O desconto foi zerado.`);
        setProposta((prev) => ({ ...prev, desconto: 0 }));
        return;
      }
    }

    const resultado = fluxogramas.find((value) => value.id === proposta.fluxo.id).etapas[proposta.etapa.id + 1];
    // No modo visualização: apenas avança a etapa localmente, sem salvar nem enviar mensagens
    if (props.visualiza) {
      setProposta({ ...proposta, etapa: steps[proposta.etapa.id + 1] });
      return;
    }
    if (resultado?.enviamensagem) {
      const { mensagem, destino } = resultado;
      setModalMensagem(true);
      await enviaMensagem(mensagem, destino, proposta, (enviadoComSucesso, mensagem) => {
        setModalMensagem(false);
        if (!enviadoComSucesso) {
          window.alert('Erro ao enviar mensagem');
        }
      });
    }
    const newArray = [...proposta.backlog];
    // newArray.push({etapa: steps[proposta.etapa.id], data: new Date()})
    setProposta({ ...proposta, etapa: steps[proposta.etapa.id + 1], backlog: newArray });
  };

  const handleBack = () => {
    setProposta({ ...proposta, etapa: steps[proposta.etapa.id - 1] });
  };

  const handleAbrirProposta = () => {
  if (produtoSelecionado.nome === 'Personalizado' || proposta.produto.nome === 'Personalizado') {
    setModalPersonalizado(true);
    return;
  }
  // Gerador Off-Grid → abre normalmente (dimensionamento está na etapa 1 do stepper)
  // Sistema de Bombeamento → abre normalmente (dimensionamento está na etapa 1 do stepper)
  setProposta({ ...proposta, produto: produtoSelecionado });
};


  const confirmModalPersonalizado = () => {
    setModalPersonalizado(false);
    setProposta({ ...proposta, produto: produtoSelecionado, orientacaotelhado: proposta.motivoPersonalizado === 'Usina de Solo' ?  {_id: "6508a00a326edddd6ff36616", nome: "Norte/Noroeste/Nordeste"} : proposta.orientacaotelhado ?? null});
  }

  const cancelModalPersonalizado = () => {

  }

  const handleVendedorExterno = (newValue) => {
    setProposta((prevProposta) => ({
      ...prevProposta,
      user: newValue,
      user2: prevProposta.user2 ? prevProposta.user2 : prevProposta.user,
    }));
  };

  // --------------------
  const handleAddFatura = (index) => {
    const newArray = [...proposta.faturas];
    newArray[index].push({ id: proposta.faturas[index].length + 1, valor: 0 });
    setProposta({ ...proposta, faturas: newArray });
  };

  // --------------------
  const handleAddTipoFatura = () => {
    const newArray = [...proposta.faturas];
    newArray.push([]);
    setProposta({ ...proposta, faturas: newArray });
  };

  const handleRemoveTipoFatura = (index) => {
    const newArray = [...proposta.faturas];
    newArray.splice(index, 1);
    setProposta({ ...proposta, faturas: newArray });
  };

  const handleChangeFatura = (e, index1, index2) => {
    const newArray = [...proposta.faturas];
    newArray[index1][index2] = { ...newArray[index1][index2], valor: parseFloat(e.target.value) };
    setProposta({ ...proposta, faturas: newArray });
  };

  const removeFatura = (e, index1, index2) => {
    const newArray = [...proposta.faturas];
    newArray[index1].splice(index2, 1);
    setProposta({ ...proposta, faturas: newArray });
  };

  const calcMediaConsumo = () => {
    let total = 0;
    let totalparcial = 0;
    proposta.faturas.map((fatura) => {
      totalparcial = 0;
      fatura.map((fat) => {
        totalparcial += parseFloat(fat.valor);
        return totalparcial;
      });
      totalparcial /= fatura.length;
      total += totalparcial;
      return total;
    });
    // total /= proposta.faturas.length
    if (Number.isNaN(total)) {
      total = 0;
    }
    setProposta((prevProposta) => ({ ...prevProposta, mediaconsumo: parseFloat(total.toFixed(2)) }));
  };

  useEffect(() => {
    calcMediaConsumo();
  }, [proposta.faturas]);

  const calcMediaConsumoAbatida = () => {
    if (proposta.disjuntorcliente?.tipodisjuntor?.nome === 'Monofásico') {
      setProposta((prevProposta) => ({
        ...prevProposta,
        mediaconsumoabatida: parseFloat((proposta.mediaconsumo - 30).toFixed(2)),
      }));
    }
    if (proposta.disjuntorcliente?.tipodisjuntor?.nome === 'Bifásico') {
      setProposta((prevProposta) => ({
        ...prevProposta,
        mediaconsumoabatida: parseFloat((proposta.mediaconsumo - 50).toFixed(2)),
      }));
    }
    if (proposta.disjuntorcliente?.tipodisjuntor?.nome === 'Trifásico') {
      setProposta((prevProposta) => ({
        ...prevProposta,
        mediaconsumoabatida: parseFloat((proposta.mediaconsumo - 100).toFixed(2)),
      }));
    }
  };
  useEffect(() => {
    calcMediaConsumoAbatida();
  }, [proposta.disjuntorcliente, proposta.mediaconsumo]);

  // Dimensionamento On-Grid — lógica extraída para ./dimensionamentoOnGrid.js
  // (era ~150 linhas inline aqui). Comportamento idêntico: continua rodando
  // automaticamente via o useEffect logo abaixo, sem etapa manual no wizard.
  const identificaKit = () => identificarKitOnGrid({
    proposta,
    setProposta,
    setErro,
    setInversoresDisponiveis,
    setInversorSelecionado,
    kits,
    inversorSelecionado,
    tipoDesconto,
    tipoAcrescimo,
    termoAcrescimoAceito,
    setAdequacaoKit: setAdequacaoOnGrid,
  });

  useEffect(() => {
    if (!props.visualiza && (
  proposta.produto.nome === 'Proposta Comercial' ||
  proposta.produto.nome === 'Gerador On-Grid' ||
  proposta.produto.nome === 'Gerador Grid-Zero'
) && proposta.fornecedor) {
      identificaKit();
    }
  }, [
    proposta.mediaconsumoabatida,
    proposta.modulo,
    kits,
    inversorSelecionado,
    proposta.fornecedor,
    proposta.topologia,
    proposta.orientacaotelhado,
    proposta.rebatimento,
  ]);

  // UseEffect para calcular o valor com base no desconto e acréscimo (% ou R$)
  useEffect(() => {
    if (!proposta.chamado && (proposta.produto.nome === 'Proposta Comercial' || proposta.produto.nome === 'Gerador On-Grid' || proposta.produto.nome === 'Gerador Grid-Zero' || proposta.produto.nome === 'Gerador Off-Grid' || proposta.produto.nome === 'Sistema de Bombeamento')) {
      // Desconto
      let valorComDesconto = proposta.valorkit;
      if (proposta.desconto) {
        if (tipoDesconto === '%') {
          valorComDesconto = proposta.valorkit * (1 - proposta.desconto / 100);
        } else {
          valorComDesconto = proposta.valorkit - proposta.desconto;
        }
      }
      // Acréscimo — só aplica se o termo foi aceito
      let valorFinal = valorComDesconto;
      if (proposta.acrescimo && termoAcrescimoAceito) {
        if (tipoAcrescimo === '%') {
          valorFinal = valorComDesconto * (1 + proposta.acrescimo / 100);
        } else {
          valorFinal = valorComDesconto + proposta.acrescimo;
        }
      }
      valorFinal = parseFloat(valorFinal.toFixed(2));
      const diffPercent = ((valorFinal - proposta.valorkit) / proposta.valorkit) * 100;
      const newMargemLucro = Number.isNaN(18 + diffPercent) ? null : (18 + diffPercent).toFixed(2);
      setProposta((prevProposta) => ({ ...prevProposta, valor: valorFinal, margemlucro: newMargemLucro }));
    }
  }, [proposta.desconto, proposta.valorkit, proposta.acrescimo, proposta.teste, tipoDesconto, tipoAcrescimo, termoAcrescimoAceito]);

  const onChangeDesconto = (e) => {
    const descontoInformado = parseFloat(e.target.value);
    setProposta((prevProposta) => ({ ...prevProposta, desconto: descontoInformado }));
  };

  const onChangeAcrescimo = (e) => {
    const acrescimoInformado = parseFloat(e.target.value);
    setProposta((prevProposta) => ({ ...prevProposta, acrescimo: acrescimoInformado, acrescimoAceito: false }));
    setTermoAcrescimoAceito(false); // Resetar aceite ao alterar o acréscimo
  };

  useEffect(() => {
    if (proposta.kit) {
      const valorKit = typeof proposta.kit?.precovenda === 'number'
        ? proposta.kit.precovenda
        : proposta.kit?.precovenda?.find(
            (item) => item.uf === proposta.localinstalacao?.estado?.sigla
          )?.precovenda;
      if (proposta.valor < valorKit * (1 - proposta.kit.descontomaximo / 100)) {
        setErro({ tipo: 1, msg: `Valor informado menor que o permitido no kit` });
        return;
      }
      setErro((erroAtual) => (
        erroAtual?.tipo === 2 ? erroAtual : false
      ));
    }
  }, [proposta.valor]);

  useEffect(() => {
    verificarDisjuntor();
  }, [proposta.kit, proposta.disjuntorcliente]);
  const verificarDisjuntor = () => {
    if (
      proposta.disjuntorcliente?.tipodisjuntor?.id < proposta.kit?.disjuntorminino?.tipodisjuntor?.id ||
      proposta.disjuntorcliente?.potenciadisjuntor?.valor < proposta.kit?.disjuntorminino?.potenciadisjuntor?.valor
    ) {
      setErro2({ tipo: 2, msg: `ATENÇÃO: Será necessário efetuar a troca do disjuntor do cliente` });
      // setProposta({...proposta, atividades: [{data: new Date(), msg: 'ATENÇÃO: Será necessário efetuar a troca do disjuntor do cliente'}]})
      return;
    }
    setErro2(false);
  };
  // ----------------------

  // PDF ------------------
  const gerarPdf = async () => {
    await generatePdf(proposta);
    setPropostaGerada(true);
  };

  const gerarPdfBombeamento = async () => {
    await generatePdfBombeamento(proposta);
    setPropostaGerada(true);
  };

  const gerarPdfOffGrid = async () => {
    await generatePdfOffGrid(proposta);
    setPropostaGerada(true);
  };

  const gerarPdfLimpeza = async () => {
    await generatePdfLimpeza(proposta);
    setPropostaGerada(true);
  };

  const gerarPdfServicos = async () => {
    await generatePdfServicos(proposta);
    setPropostaGerada(true);
  };

  const gerarPdfSeguro = async () => {
    await generatePdfSeguro(proposta);
    setPropostaGerada(true);
  };
  // PDF -------------------

  // Carregar Arquivo Base64 ---------------------
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const newArrayAnexos = [...anexo];

    if (file) {
      const name = file.name.split('.');
      const reader = new FileReader();
      reader.onloadend = () => {
        newArrayAnexos.push({
          nome: name[0],
          ext: name[1],
          base64: reader.result,
          tipo: { _id: '6721966adae8c71b0b6ac116', nome: 'Outros', obrigatorio: false },
        });
        setAnexo(newArrayAnexos);
      };
      reader.readAsDataURL(file);
    }
  };

  const addAnexo = () => {
    // Trigger the click event of the file input when the button is clicked
    const fileInputRef = document.createElement('input');
    fileInputRef.type = 'file';
    fileInputRef.style.display = 'none';
    fileInputRef.onchange = (e) => handleImageChange(e);
    document.body.appendChild(fileInputRef);
    fileInputRef.click();
  };
  // Carregar Arquivo Base64 ---------------------

  async function downloadAnexo(anexo) {
    const anexoResult = await getUnoAnexo({ _id: anexo._id });
    // console.log(anexoResult);
    // Verifique se o anexo tem dados Base64 válidos
    if (anexoResult && anexoResult.base64) {
      // Divide a string Base64 em um array usando a vírgula como delimitador
      const parts = anexoResult.base64.split(',');

      if (parts.length === 2) {
        // A primeira parte contém o tipo de arquivo
        const contentType = parts[0].split(';')[0];
        const fileType = contentType.split('/')[1];

        // A segunda parte contém os dados Base64
        const base64String = parts[1];

        // Cria um Blob a partir do Base64
        const blob = b64toBlob(base64String, fileType);

        // Use o file-saver para salvar o Blob como arquivo
        // console.log(fileType)
        let extension = '';
        if (fileType === 'plain') {
          extension = 'txt';
        } else if (fileType === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          extension = 'xlsx';
        } else {
          // Se não for um tipo de arquivo conhecido, use a extensão padrão
          extension = fileType;
        }

        saveAs(blob, `${anexoResult.nome}.${extension}`);
      }
    }
  }

  function b64toBlob(base64, contentType) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // Baixar Arquivo Base64 ---------------------
  async function downloadAnexo2(anexoPreview) {
    const anexo = await getUnoAnexo({ _id: anexoPreview._id, index: anexoPreview.index });
    // Verifique se o anexo tem dados Base64 válidos
    if (anexo && anexo.base64) {
      // Divide a string Base64 em um array usando a vírgula como delimitador
      const parts = anexo.base64.split(',');

      if (parts.length === 2) {
        // A primeira parte contém o tipo de arquivo
        const fileType = parts[0].split(';')[0].split(':')[1];

        // A segunda parte contém os dados Base64
        const base64String = parts[1];

        // Cria um Blob a partir do Base64
        const blob = b64toBlob(base64String, fileType);

        // Use o file-saver para salvar o Blob como arquivo
        saveAs(blob, `${anexo.nome}.${fileType.split('/')[1]}`);
      }
    }
  }

  function b64toBlob2(base64, contentType) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // ModalAddCLiente
  const closeModalAddCliente = () => {
    setModalAddCliente(false);
  };

  const confirmCliente = (cliente) => {
    rowsClientes.push(cliente);
    setProposta({ ...proposta, cliente });
  };

  const confirmIndicador = (cliente) => {
    rowsIndicador.push(cliente);
    // Registrar nas Atividades sem dados sensíveis (LGPD)
    const tipoInd = proposta.tipoindicacao?.nome || '';
    const valorInd = proposta.tipoindicacao?.valor
      ? `R$ ${proposta.tipoindicacao.valor},00`
      : proposta.percentualindicacao
      ? `${proposta.percentualindicacao}%`
      : '';
    const codigoIndicador = String(cliente.codigo || cliente._id).slice(-6).toUpperCase();
    const msgAtividade = `Indicação registrada - Tipo: ${tipoInd}${valorInd ? ` - Valor: ${valorInd}` : ''} - Código Indicador: #${codigoIndicador}`;
    const novaAtividade = { data: new Date(), msg: msgAtividade };
    setProposta((prev) => ({
      ...prev,
      indicador: cliente,
      atividades: [...(prev.atividades || []), novaAtividade],
    }));
  };

  // Calendario
  const [openCalendar, setOpenCalendar] = useState(false);
  const handleCalendarOpen = () => {
    setOpenCalendar(true);
  };
  const handleCalendarClose = () => {
    setOpenCalendar(false);
  };
  const handleDateChange = (date) => {
    setProposta({ ...proposta, datainstalacao: date });
    handleCalendarClose(); // Feche o calendário após a seleção da data
  };
  const handleDateChange2 = (date) => {
    setProposta({ ...proposta, dataagendamento: date });
    handleCalendarClose(); // Feche o calendário após a seleção da data
  };
  // Calendario
  const closeModalMensagem = () => {
    setModalMensagem(false);
  };

  const [fornecedores, setFornecedores] = useState([]);

  useEffect(() => {
    fectchFornecedores();
  }, []);
  const fectchFornecedores = async () => {
    const resultado = await getFornecedores();
    setFornecedores(resultado);
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setProposta({
      ...proposta,
      [idName]: value?._id,
      [name]: value,
    });
  };

  const [modalAddAgenda, setModalAddAgenda] = useState(false);

  const [modalAtividade, setModalAtividade] = useState(false);

  const closeModalAtividade = () => {
    setModalAtividade(false);
  };

  const handleSalvarAtividade = (atividade) => {
    const newArray = [...proposta.atividades];
    newArray.push({ data: new Date(), msg: atividade, ...(proposta.fluxo.id === 4 && { instalacao: true }) });
    // Atualize o estado da proposta
    const updatedProposta = {
      ...proposta, // Use o estado atual como base
      atividades: newArray,
    };
    // Atualize o estado uma vez, após todas as operações terem sido concluídas
    setProposta(updatedProposta);
    closeModalAtividade();
  };

  const handleAddAgenda = (agenda) => {
    const newProposta = { ...proposta };
    const newArray = [...newProposta.atividades];
    newArray.push({
      data: new Date(),
      msg: `Adicionado agenda - Usuário: ${agenda.usuario.nome} - Descrição: ${agenda.descricaoAgenda}`,
    });
    // Atualize o estado da proposta
    const updatedProposta = {
      ...newProposta, // Use o estado atual como base
      atividades: newArray,
    };
    props.onConfirm(updatedProposta, anexo);
    setModalAddAgenda(false);
  };

  const handleAdicionarServico = () => {
    if (!servicoSelecionado || quantidade <= 0) {
      // Adicionar feedback para o usuário, se desejar
      return;
    }

    const novoServico = {
      ...servicoSelecionado,
      // CORRIGIDO (deployment Marcki): usa o valor editado pelo usuário
      // (valorUnitario), não mais o valor fixo do catálogo.
      valor: valorUnitario,
      quantidade,
      ...(servicoSelecionado.tipo === 'recorrente' && { duracao: Number(duracao) || 1 }),
    };

    setProposta((prev) => ({
      ...prev,
      servicos: [...(prev.servicos ?? []), novoServico],
    }));

    // Limpar campos após adicionar
    setServicoSelecionado(null);
    setQuantidade(1);
    setDuracao(12);
    setValorUnitario(0);
  };

  const handleRemoverServico = (index) => {
    setProposta((prev) => ({
      ...prev,
      servicos: prev.servicos.filter((_, i) => i !== index),
    }));
  };

  // Valor de uma linha de servico: recorrente multiplica pela duracao (no de periodos).
  const valorLinhaServico = (s) =>
    (Number(s.valor) || 0) * (Number(s.quantidade) || 0) *
    (s.tipo === 'recorrente' ? (Number(s.duracao) || 1) : 1);

  // 1. Use useMemo APENAS para calcular e memorizar o valor total.
  const subTotalServicos = useMemo(() => {
    if (props.visualiza) {
      return proposta.subTotalServicos;
    }
    return proposta.servicos?.reduce((total, servico) => total + valorLinhaServico(servico), 0);
  }, [proposta.servicos]);

  const valorDeslocamento = useMemo(() => {
    if (props.visualiza) {
      return proposta.valorDeslocamento;
    }
    if (proposta.localinstalacao?.cidade?.nome !== 'Araguari') {
      return (Number(proposta.kmrodado) || 0) * 2;
    }
    return 0;
  }, [proposta.kmrodado]);

  const valorIndicacao = useMemo(() => {
    if (props.visualiza) {
      return proposta.valorIndicacao;
    }
    // CORRIGIDO (deployment Marcki — valor zerado em propostas de Serviços):
    // a checagem só por _id fixo falha em qualquer banco novo, já que o
    // seed de produtos gera IDs aleatórios a cada execução — os dois IDs
    // abaixo são específicos da Arasol. Adicionado fallback por nome
    // (mesmo padrão já usado em ehServicoNext, linha ~1158).
    if (proposta.origem?.nome === 'Indicação' && (
      proposta.produto?.nome === 'Serviços' ||
      proposta.produto?.nome === 'Proposta de Serviços' ||
      proposta.produto._id === '69fb36fba8a5817c8defc754' ||
      proposta.produto._id === '65089a46326edddd6ff36606'
    )) {
      return proposta.tipoindicacao?.valor
        ? proposta.tipoindicacao.valor
        : subTotalServicos * (proposta.percentualindicacao / 100);
    }
    return 0;
  }, [subTotalServicos, proposta.origem, proposta.tipoindicacao, proposta.percentualindicacao]);

  const valorIndicacaoProposta = useMemo(() => {
    if (props.visualiza) {
      return proposta.valorIndicacao;
    }
    if (
      !props.visualiza &&
      proposta.origem?.nome === 'Indicação' &&
      (proposta.produto.nome === 'Proposta Comercial' || 
proposta.produto.nome === 'Gerador On-Grid' ||
proposta.produto.nome === 'Gerador Grid-Zero' ||
proposta.produto.nome === 'Gerador Off-Grid' ||
proposta.produto.nome === 'Sistema de Bombeamento' || proposta.produto._id === '65089a46326edddd6ff36607')
    ) {
      return proposta.tipoindicacao?.valor
        ? proposta.tipoindicacao.valor
        : proposta.valor * (proposta.percentualindicacao / 100);
    }
    return 0;
  }, [proposta.valor, proposta.origem, proposta.tipoindicacao, proposta.percentualindicacao]);

  // Desconto sobre o subtotal de serviços (em R$)
  const valorDescontoServicos = useMemo(() => {
    if (!proposta.desconto) return 0;
    const base = Number(subTotalServicos) || 0;
    return tipoDesconto === '%' ? base * (Number(proposta.desconto) / 100) : Number(proposta.desconto);
  }, [subTotalServicos, proposta.desconto, tipoDesconto]);

  // Acréscimo sobre o subtotal já com desconto (em R$); só vale com termo aceito
  const valorAcrescimoServicos = useMemo(() => {
    if (!proposta.acrescimo || (!termoAcrescimoAceito && !proposta.acrescimoAceito)) return 0;
    const base = (Number(subTotalServicos) || 0) - (Number(valorDescontoServicos) || 0);
    return tipoAcrescimo === '%' ? base * (Number(proposta.acrescimo) / 100) : Number(proposta.acrescimo);
  }, [subTotalServicos, valorDescontoServicos, proposta.acrescimo, tipoAcrescimo, termoAcrescimoAceito, proposta.acrescimoAceito]);

  const valorTotalProposta = useMemo(() => {
    if (props.visualiza) {
      return Number(proposta.valor) || 0;
    }

    const subTotalAjustado =
      (Number(subTotalServicos) || 0) - (Number(valorDescontoServicos) || 0) + (Number(valorAcrescimoServicos) || 0);
    return subTotalAjustado + (Number(valorDeslocamento) || 0) + (Number(valorIndicacao) || 0);
  }, [subTotalServicos, valorDescontoServicos, valorAcrescimoServicos, valorDeslocamento, valorIndicacao, props.visualiza]);

  useEffect(() => {
    if (props.visualiza) {
      return;
    }
    // CORRIGIDO (deployment Marcki — valor zerado em propostas de Serviços):
    // mesma correção da linha ~1747 — este é o efeito que de fato grava
    // proposta.valor (o campo exibido na lista de Propostas). Sem o
    // fallback por nome, ele nunca rodava para o produto "Serviços" da
    // Marcki, e o valor ficava sempre 0 na listagem mesmo com o cálculo
    // interno (subTotalServicos) correto.
    if (!props.visualiza && (
      proposta.produto?.nome === 'Serviços' ||
      proposta.produto?.nome === 'Proposta de Serviços' ||
      proposta.produto._id === '69fb36fba8a5817c8defc754' ||
      proposta.produto._id === '65089a46326edddd6ff36606'
    )) {
      setProposta((prev) => ({
        ...prev,
        subTotalServicos,
        valorDeslocamento,
        valorIndicacao,
        valorDescontoServicos,
        valorAcrescimoServicos,
        valor: valorTotalProposta,
      }));
    }
    // Adicione as dependências que causam a atualização do estado.
  }, [subTotalServicos, valorDeslocamento, valorIndicacao, valorDescontoServicos, valorAcrescimoServicos, valorTotalProposta, setProposta]);

  useEffect(() => {
    if (props.visualiza) {
      return;
    }
    if (proposta.produto?.nome === 'Gerador On-Grid' || proposta.produto._id === '65089a46326edddd6ff36604' || proposta.produto._id === '65089a46326edddd6ff36607') {
      setProposta((prev) => ({
        ...prev,
        valorIndicacao: valorIndicacaoProposta,
      }));
    }
    // Adicione as dependências que causam a atualização do estado.
  }, [valorIndicacaoProposta, setProposta]);

  const [formas, setFormas] = useState([]);

  useEffect(() => {
    fetchFormas();
  }, []);
  const fetchFormas = async () => {
    const result = await getFormasPagamento();
    const opcoesLiberadas = ['PIX', 'Débito', 'Crédito a Vista', 'Crédito Parcelado'];
    const formasfiltradas = result.filter((forma) => opcoesLiberadas.includes(forma.nome));
    setFormas(formasfiltradas);
  };

  // seguro
  // Componente que renderiza uma linha de opções de parcelamento
  const duracoes = [1, 2, 3, 4, 5]; // Opções de duração em anos
  const LinhaSeguro = ({ anos, valorProjeto, sxFontBotaoMobile }) => {
    const parcelas = [1, 2, 3, 4]; // Opções de parcelamento

    return (
      <Stack direction={'row'} gap={1} alignItems={'center'} justifyContent={'center'}>
        {anos} {anos > 1 ? 'Anos' : 'Ano'}&nbsp;&nbsp;
        {parcelas.map((p) => {
          const { valorParcela } = calcularValoresSeguro(valorProjeto, anos, p);
          return (
            <TextField
              key={`${anos}-${p}`}
              size="small"
              label={p === 1 ? 'Valor Seguro' : `${p} vezes`}
              value={formatarMoeda(valorParcela)}
              variant="filled"
              InputProps={{ readOnly: true, sx: sxFontBotaoMobile }}
            />
          );
        })}
      </Stack>
    );
  };

  // useEffect para recalcular sempre que a seleção mudar
  useEffect(() => {
    if (props.visualiza) {
      return;
    }
    if (
      proposta.produto?.nome === 'Seguro' &&
      proposta.negociacao?.anos &&
      proposta.negociacao?.parcelas &&
      proposta.valorprojeto
    ) {
      // Chama a MESMA função centralizada
      const { valorTotal, valorParcela } = calcularValoresSeguro(
        proposta.valorprojeto,
        proposta.negociacao.anos.id,
        proposta.negociacao.parcelas.id
      );

      // Atualiza o estado da proposta com os valores calculados
      setProposta((prev) => ({
        ...prev,
        valor: valorTotal,
        valorParcela,
        parcelas: proposta.negociacao.parcelas.id,
      }));
    }
  }, [proposta.negociacao, proposta.valorprojeto, setProposta]);

  return (
    <ThemeProvider theme={(outerTheme) => createTheme(outerTheme, { palette: { primary: { main: coresHex.laranja, dark: coresHex.laranjaEscuro } } })}>
      {dataFetched &&
        // Caso nao tenha tipoproduto abre pra selecionar o tipo antes de abrir o modal espeficico
        proposta.produto._id === null ? (
        <Box sx={style}>
          <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
            <Autocomplete
              disablePortal
              id="produto"
              value={produtoSelecionado}
              onChange={(event, newValue) => {
                setProdutoSelecionado(newValue);
                // setProposta((prevProposta) => ({...prevProposta, produto: newValue}))
              }}
              options={produtos}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => `${option.nome}`}
              sx={styleTextField}
              renderOption={(props, option) => {
                const IconeProduto = ICONE_POR_PRODUTO[option.nome] || ICONE_PADRAO;
                return (
                  <li {...props} key={option._id}>
                    <IconeProduto fontSize="small" sx={{ mr: 1.5, color: cores.laranja }} />
                    {option.nome}
                  </li>
                );
              }}
              renderInput={(params) => {
                const IconeSelecionado = produtoSelecionado
                  ? (ICONE_POR_PRODUTO[produtoSelecionado.nome] || ICONE_PADRAO)
                  : ICONE_PADRAO;
                return (
                  <TextField
                    {...params}
                    label="Selecione o Produto"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconeSelecionado fontSize="small" sx={{ color: cores.laranja }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            />
          </Stack>

          <Stack direction={'row'} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
            <Button variant="outlined" onClick={() => onClose()} color="error">
              {' '}
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleAbrirProposta} endIcon={<ArrowForwardIosIcon />}>
              Próximo
            </Button>
          </Stack>
        </Box>
      ) : // Modal para proposta comercial -------------------------
        // -------------------------------------------------------
        // Modal para antigos leads -------------------------
        proposta.fluxo.id === 100000000 ? (
          <Box sx={style}>
            <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
              <Stepper alternativeLabel activeStep={proposta.etapa?.id} connector={<QontoConnector />}>
                {steps.map((step) => (
                  <Step key={step.id}>
                    <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
            <Stack direction={'row'} gap={2} sx={{ justifyContent: 'right', mt: 2, mb: -2 }}>
              {proposta._id && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CalendarMonth />}
                  onClick={() => setModalAddAgenda(true)}
                >
                  Adicionar Agenda
                </Button>
              )}
            </Stack>
            {
              // Lead Recebido --------------
              proposta.etapa?.id === 0 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                  <Stack alignItems={'center'} sx={{ width: '50%', height: '85%', border: 1, borderRadius: 2 }}>
                    <Stack
                      alignItems={'center'}
                      borderRadius={2}
                      sx={{ backgroundColor: 'white', width: '100%', maxHeight: '80vh' }}
                    >
                      <b>Atividades</b>
                    </Stack>
                    <Button
                      sx={{ ...sxFontBotaoMobile, mt: 2 }}
                      variant="contained"
                      onClick={() => setModalAtividade(true)}
                    >
                      Adicionar Atividade
                    </Button>
                    <Container sx={{ height: '55vh', overflowY: 'auto' }}>
                      {proposta.atividades.map((atividade, index) => {
                        return (
                          <Typography
                            key={index}
                            variant="subtitle2"
                            sx={{
                              backgroundColor: 'white',
                              width: '100%',
                              borderRadius: 2,
                              mt: 2,
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              '&:hover': { backgroundColor: 'lightgray', cursor: 'pointer' },
                            }}
                          >
                            {`${new Date(atividade.data).toLocaleDateString()} - `}
                            {atividade.msg.split('\n').map((line, index) => (
                              <React.Fragment key={index}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                          </Typography>
                        );
                      })}
                    </Container>
                  </Stack>
                </Stack>
              ) : // Primeiro Contato --------------
                proposta.etapa?.id === 1 ? (
                  <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                    <Stack alignItems={'center'} sx={{ width: '50%', height: '85%', border: 1, borderRadius: 2 }}>
                      <Stack
                        alignItems={'center'}
                        borderRadius={2}
                        sx={{ backgroundColor: 'white', width: '100%', maxHeight: '80vh' }}
                      >
                        <b>Atividades</b>
                      </Stack>
                      <Button
                        sx={{ ...sxFontBotaoMobile, mt: 2 }}
                        variant="contained"
                        onClick={() => setModalAtividade(true)}
                      >
                        Adicionar Atividade
                      </Button>
                      <Container sx={{ height: '55vh', overflowY: 'auto' }}>
                        {proposta.atividades.map((atividade, index) => {
                          return (
                            <Typography
                              key={index}
                              variant="subtitle2"
                              sx={{
                                backgroundColor: 'white',
                                width: '100%',
                                borderRadius: 2,
                                mt: 2,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                '&:hover': { backgroundColor: 'lightgray', cursor: 'pointer' },
                              }}
                            >
                              {`${new Date(atividade.data).toLocaleDateString()} - `}
                              {atividade.msg.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))}
                            </Typography>
                          );
                        })}
                      </Container>
                    </Stack>
                  </Stack>
                ) : // Tentativa de contato --------------
                  proposta.etapa?.id === 2 ? (
                    console.log('REVISAO - proposta.kit:', proposta.kit, 'proposta.erro', erro)||
                    <Stack flex={1} alignItems={'center'} direction={'row'} gap={5}>
                      <Stack alignItems={'center'} sx={{ width: '50%', height: '85%', border: 1, borderRadius: 2, mb: 5 }}>
                        <Stack
                          alignItems={'center'}
                          borderRadius={2}
                          sx={{ backgroundColor: 'white', width: '100%', maxHeight: '80vh' }}
                        >
                          <b>Atividades</b>
                        </Stack>
                        <Button
                          sx={{ ...sxFontBotaoMobile, mt: 2 }}
                          variant="contained"
                          onClick={() => setModalAtividade(true)}
                        >
                          Adicionar Atividade
                        </Button>
                        <Container sx={{ height: '55vh', overflowY: 'auto' }}>
                          {proposta.atividades.map((atividade, index) => {
                            return (
                              <Typography
                                key={index}
                                variant="subtitle2"
                                sx={{
                                  backgroundColor: 'white',
                                  width: '100%',
                                  borderRadius: 2,
                                  mt: 2,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  '&:hover': { backgroundColor: 'lightgray', cursor: 'pointer' },
                                }}
                              >
                                {`${new Date(atividade.data).toLocaleDateString()} - `}
                                {atividade.msg.split('\n').map((line, index) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    <br />
                                  </React.Fragment>
                                ))}
                              </Typography>
                            );
                          })}
                        </Container>
                      </Stack>
                      <Stack alignItems={'center'} sx={{ width: '50%' }}>
                        <Autocomplete
                          disablePortal
                          disabled={props.visualiza}
                          id="finalizadoLead"
                          value={proposta.finalizadoLead || null}
                          options={finalizadoLead}
                          isOptionEqualToValue={(option, value) => option.nome === value.nome}
                          getOptionLabel={(option) => `${option.nome}`}
                          sx={{ ...styleTextField, marginBottom: 2, width: '80%' }}
                          renderInput={(params) => <TextField {...params} label="Selecione" />}
                          onChange={(event, newValue) => {
                            setProposta((prevProposta) => ({ ...prevProposta, finalizadoLead: newValue }));
                          }}
                        />
                        {proposta.finalizadoLead?.nome === 'Lead Desqualificado' && (
                          <>
                            <Autocomplete
                              disablePortal
                              id="motivoRejeicao"
                              name="motivoRejeicao"
                              value={proposta?.motivoRejeicao || null}
                              onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                              options={motivos}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              getOptionLabel={(option) => `${option.nome}`}
                              renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                              sx={{ ...styleTextField, marginBottom: 2, width: '80%' }}
                            />
                            <TextField
                              id="obs"
                              label="Descrição"
                              variant="outlined"
                              value={proposta?.finalizado?.obs || ''}
                              multiline
                              rows={4}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({
                                  ...prevProposta,
                                  finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                }));
                              }}
                              sx={{ width: '80%', backgroundColor: 'white', mt: 1 }}
                            />
                          </>
                        )}
                      </Stack>
                    </Stack>
                  ) : null
            }

            <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
              <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                {' '}
                Cancelar
              </Button>
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={proposta.etapa.id === 0}
                variant="contained"
                onClick={handleBack}
                startIcon={<ArrowBackIosNewIcon />}
              >
                Anterior
              </Button>
              {proposta.etapa.id + 1 === steps.length ? (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={
                    modalLoading ||
                    props.visualiza ||
                    !proposta.finalizadoLead ||
                    (proposta.finalizadoLead?.nome === 'Lead Desqualificado' && !proposta.idMotivoRejeicao)
                  }
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  endIcon={<BeenhereOutlinedIcon />}
                >
                  Finalizar
                </Button>
              ) : (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  variant="contained"
                  disabled={
                    (!props.visualiza &&
                      proposta.etapa.nome === 'Revisão' &&
                      erro.tipo === 1 &&
                      !JSON.parse(localStorage.getItem('userData')).supervisor) ||
                    (proposta.etapa.nome === 'Revisão' && adequacaoOnGrid?.bloqueia)
                  }
                  onClick={handleNext}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Próximo
                </Button>
              )}
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={modalLoading || props.visualiza}
                variant="outlined"
                color="success"
                onClick={handleSaveDebug}
                startIcon={<SaveIcon />}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        ) : (proposta.produto.nome === 'Proposta Comercial' || proposta.produto.nome === 'Gerador On-Grid' || proposta.produto.nome === 'Gerador Grid-Zero') ? (
          <Box sx={style}>
            <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
              <Stepper alternativeLabel activeStep={proposta.etapa?.id} connector={<QontoConnector />}>
                {steps.map((step) => (
                  <Step key={step.id}>
                    <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
            {
              // Cadastro --------------
              proposta.etapa?.id === 0 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                  <CamposInstalacao
                    campos={['cliente', 'estado', 'cidade', 'superficie', 'orientacao', 'disjuntor', 'origem', 'unidade']}
                    proposta={proposta}
                    setProposta={setProposta}
                    rowsClientes={rowsClientes}
                    estados={estados}
                    cidades={cidades}
                    superficies={superficies}
                    orientacoes={orientacaotelhado}
                    tiposDisjuntor={tipodisjuntor}
                    potenciasDisjuntor={potenciadisjuntor}
                    origens={origem}
                    unidades={unidades}
                    isSameUnidade={isSameUnidade}
                    onAbrirModalCliente={() => setModalAddCliente(true)}
                  />

                  {proposta?.origem?.nome === 'Indicação' && (
                    <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                      <IconButtonWrapper>
                        <Autocomplete
                          disablePortal
                          id="Indicador"
                          value={proposta.indicador || null}
                          options={rowsIndicador}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            const digits = String(option.cpf || '').replace(/\D/g, '');
                            const masked = digits.length <= 11
                              ? digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '***.***.$3-**')
                              : digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '**.***/0001-**');
                            return `${option.nome} - ${masked}`;
                          }}
                          key={(option) => option._id}
                          sx={styleTextField}
                          renderInput={(params) => <TextField {...params} label="Indicador" />}
                          onChange={(event, newValue) => {
                            setProposta((prevProposta) => ({ ...prevProposta, indicador: newValue }));
                          }}
                        />
                        <IconButton aria-label="add" onClick={() => setModalAddIndicador(true)}>
                          <AddCircleIcon />
                        </IconButton>
                      </IconButtonWrapper>

                      <Autocomplete
                        disablePortal
                        id="tipoindicacao"
                        value={proposta.tipoindicacao || null}
                        options={tipoindicacao}
                        isOptionEqualToValue={(option, value) => option.nome === value.nome}
                        getOptionLabel={(option) => `${option.nome}`}
                        sx={styleTextField}
                        renderInput={(params) => <TextField {...params} label="Tipo Indicação" />}
                        onChange={(event, newValue) => {
                          setProposta((prevProposta) => ({ ...prevProposta, tipoindicacao: newValue }));
                        }}
                      />
                      {proposta?.tipoindicacao?.nome === 'Indicador PJ' && (
                        <Autocomplete
                          disablePortal
                          id="percentual"
                          value={proposta.percentualindicacao || null}
                          options={percentualindicacao}
                          isOptionEqualToValue={(option, value) => option === value}
                          getOptionLabel={(option) => `${option}`}
                          sx={styleTextField}
                          renderInput={(params) => <TextField {...params} label="Percentual Indicação" />}
                          onChange={(event, newValue) => {
                            setProposta((prevProposta) => ({ ...prevProposta, percentualindicacao: newValue }));
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Stack>
              ) : // Cadastro --------------
                // Necessidade --------------
                proposta.etapa?.id === 1 ? (
                  <Box sx={{ p: 3, width: '100%', maxWidth: 960, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: cores.textoClaro }}>
                      Consumo de Energia
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={2}>Faturas</Typography>
                      <Stack direction={'row'} flexWrap="wrap" gap={2}>
                        {proposta.faturas.map((fatura, index1) => (
                          <Paper key={index1} variant="outlined" sx={{ p: 2, minWidth: 220 }}>
                            <Stack alignItems={'center'} gap={1.5}>
                              <Button variant="contained" onClick={() => handleAddFatura(index1)} endIcon={<AddCircleIcon />} size="small">
                                Adicionar Fatura
                              </Button>
                              {fatura.map((fat, index2) => (
                                <Stack key={index2} direction={'row'} alignItems={'center'} gap={0.5} width="100%">
                                  <TextField
                                    size="small"
                                    id="fatura"
                                    label="Valor Fatura"
                                    inputProps={{ type: 'number' }}
                                    sx={{ ...styleTextField2, width: '100%' }}
                                    value={fat.valor}
                                    onChange={(e) => handleChangeFatura(e, index1, index2)}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                  <IconButton aria-label="remover fatura" size="small" onClick={(e) => removeFatura(e, index1, index2)}>
                                    <RemoveCircleIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ))}
                              {fatura.length === 0 ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleRemoveTipoFatura(index1)}
                                  endIcon={<RemoveCircleIcon />}
                                >
                                  Remover Grupo
                                </Button>
                              ) : null}
                            </Stack>
                          </Paper>
                        ))}
                        <Stack width={150} alignItems={'center'} justifyContent={'center'}>
                          <Button
                            variant="outlined"
                            onClick={handleAddTipoFatura}
                            endIcon={<AddCircleIcon />}
                            sx={{ color: cores.laranja, borderColor: cores.laranja }}
                          >
                            Adicionar Grupo
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={2}>Tarifa e Distribuidora</Typography>
                      <Stack direction={'row'} gap={2} flexWrap="wrap">
                        <TextField
                          size="small"
                          id="tarifaEnergia"
                          label="Tarifa de Energia (R$/kWh)"
                          inputProps={{ type: 'number', step: '0.01' }}
                          sx={{ ...styleTextField3, minWidth: 190 }}
                          value={proposta.tarifaEnergia || ''}
                          onChange={(e) => {
                            setProposta((prev) => ({ ...prev, tarifaEnergia: e.target.value }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Autocomplete
                          disablePortal
                          id="distribuidora"
                          value={proposta.distribuidora || null}
                          options={distribuidoras}
                          isOptionEqualToValue={(option, value) => option === value}
                          getOptionLabel={(option) => option}
                          sx={{ ...styleTextField3, minWidth: 190 }}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Distribuidora"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BusinessIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                          onChange={(event, newValue) => {
                            setProposta((prev) => ({ ...prev, distribuidora: newValue }));
                          }}
                        />
                      </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={2}>Instalação e Consumo</Typography>
                      <Stack direction={'row'} gap={2} flexWrap="wrap">
                        <Autocomplete
                          size={'small'}
                          disablePortal
                          disabled
                          id="tipoinstalacao"
                          value={proposta.disjuntorcliente.tipodisjuntor || null}
                          options={tipodisjuntor}
                          getOptionLabel={(option) => `${option.nome}`}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          sx={{ ...styleTextField3, minWidth: 190 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo Instalação"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <FlashOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />

                        <TextField
                          disabled
                          size="small"
                          id="mediaconsumo"
                          label="Média Consumo"
                          inputProps={{ type: 'number' }}
                          sx={{ ...styleTextField3, minWidth: 170 }}
                          value={proposta.mediaconsumo}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" sx={{ color: cores.laranja }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          disabled
                          size="small"
                          id="mediaconsumoabatida"
                          label="Média Consumo Abatida"
                          inputProps={{ type: 'number' }}
                          sx={{ ...styleTextField3, minWidth: 170 }}
                          value={proposta.mediaconsumoabatida}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" sx={{ color: cores.laranja }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Paper>
                    {erro?.tipo === 2 && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mt: 2,
                          borderColor: 'warning.main',
                          bgcolor: '#fff8e1',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              Kit disponível insuficiente
                            </Typography>
                            <Typography variant="body2">{erro.msg}</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}

                  </Box>
                ) : // Necessidade --------------
                  // Revisao --------------
                  proposta.etapa?.id === 2 ? (
                    <Box sx={{ p: 3, width: '100%', overflowY: 'auto' }}>
                      <AlertaAdequacaoKit
                        adequacao={adequacaoOnGrid}
                        alternativas={alternativasOnGrid}
                        onUsarAlternativa={usarAlternativaOnGrid}
                        onCadastrar={() => abrirCadastroKitContextual('ongrid', adequacaoOnGrid)}
                        disabled={props.visualiza}
                      />
                      {/* Filtro de Kit */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                          <TuneIcon fontSize="small" sx={{ color: cores.laranja }} />
                          <Typography variant="subtitle2" fontWeight={600}>Filtro de Kit</Typography>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              size="small" fullWidth
                              disabled
                              id="mediaconsumo"
                              label="Média Consumo"
                              value={proposta.mediaconsumoabatida}
                              inputProps={{ type: 'number' }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SpeedIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Autocomplete
                              disablePortal
                              id="fornecedor"
                              value={proposta.fornecedor || null}
                              options={fornecedores}
                              getOptionLabel={(option) => `${option.nome}`}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Fornecedor"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <StoreIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                              onChange={(option, value) => {
                                setProposta({ ...proposta, fornecedor: value, topologia: null, modulo: null, kit: null });
                                setInversorSelecionado(null);
                                setInversoresDisponiveis([]);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Autocomplete
                              disablePortal
                              id="modulo"
                              value={proposta.modulo}
                              options={modulos}
                              getOptionLabel={(option) => `${option.marca} ${option.potencia} ${option.modelo}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Módulo"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <GridOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                              onChange={(option, value) => {
                                setProposta({ ...proposta, modulo: value, topologia: null, kit: null });
                                setInversorSelecionado(null);
                                setInversoresDisponiveis([]);
                              }}
                              disabled={modulos.length === 1}
                            />
                          </Grid>
                          {(inversoresDisponiveis.length > 0 || proposta.topologia) && (
                            <Grid item xs={12} sm={3}>
                              <Autocomplete
                                disablePortal
                                id="inversorFiltro"
                                value={inversorSelecionado || proposta.topologia || null}
                                options={inversoresDisponiveis}
                                getOptionLabel={(option) => `${option.modelo || 'Inversor sem modelo'}`}
                                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                size="small"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Inversor"
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <MemoryIcon fontSize="small" sx={{ color: cores.laranja }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                                onChange={(option, value) => {
                                  setInversorSelecionado(value);
                                  setProposta((prev) => ({
                                    ...prev,
                                    topologia: value,
                                    kit: null,
                                  }));
                                }}
                                disabled={inversoresDisponiveis.length === 1}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Paper>

                      {proposta.kit ? (
                        <>
                          {/* Especificações do Kit Selecionado */}
                          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Stack direction="row" alignItems="center" gap={1} mb={1}>
                              <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="subtitle1" fontWeight={600}>Especificações do Kit Selecionado</Typography>
                            </Stack>
                            <Grid container spacing={1}>
                              {[
                                { label: 'Kit',                  value: proposta.kit?.nome,                Icone: WidgetsIcon },
                                { label: 'Qtd. Módulos',         value: proposta.kit?.quantidademodulos,    Icone: GridOnIcon },
                                { label: 'Modelo Módulo',        value: proposta.kit?.modulo?.modelo,       Icone: GridOnIcon },
                                { label: 'Qtd. Inversores',      value: proposta.kit?.quantidadeinversores, Icone: MemoryIcon },
                                { label: 'Modelo Inversor',      value: proposta.kit?.inversor?.modelo,     Icone: MemoryIcon },
                                { label: 'Potência Inversor',    value: proposta.kit?.inversor?.potencia,   Icone: MemoryIcon },
                                { label: 'Tipo Disjuntor',       value: proposta.kit?.disjuntorminino?.tipodisjuntor?.nome,     Icone: FlashOnIcon },
                                { label: 'Potência Disjuntor',   value: proposta.kit?.disjuntorminino?.potenciadisjuntor?.valor, Icone: FlashOnIcon },
                                { label: 'Valor Kit',            value: proposta.valorkit?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), Icone: AttachMoneyIcon },
                              ].map((i) => (
                                <Grid item xs={6} sm={3} key={i.label}>
                                  <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                    <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                      <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                    </Box>
                                  </Stack>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>

                          {/* Ajuste de Valor */}
                          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                              <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="subtitle2" fontWeight={600}>Ajuste de Valor</Typography>
                            </Stack>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" gap={1} alignItems="center">
                                  <TextField
                                    size="small" label="Desconto" type="number" id="desconto"
                                    value={proposta.desconto}
                                    onChange={onChangeDesconto}
                                    InputProps={{ endAdornment: <InputAdornment position="end">{tipoDesconto}</InputAdornment> }}
                                    sx={{ maxWidth: 160 }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={tipoDesconto}
                                      onChange={(e) => { setTipoDesconto(e.target.value); setProposta((prev) => ({ ...prev, desconto: 0 })); }}
                                    >
                                      <MenuItem value="%">%</MenuItem>
                                      <MenuItem value="R$">R$</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Stack>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Stack direction="row" gap={1} alignItems="center">
                                  <Stack direction="row" alignItems="center" gap={1}>
                                    <Checkbox
                                      size="small"
                                      checked={termoAcrescimoAceito}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          if (!proposta.acrescimo || proposta.acrescimo <= 0) {
                                            window.alert('Digite o valor do acréscimo antes de habilitar.');
                                            return;
                                          }
                                          setModalTermoAcrescimo(true);
                                        } else {
                                          setTermoAcrescimoAceito(false);
                                          setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                                        }
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                      Habilitar Acréscimo
                                    </Typography>
                                  </Stack>
                                  <TextField
                                    size="small" label="Acréscimo" type="number" id="acrescimo"
                                    value={proposta.acrescimo}
                                    onChange={onChangeAcrescimo}
                                    InputProps={{ endAdornment: <InputAdornment position="end">{tipoAcrescimo}</InputAdornment> }}
                                    sx={{ maxWidth: 160 }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={tipoAcrescimo}
                                      onChange={(e) => {
                                        setTipoAcrescimo(e.target.value);
                                        setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                                        setTermoAcrescimoAceito(false);
                                      }}
                                    >
                                      <MenuItem value="%">%</MenuItem>
                                      <MenuItem value="R$">R$</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Stack>
                              </Grid>
                            </Grid>
                            <Box mt={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                              <Chip label={`Valor Final: ${(proposta.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`} color="success" />
                              {user.supervisor && (
                                <Chip label={`Margem Lucro: ${proposta.margemlucro || 0}%`} color="info" variant="outlined" />
                              )}
                            </Box>
                          </Paper>

                          {/* Observação e Alertas */}
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                              <DescriptionIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="subtitle2" fontWeight={600}>Observação e Alertas</Typography>
                            </Stack>
                            <TextField
                              multiline rows={3} id="obs" label="Observação"
                              variant="outlined" value={proposta.obs}
                              onChange={(e) => setProposta({ ...proposta, obs: e.target.value })}
                              fullWidth
                            />
                            {erro ? (
                              <TextField
                                disabled error fullWidth
                                id="erro" label="Alerta" multiline rows={2}
                                defaultValue={erro.msg}
                                sx={{ mt: 2 }}
                              />
                            ) : null}
                            {erro2 ? (
                              <Stack
                                direction="row" alignItems="center" gap={1}
                                sx={{
                                  backgroundColor: '#fff3e0', border: '1.5px solid #e65100',
                                  borderRadius: 2, px: 2, py: 1.5, mt: 2,
                                }}
                              >
                                <WarningAmberIcon sx={{ color: '#e65100', fontSize: 28 }} />
                                <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                                  Necessária alteração de carga para instalação do sistema.
                                </Typography>
                              </Stack>
                            ) : null}
                          </Paper>
                        </>
                      ) : (
                        <TextField disabled error id="erro" label="Erro" multiline rows={4} defaultValue={erro.msg} fullWidth />
                      )}
                    </Box>
                  ) : // Revisao --------------
                    // Preview de Produção --------------
                    proposta.etapa?.id === 3 ? (
                      <PreviewProducao proposta={proposta} />
                    ) : // Preview de Produção --------------
                    // Gerar Proposta --------------
                    proposta.etapa?.id === 4 ? (
                      <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                          <Stack direction="row" alignItems="center" gap={1} mb={2}>
                            <DescriptionIcon sx={{ color: cores.laranja }} />
                            <Typography variant="h6" fontWeight={700}>Resumo da Proposta</Typography>
                          </Stack>

                          <Grid container spacing={2}>
                            {[
                              { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                              {
                                label: 'Instalação',
                                value: [proposta.localinstalacao?.cidade?.nome, proposta.localinstalacao?.estado?.sigla]
                                  .filter(Boolean).join(' - '),
                                Icone: LocationCityIcon,
                              },
                              { label: 'Kit', value: proposta.kit?.nome, Icone: WidgetsIcon },
                              {
                                label: 'Módulos / Inversores',
                                value: (proposta.kit?.quantidademodulos || proposta.kit?.quantidadeinversores)
                                  ? `${proposta.kit?.quantidademodulos || 0} módulo(s) · ${proposta.kit?.quantidadeinversores || 0} inversor(es)`
                                  : null,
                                Icone: GridOnIcon,
                              },
                              { label: 'Distribuidora', value: proposta.distribuidora, Icone: BusinessIcon },
                              {
                                label: 'Consumo Médio Abatido',
                                value: proposta.mediaconsumoabatida ? `${proposta.mediaconsumoabatida} kWh` : null,
                                Icone: SpeedIcon,
                              },
                            ].map((i) => (
                              <Grid item xs={12} sm={6} key={i.label}>
                                <Stack direction="row" alignItems="flex-start" gap={1}>
                                  <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                            <Chip
                              icon={<AttachMoneyIcon />}
                              label={`Valor Final: ${(proposta.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                            {proposta.desconto > 0 && (
                              <Chip label={`Desconto: ${proposta.desconto}${tipoDesconto}`} variant="outlined" />
                            )}
                            {proposta.acrescimoAceito && proposta.acrescimo > 0 && (
                              <Chip label={`Acréscimo: ${proposta.acrescimo}${tipoAcrescimo}`} variant="outlined" />
                            )}
                            {user.supervisor && (
                              <Chip label={`Margem Lucro: ${proposta.margemlucro || 0}%`} color="info" variant="outlined" />
                            )}
                          </Stack>

                          {proposta.obs ? (
                            <Box mt={2}>
                              <Typography variant="caption" color="text.secondary" display="block">Observação</Typography>
                              <Typography variant="body2">{proposta.obs}</Typography>
                            </Box>
                          ) : null}
                        </Paper>

                        <Stack alignItems={'center'} justifyContent={'center'} gap={2}>
                          {['Agendamento', 'Lead', 'Indicação'].includes(proposta.origem.nome) ? (
                            <Autocomplete
                              disablePortal
                              disabled={props.visualiza}
                              id="finalizado"
                              value={proposta.user || null}
                              options={usuarios}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={{ ...styleTextField, marginBottom: 2 }}
                              renderInput={(params) => <TextField {...params} label="Vendedor Externo" />}
                              onChange={(e, newValue) => handleVendedorExterno(newValue)}
                            />
                          ) : null}
                          <Button
                            variant="contained"
                            onClick={gerarPdf}
                            endIcon={<DownloadIcon />}
                            color={propostaGerada ? 'success' : 'primary'}
                          >
                            Gerar Proposta
                          </Button>
                        </Stack>
                      </Box>
                    ) : // Gerar Proposta --------------
                      // Finalizar Proposta --------------
                      proposta.etapa?.id === 5 ? (
                        <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                          {proposta.user2 ? (
                            <Autocomplete
                              disablePortal
                              id="vendedor abertura"
                              disabled
                              value={proposta.user2 || null}
                              options={usuarios}
                              isOptionEqualToValue={(option, value) => option._id === value._id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={{ ...styleTextField, marginBottom: 2 }}
                              renderInput={(params) => <TextField {...params} label="Vendedor Abertura" />}
                            />
                          ) : null}
                          <Autocomplete
                            disablePortal
                            disabled={props.visualiza}
                            id="finalizado"
                            value={proposta.finalizado || null}
                            options={finalizado}
                            isOptionEqualToValue={(option, value) => option.nome === value.nome}
                            getOptionLabel={(option) => `${option.nome}`}
                            sx={{ ...styleTextField, marginBottom: 2 }}
                            renderInput={(params) => <TextField {...params} label="Selecione" />}
                            onChange={(event, newValue) => {
                              setProposta((prevProposta) => ({ ...prevProposta, finalizado: newValue }));
                            }}
                          />
                          {proposta.finalizado?.nome === 'Rejeitado' && (
                            <>
                              <AutoCompleteMedium
                                disablePortal
                                id="motivoRejeicao"
                                name="motivoRejeicao"
                                value={proposta?.motivoRejeicao || null}
                                onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                                options={motivos}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                getOptionLabel={(option) => `${option.nome}`}
                                renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                              />
                              <TextField
                                id="obs"
                                label="Descrição"
                                variant="outlined"
                                value={proposta?.finalizado?.obs || ''}
                                multiline
                                rows={4}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({
                                    ...prevProposta,
                                    finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                  }));
                                }}
                                sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                              />
                            </>
                          )}
                        </Stack>
                      ) : // Finalizar Proposta --------------
                        null
            }

            <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
              <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                {' '}
                Cancelar
              </Button>
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={proposta.etapa.id === 0}
                variant="contained"
                onClick={handleBack}
                startIcon={<ArrowBackIosNewIcon />}
              >
                Anterior
              </Button>
              {proposta.etapa.id + 1 === steps.length ? (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={
                    modalLoading ||
                    props.visualiza ||
                    (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)
                  }
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  endIcon={<BeenhereOutlinedIcon />}
                >
                  Finalizar
                </Button>
              ) : (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  variant="contained"
                  disabled={
                    !props.visualiza &&
                    proposta.etapa.nome === 'Revisão' &&
                    erro.tipo === 1 &&
                    !JSON.parse(localStorage.getItem('userData')).supervisor
                  }
                  onClick={handleNext}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Próximo
                </Button>
              )}
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={modalLoading || props.visualiza}
                variant="outlined"
                color="success"
                onClick={handleSaveDebug}
                startIcon={<SaveIcon />}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        ) : // -------------------------------------------------------
          // Modal para proposta comercial -------------------------

          // Modal para Sistema de Bombeamento ---------------------
          // -------------------------------------------------------
          proposta.produto.nome === 'Sistema de Bombeamento' ? (
          <Box sx={style}>
            <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
              <Stepper alternativeLabel activeStep={proposta.etapa?.id} connector={<QontoConnector />}>
                {steps.map((step) => (
                  <Step key={step.id}>
                    <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
            {
              // ── Etapa 0: Cadastro ──────────────────────────────────────────────────
              proposta.etapa?.id === 0 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                  <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                    <CamposInstalacao
                      campos={['cliente', 'estado', 'cidade', 'origem', 'unidade']}
                      proposta={proposta}
                      setProposta={setProposta}
                      rowsClientes={rowsClientes}
                      estados={estados}
                      cidades={cidades}
                      origens={origem}
                      unidades={unidades}
                      isSameUnidade={isSameUnidade}
                      onAbrirModalCliente={() => setModalAddCliente(true)}
                    />

                    {/* Superfície fixada em Solo para bombeamento */}
                    <TextField
                      disabled
                      size="small"
                      label="Tipo de Estrutura"
                      value="Solo"
                      sx={styleTextField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TerrainIcon fontSize="small" sx={{ color: cores.laranja }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  {proposta?.origem?.nome === 'Indicação' && (
                    <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                      <IconButtonWrapper>
                        <Autocomplete
                          disablePortal
                          id="indicador-bomb"
                          value={proposta.indicador || null}
                          options={rowsIndicador}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            const digits = String(option.cpf || '').replace(/\D/g, '');
                            const masked = digits.length <= 11
                              ? digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '***.***.$3-**')
                              : digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '**.***/0001-**');
                            return `${option.nome} - ${masked}`;
                          }}
                          sx={styleTextField}
                          renderInput={(params) => <TextField {...params} label="Indicador" />}
                          onChange={(event, newValue) => {
                            setProposta((prev) => ({ ...prev, indicador: newValue }));
                          }}
                        />
                        <IconButton aria-label="add" onClick={() => setModalAddIndicador(true)}>
                          <AddCircleIcon />
                        </IconButton>
                      </IconButtonWrapper>
                      <Autocomplete
                        disablePortal
                        id="tipoindicacao-bomb"
                        value={proposta.tipoindicacao || null}
                        options={tipoindicacao}
                        isOptionEqualToValue={(option, value) => option.nome === value.nome}
                        getOptionLabel={(option) => `${option.nome}`}
                        sx={styleTextField}
                        renderInput={(params) => <TextField {...params} label="Tipo Indicação" />}
                        onChange={(event, newValue) => {
                          setProposta((prev) => ({ ...prev, tipoindicacao: newValue }));
                        }}
                      />
                      {proposta?.tipoindicacao?.nome === 'Indicador PJ' && (
                        <Autocomplete
                          disablePortal
                          id="percentual-bomb"
                          value={proposta.percentualindicacao || null}
                          options={percentualindicacao}
                          isOptionEqualToValue={(option, value) => option === value}
                          getOptionLabel={(option) => `${option}`}
                          sx={styleTextField}
                          renderInput={(params) => <TextField {...params} label="Percentual Indicação" />}
                          onChange={(event, newValue) => {
                            setProposta((prev) => ({ ...prev, percentualindicacao: newValue }));
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Stack>

              // ── Etapa 1: Dimensionamento ───────────────────────────────────────────
              ) : proposta.etapa?.id === 1 ? (
                <DimensionamentoBombeamento
                  proposta={proposta}
                  onVoltar={() => setProposta((prev) => ({ ...prev, etapa: steps[0] }))}
                  onDimensionado={(dados) => {
                    setProposta((prev) => ({
                      ...prev,
                      mediaconsumo:        dados.dimensionamento.consumoMensal,
                      dadosBombeamento:    dados.inputs,
                      dimensionamentoBomb: dados.dimensionamento,
                      pr:                  dados.dimensionamento.rendFV,
                      etapa:               steps[2], // avança para Revisão do Kit
                    }));
                  }}
                />

              // ── Etapa 2: Revisão do Kit ────────────────────────────────────────────
              ) : proposta.etapa?.id === 2 ? (
                <Box sx={{ p: 3, width: '100%', overflowY: 'auto' }}>
                  <AlertaAdequacaoKit
                    adequacao={adequacaoBombeamento}
                    alternativas={alternativasBombeamento}
                    onUsarAlternativa={usarAlternativaBombeamento}
                    onCadastrar={() => abrirCadastroKitContextual('bombeamento', adequacaoBombeamento)}
                    disabled={props.visualiza}
                  />
                  {/* Filtro de Kit */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                      <TuneIcon fontSize="small" sx={{ color: cores.laranja }} />
                      <Typography variant="subtitle2" fontWeight={600}>Filtro de Kit</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          disablePortal
                          id="fornecedor-bomb"
                          value={proposta.fornecedor || null}
                          options={fornecedores}
                          getOptionLabel={(option) => `${option.nome}`}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Fornecedor"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <StoreIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                          onChange={(option, value) => {
                            setProposta({ ...proposta, fornecedor: value, kit: null, valorkit: 0, valor: 0 });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          disablePortal
                          id="modulo-bomb"
                          value={proposta.modulo || null}
                          options={[
                            ...new Map(
                              kitsBombeamento
                                .filter((k) => !proposta.fornecedor || k.fornecedor?._id === proposta.fornecedor?._id)
                                .map((k) => k.modulo)
                                .filter(Boolean)
                                .map((m) => [m._id, m])
                            ).values()
                          ]}
                          getOptionLabel={(option) => `${option.marca || ''} ${option.potencia || ''} ${option.modelo || ''}`.trim()}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Módulo"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <GridOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                          onChange={(event, value) => setProposta({ ...proposta, modulo: value, kit: null })}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Resumo do Dimensionamento */}
                  {proposta.dimensionamentoBomb && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>Resumo do Dimensionamento</Typography>
                      <Grid container spacing={1.5}>
                        {[
                          { label: 'Potência Motor',        value: `${proposta.dadosBombeamento?.potenciaMotorCV} CV`, Icone: SettingsIcon },
                          { label: 'Tensão',                value: `${proposta.dadosBombeamento?.tensaoOperacao} V`, Icone: FlashOnIcon },
                          { label: 'Altura Manométrica',    value: `${proposta.dadosBombeamento?.alturaManometrica} m`, Icone: ArrowUpwardIcon },
                          { label: 'Potência CC',           value: `${proposta.dimensionamentoBomb?.potCC} kW`, Icone: PowerIcon },
                          { label: 'Vazão Ajustada Média',  value: `${proposta.dimensionamentoBomb?.meses ? (proposta.dimensionamentoBomb.meses.reduce((acc, m) => acc + (m.vazaoHoraria || 0), 0) / 12).toFixed(2) : '–'} m³/h`, Icone: OpacityIcon },
                          { label: 'Consumo Mensal Médio',  value: `${proposta.dimensionamentoBomb?.consumoMensal} kWh/mês`, Icone: SpeedIcon },
                          { label: 'Economia Mensal Média', value: proposta.dimensionamentoBomb?.meses ? (proposta.dimensionamentoBomb.meses.reduce((acc, m) => acc + (m.economiaMes || 0), 0) / 12).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '–', Icone: AttachMoneyIcon },
                          { label: 'HSP Mínimo',            value: `${proposta.dimensionamentoBomb?.hspMin} h/dia`, Icone: WbSunnyIcon },
                          { label: 'Dias de Operação',      value: `${proposta.dimensionamentoBomb?.diasOperacao || 30} dias`, Icone: CalendarMonth },
                          { label: 'Horas de Operação',     value: `${proposta.dimensionamentoBomb?.horasOperacao || 8} h/dia`, Icone: AccessTimeIcon },
                        ].map((kpi) => (
                          <Grid item xs={6} sm={4} key={kpi.label}>
                            <Paper variant="outlined" sx={{ p: 1.2, textAlign: 'center' }}>
                              <kpi.Icone fontSize="small" sx={{ color: cores.laranja, mb: 0.25 }} />
                              <Typography variant="caption" color="text.secondary" display="block">{kpi.label}</Typography>
                              <Typography variant="subtitle2" fontWeight={700}>{kpi.value}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {proposta.dimensionamentoBomb?.potCC && proposta.kit && (() => {
                    const potNec = Number(proposta.dimensionamentoBomb?.potCC) || 0;
                    const potKit = normalizarPotenciaKit(proposta.kit?.potenciacc);
                    if (!Number.isFinite(potKit) || potKit >= potNec) return null;
                    return (
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'warning.main', bgcolor: '#fff8e1' }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>Kit disponível insuficiente</Typography>
                            <Typography variant="body2">
                              O dimensionamento requer {potNec.toFixed(2)} kWp, porém o kit selecionado possui {potKit.toFixed(2)} kWp. O TriAxis selecionou o maior kit compatível disponível; revise o dimensionamento ou cadastre um kit de maior potência antes de concluir a proposta.
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })()}

                  {/* Especificações do Kit Selecionado */}
                  {proposta.kit ? (
                    <>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1}>
                          <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                          <Typography variant="subtitle1" fontWeight={600}>Especificações do Kit Selecionado</Typography>
                        </Stack>
                        <Grid container spacing={1}>
                          {[
                            { label: 'Kit',           value: proposta.kit?.nome,       Icone: WidgetsIcon },
                            { label: 'Potência Kit',  value: `${proposta.kit?.potenciacc} kWp`, Icone: PowerIcon },
                            { label: 'Qtd. Módulos',  value: proposta.kit?.quantidademodulos, Icone: GridOnIcon },
                            { label: 'Modelo Módulo', value: proposta.kit?.modulo?.modelo, Icone: GridOnIcon },
                            { label: 'Inversor',      value: proposta.kit?.inversor?.modelo, Icone: MemoryIcon },
                            { label: 'Valor Kit',     value: proposta.valorkit?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), Icone: AttachMoneyIcon },
                          ].map((i) => (
                            <Grid item xs={6} sm={3} key={i.label}>
                              <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                  <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>

                      {/* Ajuste de Valor */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                          <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                          <Typography variant="subtitle2" fontWeight={600}>Ajuste de Valor</Typography>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" gap={1} alignItems="center">
                              <TextField
                                size="small" label="Desconto" type="number" id="desconto-bomb"
                                value={proposta.desconto || ''}
                                onChange={(e) => setProposta((prev) => ({ ...prev, desconto: parseFloat(e.target.value) || 0 }))}
                                InputProps={{ endAdornment: <InputAdornment position="end">{tipoDesconto}</InputAdornment> }}
                                sx={{ maxWidth: 160 }}
                              />
                              <FormControl size="small" sx={{ minWidth: 60 }}>
                                <Select
                                  value={tipoDesconto}
                                  onChange={(e) => { setTipoDesconto(e.target.value); setProposta((prev) => ({ ...prev, desconto: 0 })); }}
                                >
                                  <MenuItem value="%">%</MenuItem>
                                  <MenuItem value="R$">R$</MenuItem>
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" gap={1} alignItems="center">
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Checkbox
                                  size="small"
                                  checked={termoAcrescimoAceito}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (!proposta.acrescimo || proposta.acrescimo <= 0) {
                                        window.alert('Digite o valor do acréscimo antes de habilitar.');
                                        return;
                                      }
                                      setModalTermoAcrescimo(true);
                                    } else {
                                      setTermoAcrescimoAceito(false);
                                      setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                                    }
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                  Habilitar Acréscimo
                                </Typography>
                              </Stack>
                              <TextField
                                size="small" label="Acréscimo" type="number" id="acrescimo-bomb"
                                value={proposta.acrescimo || ''}
                                onChange={(e) => {
                                  setProposta((prev) => ({ ...prev, acrescimo: parseFloat(e.target.value) || 0, acrescimoAceito: false }));
                                  setTermoAcrescimoAceito(false);
                                }}
                                InputProps={{ endAdornment: <InputAdornment position="end">{tipoAcrescimo}</InputAdornment> }}
                                sx={{ maxWidth: 160 }}
                              />
                              <FormControl size="small" sx={{ minWidth: 60 }}>
                                <Select
                                  value={tipoAcrescimo}
                                  onChange={(e) => {
                                    setTipoAcrescimo(e.target.value);
                                    setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                                    setTermoAcrescimoAceito(false);
                                  }}
                                >
                                  <MenuItem value="%">%</MenuItem>
                                  <MenuItem value="R$">R$</MenuItem>
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>
                        </Grid>
                        <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                          <Chip
                            icon={<AttachMoneyIcon />}
                            label={`Valor Final: ${(proposta.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                            color="success"
                          />
                        </Box>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                          <DescriptionIcon fontSize="small" sx={{ color: cores.laranja }} />
                          <Typography variant="subtitle2" fontWeight={600}>Observação</Typography>
                        </Stack>
                        <TextField
                          multiline rows={3} id="obs-bomb" label="Observação"
                          variant="outlined" value={proposta.obs || ''}
                          onChange={(e) => setProposta({ ...proposta, obs: e.target.value })}
                          fullWidth
                        />
                      </Paper>
                    </>
                  ) : loadingKitsBomb ? (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Carregando kits...</Typography>
                    </Box>
                  ) : (
                    <TextField disabled error label="Alerta" value="Nenhum kit encontrado para o filtro selecionado." sx={{ width: '60%' }} />
                  )}
                </Box>

              // ── Etapa 3: Preview de Produção ───────────────────────────────────────
              ) : proposta.etapa?.id === 3 ? (
                <PreviewProducaoBombeamento proposta={proposta} />

              // ── Etapa 4: Gerar Proposta ────────────────────────────────────────────
              ) : proposta.etapa?.id === 4 ? (
                <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                  <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={2}>
                      <DescriptionIcon sx={{ color: cores.laranja }} />
                      <Typography variant="h6" fontWeight={700}>Resumo da Proposta</Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      {[
                        { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                        {
                          label: 'Instalação',
                          value: [proposta.localinstalacao?.cidade?.nome, proposta.localinstalacao?.estado?.sigla]
                            .filter(Boolean).join(' - '),
                          Icone: LocationCityIcon,
                        },
                        { label: 'Kit', value: proposta.kit?.nome, Icone: WidgetsIcon },
                        {
                          label: 'Potência Motor',
                          value: proposta.dadosBombeamento?.potenciaMotorCV ? `${proposta.dadosBombeamento.potenciaMotorCV} CV` : null,
                          Icone: SettingsIcon,
                        },
                        {
                          label: 'Vazão Ajustada Média',
                          value: proposta.dimensionamentoBomb?.meses
                            ? `${(proposta.dimensionamentoBomb.meses.reduce((acc, m) => acc + (m.vazaoHoraria || 0), 0) / 12).toFixed(2)} m³/h`
                            : null,
                          Icone: OpacityIcon,
                        },
                        {
                          label: 'Consumo Mensal Médio',
                          value: proposta.dimensionamentoBomb?.consumoMensal ? `${proposta.dimensionamentoBomb.consumoMensal} kWh/mês` : null,
                          Icone: SpeedIcon,
                        },
                      ].map((i) => (
                        <Grid item xs={12} sm={6} key={i.label}>
                          <Stack direction="row" alignItems="flex-start" gap={1}>
                            <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                              <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                      <Chip
                        icon={<AttachMoneyIcon />}
                        label={`Valor Final: ${(proposta.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                      {proposta.desconto > 0 && (
                        <Chip label={`Desconto: ${proposta.desconto}${tipoDesconto}`} variant="outlined" />
                      )}
                      {proposta.acrescimoAceito && proposta.acrescimo > 0 && (
                        <Chip label={`Acréscimo: ${proposta.acrescimo}${tipoAcrescimo}`} variant="outlined" />
                      )}
                      {user.supervisor && (
                        <Chip label={`Margem Lucro: ${proposta.margemlucro || 0}%`} color="info" variant="outlined" />
                      )}
                    </Stack>

                    {proposta.obs ? (
                      <Box mt={2}>
                        <Typography variant="caption" color="text.secondary" display="block">Observação</Typography>
                        <Typography variant="body2">{proposta.obs}</Typography>
                      </Box>
                    ) : null}
                  </Paper>

                  <Stack alignItems={'center'} justifyContent={'center'} gap={2}>
                    <Button
                      variant="contained"
                      onClick={gerarPdfBombeamento}
                      endIcon={<DownloadIcon />}
                      color={propostaGerada ? 'success' : 'primary'}
                    >
                      Gerar Proposta
                    </Button>
                  </Stack>
                </Box>

              // ── Etapa 5: Finalizar ─────────────────────────────────────────────────
              ) : proposta.etapa?.id === 5 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                  <Autocomplete
                    disablePortal
                    disabled={props.visualiza}
                    id="finalizado-bomb"
                    value={proposta.finalizado || null}
                    options={finalizado}
                    isOptionEqualToValue={(option, value) => option.nome === value.nome}
                    getOptionLabel={(option) => `${option.nome}`}
                    sx={{ ...styleTextField, marginBottom: 2 }}
                    renderInput={(params) => <TextField {...params} label="Selecione" />}
                    onChange={(event, newValue) => {
                      setProposta((prev) => ({ ...prev, finalizado: newValue }));
                    }}
                  />
                  {proposta.finalizado?.nome === 'Rejeitado' && (
                    <>
                      <AutoCompleteMedium
                        disablePortal
                        id="motivoRejeicao-bomb"
                        name="motivoRejeicao"
                        value={proposta?.motivoRejeicao || null}
                        onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                        options={motivos}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        getOptionLabel={(option) => `${option.nome}`}
                        renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                      />
                      <TextField
                        id="obs-bomb-final"
                        label="Descrição"
                        variant="outlined"
                        value={proposta?.finalizado?.obs || ''}
                        multiline
                        rows={4}
                        onChange={(event) => {
                          setProposta((prev) => ({
                            ...prev,
                            finalizado: { ...prev?.finalizado, obs: event.target.value },
                          }));
                        }}
                        sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                      />
                    </>
                  )}
                </Stack>
              ) : null
            }

            <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
              <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                Cancelar
              </Button>
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={proposta.etapa.id === 0 || proposta.etapa.id === 1}
                variant="contained"
                onClick={handleBack}
                startIcon={<ArrowBackIosNewIcon />}
              >
                Anterior
              </Button>
              {proposta.etapa.id + 1 === steps.length ? (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={modalLoading || props.visualiza || (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)}
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  endIcon={<BeenhereOutlinedIcon />}
                >
                  Finalizar
                </Button>
              ) : proposta.etapa.id === 1 ? (
                // Na etapa de Dimensionamento o botão Próximo não existe — o DimensionamentoBombeamento tem o próprio
                null
              ) : (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={proposta.etapa.id === 2 && adequacaoBombeamento?.bloqueia}
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Próximo
                </Button>
              )}
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={modalLoading || props.visualiza}
                variant="outlined"
                color="success"
                onClick={handleSaveDebug}
                startIcon={<SaveIcon />}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        ) : // -------------------------------------------------------
          // Modal para Sistema de Bombeamento ---------------------

          // Modal para Gerador Off-Grid ---------------------------
          // -------------------------------------------------------
          proposta.produto.nome === 'Gerador Off-Grid' ? (
          <Box sx={style}>
            <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
              <Stepper alternativeLabel activeStep={proposta.etapa?.id} connector={<QontoConnector />}>
                {steps.map((step) => (
                  <Step key={step.id}>
                    <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
            {
              // ── Etapa 0: Cadastro ──────────────────────────────────────────────────
              proposta.etapa?.id === 0 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                  <CamposInstalacao
                    campos={['cliente', 'estado', 'cidade', 'origem', 'unidade']}
                    proposta={proposta}
                    setProposta={setProposta}
                    rowsClientes={rowsClientes}
                    estados={estados}
                    cidades={cidades}
                    origens={origem}
                    unidades={unidades}
                    isSameUnidade={isSameUnidade}
                    onAbrirModalCliente={() => setModalAddCliente(true)}
                  />

                  {proposta.origem?.nome === 'Indicação' && (
                    <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                      <IconButtonWrapper>
                        <Autocomplete
                          disablePortal
                          id="indicador-offgrid"
                          value={proposta.indicador || null}
                          options={rowsIndicador}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          getOptionLabel={(option) => {
                            const masked = option.telefone ? option.telefone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3') : '';
                            return `${option.nome} - ${masked}`;
                          }}
                          sx={styleTextField}
                          renderInput={(params) => <TextField {...params} label="Indicador" />}
                          onChange={(event, newValue) => {
                            setProposta((prev) => ({ ...prev, indicador: newValue }));
                          }}
                        />
                        <IconButton aria-label="add" onClick={() => setModalAddIndicador(true)}>
                          <AddCircleIcon />
                        </IconButton>
                      </IconButtonWrapper>
                      <Autocomplete
                        disablePortal
                        id="tipoindicacao-offgrid"
                        value={proposta.tipoindicacao || null}
                        options={tipoindicacao}
                        isOptionEqualToValue={(option, value) => option.nome === value.nome}
                        getOptionLabel={(option) => option.nome}
                        sx={styleTextField}
                        renderInput={(params) => <TextField {...params} label="Tipo Indicação" />}
                        onChange={(event, newValue) => {
                          setProposta((prev) => ({ ...prev, tipoindicacao: newValue }));
                        }}
                      />
                    </Stack>
                  )}
                </Stack>

              // ── Etapa 1: Dimensionamento Off-Grid ─────────────────────────────────
              ) : proposta.etapa?.id === 1 ? (
                <DimensionamentoOffGrid
                  proposta={proposta}
                  onVoltar={() => setProposta((prev) => ({ ...prev, etapa: steps[0] }))}
                  onDimensionado={(dados) => {
                    setDimOffGrid(dados);
                    setKitManualOG(null);
                    setKitSugeridoOG(null);
                    setDescontoOG('');
                    setAcrescimoOG('');
                    setAcrescimoAceitoOG(false);
                    setProposta((prev) => ({
                      ...prev,
                      mediaconsumo:           dados.mediaMensal,
                      mediaconsumoabatida:    0,
                      dadosOffGrid:           dados,
                      dimensionamentoOffGrid: dados,
                      pr:                    dados.pr,
                      etapa:                  steps[2],
                    }));
                  }}
                />

              // ── Etapa 2: Revisão ───────────────────────────────────────────────────
              ) : proposta.etapa?.id === 2 ? (
                <Box sx={{ p: 3, width: '100%', overflowY: 'auto' }}>
                  <AlertaAdequacaoKit
                    adequacao={adequacaoOffGrid}
                    alternativas={alternativasOffGrid}
                    onUsarAlternativa={usarAlternativaOffGrid}
                    onCadastrar={() => abrirCadastroKitContextual('offgrid', adequacaoOffGrid)}
                    disabled={props.visualiza}
                  />
                  {/* Resumo do Dimensionamento */}
                  {dimOffGrid && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>Resumo do Dimensionamento</Typography>
                      <Grid container spacing={1.5}>
                        {[
                          { label: 'Média de Consumo',      value: `${dimOffGrid.mediaMensal?.toFixed(2)} kWh/mês`, Icone: SpeedIcon },
                          { label: 'Autonomia Necessária',   value: `${dimOffGrid.autonomia?.toFixed(2)} kWh/dia`, Icone: BatteryChargingFullIcon },
                          { label: 'Potência CC Necessária', value: `${dimOffGrid.potenciaCC?.toFixed(2)} kWp`, Icone: PowerIcon },
                          { label: 'HSP Médio',              value: `${dimOffGrid.hspMedio?.toFixed(2)} h/dia`, Icone: WbSunnyIcon },
                          { label: 'Tipo de Cliente',        value: dimOffGrid.tipoCliente, Icone: PersonIcon },
                          { label: '% Noturno',              value: `${((dimOffGrid.pctNoturno || 0) * 100).toFixed(0)}%`, Icone: Brightness3Icon },
                        ].map(kpi => (
                          <Grid item xs={6} sm={4} key={kpi.label}>
                            <Paper variant="outlined" sx={{ p: 1.2, textAlign: 'center' }}>
                              <kpi.Icone fontSize="small" sx={{ color: cores.laranja, mb: 0.25 }} />
                              <Typography variant="caption" color="text.secondary" display="block">{kpi.label}</Typography>
                              <Typography variant="subtitle2" fontWeight={700}>{kpi.value}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {/* Kit Sugerido */}
                  {loadingKitsOG && (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Buscando kit ideal...</Typography>
                    </Box>
                  )}
                  
                  {kitSugeridoOG && !kitManualOG && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'success.main' }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Kit Sugerido: {kitSugeridoOG.nome}
                        </Typography>
                      </Stack>
                      <Grid container spacing={1}>
                        {[
                          { label: 'Potência CC',     value: `${kitSugeridoOG.potenciacc} kWp`, Icone: PowerIcon },
                          { label: 'Autonomia',       value: `${kitSugeridoOG.bateria?.autonomia || 0} kWh`, Icone: BatteryChargingFullIcon },
                          { label: 'Aut. Líquida',    value: `${((kitSugeridoOG.bateria?.autonomia || 0) * (dimOffGrid?.dod || 0.9) * (dimOffGrid?.rte || 0.9)).toFixed(2)} kWh`, Icone: BatteryChargingFullIcon },
                          { label: 'Fornecedor',      value: kitSugeridoOG.fornecedor?.nome || '–', Icone: StoreIcon },
                          { label: 'Módulo',          value: `${kitSugeridoOG.modulo?.qtde || 1}× ${kitSugeridoOG.modulo?.modelo || '–'}`, Icone: GridOnIcon },
                          { label: 'Inversor',        value: `${kitSugeridoOG.inversor?.qtde || 1}× ${kitSugeridoOG.inversor?.modelo || '–'}`, Icone: MemoryIcon },
                          { label: 'Bateria',         value: `${kitSugeridoOG.bateria?.qtde || 1}× ${kitSugeridoOG.bateria?.modelo || '–'}`, Icone: BatteryChargingFullIcon },
                          { label: 'Valor à Vista',   value: (kitSugeridoOG.precovenda || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), Icone: AttachMoneyIcon },
                        ].map(i => (
                          <Grid item xs={6} sm={3} key={i.label}>
                            <Stack direction="row" alignItems="flex-start" gap={0.75}>
                              <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                <Typography variant="body2" fontWeight={600}>{i.value}</Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {/* Seleção Manual de Kit */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                      <TuneIcon fontSize="small" sx={{ color: cores.laranja }} />
                      <Typography variant="subtitle2" fontWeight={600}>Selecionar Kit Manualmente</Typography>
                    </Stack>
                    <Grid container spacing={1.5} mb={2}>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" fullWidth
                          options={[...new Map(kitsOffGrid.filter(k => k.fornecedor?._id).map(k => [k.fornecedor._id, k.fornecedor])).values()]}
                          getOptionLabel={(o) => o.nome || ''}
                          isOptionEqualToValue={(o, v) => o._id === v._id}
                          value={filtroFornecedorOG}
                          onChange={(_, v) => setFiltroFornecedorOG(v)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Fornecedor"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <StoreIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" fullWidth
                          options={[...new Map(kitsOffGrid.filter(k => k.modulo?._id).map(k => [k.modulo._id, k.modulo])).values()]}
                          getOptionLabel={(o) => `${o.marca || ''} ${o.potencia || ''} ${o.modelo || ''}`.trim()}
                          isOptionEqualToValue={(o, v) => o._id === v._id}
                          value={filtroModuloOG}
                          onChange={(_, v) => setFiltroModuloOG(v)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Módulo"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <GridOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" fullWidth
                          options={[...new Map(kitsOffGrid.filter(k => k.bateria?._id).map(k => [k.bateria._id, k.bateria])).values()]}
                          getOptionLabel={(o) => `${o.modelo || ''} ${o.autonomia ? `— ${o.autonomia} kWh` : ''}`.trim()}
                          isOptionEqualToValue={(o, v) => o._id === v._id}
                          value={filtroBateriaOG}
                          onChange={(_, v) => setFiltroBateriaOG(v)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bateria"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BatteryChargingFullIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['Nome','Fornecedor','kWp','Autonomia','Aut. Líquida','Valor',''].map(h => (
                              <TableCell key={h}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {kitsOffGrid
                            .filter(k => {
                              if (filtroFornecedorOG && k.fornecedor?._id !== filtroFornecedorOG._id) return false;
                              if (filtroModuloOG     && k.modulo?._id     !== filtroModuloOG._id)     return false;
                              if (filtroBateriaOG    && k.bateria?._id    !== filtroBateriaOG._id)    return false;
                              return true;
                            })
                            .map(k => {
                              const autLiq = ((k.bateria?.autonomia || 0) * (dimOffGrid?.dod || 0.9) * (dimOffGrid?.rte || 0.9)).toFixed(2);
                              const sel = kitManualOG?._id === k._id;
                              return (
                                <TableRow key={k._id} selected={sel}
                                  sx={{ cursor: 'pointer', '&.Mui-selected': { background: '#FFF3E0' } }}
                                  onClick={() => {
                                    setKitManualOG(k);
                                    setKitSugeridoOG(null);
                                    setProposta(prev => ({ ...prev, kit: k, valorkit: k.precovenda || 0, valor: k.precovenda || 0 }));
                                  }}
                                >
                                  <TableCell>{k.nome}</TableCell>
                                  <TableCell>{k.fornecedor?.nome || '–'}</TableCell>
                                  <TableCell>{k.potenciacc}</TableCell>
                                  <TableCell>{k.bateria?.autonomia || 0} kWh</TableCell>
                                  <TableCell>{autLiq} kWh</TableCell>
                                  <TableCell>{(k.precovenda || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                                  <TableCell>{sel && <Chip label="Selecionado" size="small" color="primary" />}</TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>

                  {/* Desconto / Acréscimo */}
                  {(kitManualOG || kitSugeridoOG) && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                        <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                        <Typography variant="subtitle2" fontWeight={600}>Ajuste de Valor</Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" gap={1} alignItems="center">
                            <TextField size="small" label="Desconto" type="number"
                              value={descontoOG}
                              onChange={e => setDescontoOG(e.target.value)}
                              InputProps={{ endAdornment: <InputAdornment position="end">{tipoDescontoOG}</InputAdornment> }}
                              sx={{ maxWidth: 160 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                              <Select value={tipoDescontoOG} onChange={e => { setTipoDescontoOG(e.target.value); setDescontoOG(''); }}>
                                <MenuItem value="%">%</MenuItem>
                                <MenuItem value="R$">R$</MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" gap={1} alignItems="center">
                            <TextField size="small" label="Acréscimo" type="number"
                              value={acrescimoOG}
                              onChange={e => { setAcrescimoOG(e.target.value); setAcrescimoAceitoOG(false); }}
                              InputProps={{ endAdornment: <InputAdornment position="end">{tipoAcrescimoOG}</InputAdornment> }}
                              sx={{ maxWidth: 160 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                              <Select value={tipoAcrescimoOG} onChange={e => { setTipoAcrescimoOG(e.target.value); setAcrescimoOG(''); setAcrescimoAceitoOG(false); }}>
                                <MenuItem value="%">%</MenuItem>
                                <MenuItem value="R$">R$</MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>
                        </Grid>
                      </Grid>
                      {/* Valor final */}
                      {(() => {
                        const kitEf = kitManualOG || kitSugeridoOG;
                        let base = kitEf?.precovenda || 0;
                        if (acrescimoOG && acrescimoAceitoOG) base = tipoAcrescimoOG === '%' ? base * (1 + Number(acrescimoOG) / 100) : base + Number(acrescimoOG);
                        if (descontoOG)  base = tipoDescontoOG  === '%' ? base * (1 - Number(descontoOG)  / 100) : base - Number(descontoOG);
                        base = Math.max(base, 0);
                        return (
                          <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                            <Chip
                              icon={<AttachMoneyIcon />}
                              label={`Valor Final: ${base.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                              color="success"
                            />
                            <Chip
                              icon={<CreditCardIcon />}
                              label={`Cartão: ${(base * ((kitManualOG || kitSugeridoOG)?.fatorCartao || 1.1428)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                              color="info"
                              variant="outlined"
                            />
                          </Box>
                        );
                      })()}
                    </Paper>
                  )}

                  {/* Termo acréscimo off-grid */}
                  <Dialog open={termoAbertoOG} onClose={() => setTermoAbertoOG(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Termo de Responsabilidade</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2">
                        Estou ciente de que o aumento inadequado do valor de venda deste orçamento pode
                        ocasionar na perda do negócio, podendo afetar meus resultados e os resultados da empresa.
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button color="error" onClick={() => { setTermoAbertoOG(false); setAcrescimoOG(''); }}>Cancelar</Button>
                      <Button variant="contained" onClick={() => { setAcrescimoAceitoOG(true); setTermoAbertoOG(false); }}>Aceito</Button>
                    </DialogActions>
                  </Dialog>
                </Box>

              // ── Etapa 3: Preview de Produção ───────────────────────────────────────
              ) : proposta.etapa?.id === 3 ? (
                <Box sx={{ p: 3, width: '100%', overflowY: 'auto' }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1}>
                    <BarChartIcon sx={{ color: cores.laranja }} />
                    <Typography variant="h6" fontWeight={700}>Preview de Produção</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Os gráficos abaixo serão incluídos na proposta comercial.
                  </Typography>

                  {/* Gráfico Mensal */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>Produção Mensal × Economia Mensal</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={producaoMesesOG} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left"  orientation="left"  stroke={cores.laranja} tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#f57c00" tick={{ fontSize: 11 }} />
                        <RTooltip formatter={(v, n) => n === 'Produção (kWh)' ? [`${v.toFixed(1)} kWh`, n] : [v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), n]} />
                        <Legend />
                        <Bar yAxisId="left"  dataKey="producao" name="Produção (kWh)" fill={cores.laranja} />
                        <Bar yAxisId="right" dataKey="economia" name="Economia (R$)"  fill="#f57c00" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>

                  {/* Gráfico Anual */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>Produção Anual (25 anos)</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={producaoAnualOG} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ano" tick={{ fontSize: 10 }} interval={1} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RTooltip formatter={(v) => [`${v.toFixed(0)} kWh`, 'Produção']} />
                        <Legend />
                        <Bar dataKey="producao" name="Produção (kWh)" fill="#2e7d32" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>

                  {/* Resumo do kit */}
                  {(kitManualOG || kitSugeridoOG) && (() => {
                    const kitEf = kitManualOG || kitSugeridoOG;
                    let base = kitEf?.precovenda || 0;
                    if (acrescimoOG && acrescimoAceitoOG) base = tipoAcrescimoOG === '%' ? base * (1 + Number(acrescimoOG) / 100) : base + Number(acrescimoOG);
                    if (descontoOG)  base = tipoDescontoOG  === '%' ? base * (1 - Number(descontoOG)  / 100) : base - Number(descontoOG);
                    base = Math.max(base, 0);
                    return (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1}>
                          <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                          <Typography variant="subtitle2" fontWeight={600}>Kit Selecionado</Typography>
                        </Stack>
                        <Grid container spacing={1}>
                          {[
                            { label: 'Nome',           value: kitEf.nome || '–', Icone: WidgetsIcon },
                            { label: 'Potência (kWp)', value: kitEf.potenciacc || '–', Icone: PowerIcon },
                            { label: 'Valor Final',    value: base.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), Icone: AttachMoneyIcon },
                            { label: 'Valor Cartão',   value: (base * (kitEf.fatorCartao || 1.1428)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), Icone: CreditCardIcon },
                          ].map(i => (
                            <Grid item xs={6} sm={3} key={i.label}>
                              <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                  <Typography variant="body2" fontWeight={600}>{i.value}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    );
                  })()}
                </Box>

              // ── Etapa 4: Gerar Proposta ────────────────────────────────────────────
              ) : proposta.etapa?.id === 4 ? (
                <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                  {(() => {
                    const kitEf = kitManualOG || kitSugeridoOG;
                    let base = kitEf?.precovenda || 0;
                    if (acrescimoOG && acrescimoAceitoOG) base = tipoAcrescimoOG === '%' ? base * (1 + Number(acrescimoOG) / 100) : base + Number(acrescimoOG);
                    if (descontoOG)  base = tipoDescontoOG  === '%' ? base * (1 - Number(descontoOG)  / 100) : base - Number(descontoOG);
                    base = Math.max(base, 0);
                    return (
                      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={2}>
                          <DescriptionIcon sx={{ color: cores.laranja }} />
                          <Typography variant="h6" fontWeight={700}>Resumo da Proposta</Typography>
                        </Stack>

                        <Grid container spacing={2}>
                          {[
                            { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                            {
                              label: 'Instalação',
                              value: [proposta.localinstalacao?.cidade?.nome, proposta.localinstalacao?.estado?.sigla]
                                .filter(Boolean).join(' - '),
                              Icone: LocationCityIcon,
                            },
                            { label: 'Kit', value: kitEf?.nome, Icone: WidgetsIcon },
                            { label: 'Potência CC', value: kitEf?.potenciacc ? `${kitEf.potenciacc} kWp` : null, Icone: PowerIcon },
                            {
                              label: 'Autonomia da Bateria',
                              value: kitEf?.bateria?.autonomia ? `${kitEf.bateria.autonomia} kWh` : null,
                              Icone: BatteryChargingFullIcon,
                            },
                            { label: 'Fornecedor', value: kitEf?.fornecedor?.nome, Icone: StoreIcon },
                          ].map((i) => (
                            <Grid item xs={12} sm={6} key={i.label}>
                              <Stack direction="row" alignItems="flex-start" gap={1}>
                                <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                  <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                </Box>
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                          <Chip
                            icon={<AttachMoneyIcon />}
                            label={`Valor Final: ${base.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            icon={<CreditCardIcon />}
                            label={`Cartão: ${(base * (kitEf?.fatorCartao || 1.1428)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                            variant="outlined"
                          />
                          {descontoOG > 0 && (
                            <Chip label={`Desconto: ${descontoOG}${tipoDescontoOG}`} variant="outlined" />
                          )}
                          {acrescimoAceitoOG && acrescimoOG > 0 && (
                            <Chip label={`Acréscimo: ${acrescimoOG}${tipoAcrescimoOG}`} variant="outlined" />
                          )}
                        </Stack>

                        {proposta.obs ? (
                          <Box mt={2}>
                            <Typography variant="caption" color="text.secondary" display="block">Observação</Typography>
                            <Typography variant="body2">{proposta.obs}</Typography>
                          </Box>
                        ) : null}
                      </Paper>
                    );
                  })()}

                  <Stack alignItems={'center'} justifyContent={'center'} gap={2}>
                    <Button
                      variant="contained"
                      onClick={gerarPdfOffGrid}
                      endIcon={<DownloadIcon />}
                      color={propostaGerada ? 'success' : 'primary'}
                    >
                      Gerar Proposta Off-Grid
                    </Button>
                  </Stack>
                </Box>

              // ── Etapa 5: Finalizar ─────────────────────────────────────────────────
              ) : proposta.etapa?.id === 5 ? (
                <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                  <Autocomplete
                    disablePortal
                    disabled={props.visualiza}
                    id="finalizado-offgrid"
                    value={proposta.finalizado || null}
                    options={finalizado}
                    isOptionEqualToValue={(option, value) => option.nome === value.nome}
                    getOptionLabel={(option) => option.nome}
                    sx={{ ...styleTextField, marginBottom: 2 }}
                    renderInput={(params) => <TextField {...params} label="Selecione" />}
                    onChange={(event, newValue) => {
                      setProposta((prev) => ({ ...prev, finalizado: newValue }));
                    }}
                  />
                  {proposta.finalizado?.nome === 'Rejeitado' && (
                    <>
                      <AutoCompleteMedium
                        disablePortal
                        id="motivoRejeicao-offgrid"
                        name="motivoRejeicao"
                        value={proposta?.motivoRejeicao || null}
                        onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                        options={motivos}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        getOptionLabel={(option) => option.nome}
                        renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                      />
                      <TextField
                        id="obs-offgrid-final"
                        label="Descrição"
                        variant="outlined"
                        value={proposta?.finalizado?.obs || ''}
                        multiline
                        rows={4}
                        onChange={(event) => {
                          setProposta((prev) => ({
                            ...prev,
                            finalizado: { ...prev?.finalizado, obs: event.target.value },
                          }));
                        }}
                        sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                      />
                    </>
                  )}
                </Stack>
              ) : null
            }

            <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
              <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                Cancelar
              </Button>
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={proposta.etapa.id === 0 || proposta.etapa.id === 1}
                variant="contained"
                onClick={handleBack}
                startIcon={<ArrowBackIosNewIcon />}
              >
                Anterior
              </Button>
              {proposta.etapa.id + 1 === steps.length ? (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={modalLoading || props.visualiza || (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)}
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  endIcon={<BeenhereOutlinedIcon />}
                >
                  Finalizar
                </Button>
              ) : proposta.etapa.id === 1 ? (
                // Na etapa de Dimensionamento o botão Próximo não existe — DimensionamentoOffGrid tem o próprio
                null
              ) : proposta.etapa.id === 2 ? (
                // Na etapa de Revisão: salva o kit antes de avançar
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  variant="contained"
                  disabled={(!kitManualOG && !kitSugeridoOG) || adequacaoOffGrid?.bloqueia}
                  onClick={() => {
                    if (acrescimoOG && !acrescimoAceitoOG) { setTermoAbertoOG(true); return; }
                    const kitEf = kitManualOG || kitSugeridoOG;
                    let base = kitEf?.precovenda || 0;
                    if (acrescimoOG && acrescimoAceitoOG) base = tipoAcrescimoOG === '%' ? base * (1 + Number(acrescimoOG) / 100) : base + Number(acrescimoOG);
                    if (descontoOG)  base = tipoDescontoOG  === '%' ? base * (1 - Number(descontoOG)  / 100) : base - Number(descontoOG);
                    base = Math.max(base, 0);
                    setProposta(prev => ({
                      ...prev,
                      kit:      kitEf,
                      valorkit: kitEf.precovenda || 0,
                      valor:    base,
                      dimensionamentoOffGrid: dimOffGrid,
                      etapa:    steps[3],
                    }));
                  }}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Próximo
                </Button>
              )}
              <Button
                sx={sxFontBotaoMobile}
                size={'small'}
                disabled={modalLoading || props.visualiza}
                variant="outlined"
                color="success"
                onClick={handleSaveDebug}
                startIcon={<SaveIcon />}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        ) : // -------------------------------------------------------
          // Modal para Gerador Off-Grid ---------------------------

          // Modal para seguro -------------------------------------
          // -------------------------------------------------------
          proposta.produto.nome === 'Seguro' ? (
            <Box sx={style}>
              <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
                <Stepper alternativeLabel activeStep={proposta.etapa.id} connector={<QontoConnector />}>
                  {steps.map((step) => (
                    <Step key={step.id}>
                      <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>
              {
                // Cadastro --------------
                proposta.etapa.id === 0 ? (
                  <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                    <CamposInstalacao
                      campos={['cliente', 'estado', 'cidade']}
                      proposta={proposta}
                      setProposta={setProposta}
                      rowsClientes={rowsClientes}
                      estados={estados}
                      cidades={cidades}
                      onAbrirModalCliente={() => setModalAddCliente(true)}
                    />

                    <TextField
                      id="valorprojeto"
                      label="Valor Projeto"
                      inputProps={{ type: 'number' }}
                      sx={styleTextField}
                      value={proposta.valorprojeto}
                      onChange={(event) => {
                        setProposta((prevProposta) => ({
                          ...prevProposta,
                          valorprojeto: event.target.value === '' ? null : parseFloat(event.target.value),
                        }));
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptLocale}>
                      <DatePicker
                        open={openCalendar}
                        onOpen={handleCalendarOpen}
                        onClose={handleCalendarClose}
                        value={proposta.datainstalacao}
                        onChange={handleDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Data Instalação"
                            sx={styleTextField}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarMonth fontSize="small" sx={{ color: cores.laranja }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <Autocomplete
                      disablePortal
                      id="unidade"
                      value={proposta.unidade || null}
                      options={unidades}
                      sx={styleTextField}
                      isOptionEqualToValue={isSameUnidade}
                      getOptionLabel={(option) => `${option.nome}`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unidade"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <ApartmentIcon fontSize="small" sx={{ color: cores.laranja }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                      onChange={(event, newValue) => {
                        setProposta((prevProposta) => ({ ...prevProposta, unidade: newValue }));
                      }}
                    />
                  </Stack>
                ) : // Cadastro --------------
                  // Necessidade --------------
                  proposta.etapa.id === 1 ? (
                    <Box sx={{ p: 3, width: '100%', maxWidth: 520, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <AttachFileIcon fontSize="small" sx={{ color: cores.laranja }} />
                            <Typography variant="subtitle1" fontWeight={600}>Anexos</Typography>
                          </Stack>
                          <Button
                            onClick={addAnexo}
                            variant="outlined"
                            size="small"
                            startIcon={<AddCircleIcon />}
                            sx={{ color: cores.laranja, borderColor: cores.laranja }}
                          >
                            Adicionar Anexo
                          </Button>
                        </Stack>
                        <Stack gap={1}>
                          {anexo?.length ? anexo.map((anexod, index) => (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              gap={1}
                              onClick={() => downloadAnexo(anexod)}
                              sx={{
                                p: 1, borderRadius: 1, border: '1px solid #e0e0e0',
                                '&:hover': { backgroundColor: '#FFF3E0', cursor: 'pointer' },
                              }}
                            >
                              <InsertDriveFileIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="body2">
                                {`${anexod.nome}.${anexod.ext}`}
                              </Typography>
                            </Stack>
                          )) : (
                            <Typography variant="body2" color="text.secondary">Nenhum anexo adicionado.</Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Box>
                  ) : // Necessidade --------------
                    // Revisao --------------
                    proposta.etapa.id === 2 ? (
                      <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                            <DescriptionIcon fontSize="small" sx={{ color: cores.laranja }} />
                            <Typography variant="subtitle1" fontWeight={600}>Resumo do Cadastro</Typography>
                          </Stack>
                          <Grid container spacing={1.5} sx={sxFontBotaoMobile}>
                            {[
                              { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                              {
                                label: 'Local Instalação',
                                value: `${proposta.localinstalacao?.cidade.nome} - ${proposta.localinstalacao?.estado.sigla}`,
                                Icone: LocationCityIcon,
                              },
                              {
                                label: 'Data Instalação',
                                value: new Date(proposta.datainstalacao).toLocaleDateString(),
                                Icone: CalendarMonth,
                              },
                              {
                                label: 'Valor Projeto',
                                value: parseFloat(proposta.valorprojeto).toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }),
                                Icone: AttachMoneyIcon,
                              },
                            ].map((i) => (
                              <Grid item xs={6} sm={3} key={i.label}>
                                <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                  <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                            <CreditCardIcon fontSize="small" sx={{ color: cores.laranja }} />
                            <Typography variant="subtitle1" fontWeight={600}>Opções de Parcelamento</Typography>
                          </Stack>
                          <Stack direction={'column'} gap={2} sx={sxFontBotaoMobile}>
                            {duracoes.map((anos) => (
                              <LinhaSeguro
                                key={anos}
                                anos={anos}
                                valorProjeto={proposta.valorprojeto}
                                sxFontBotaoMobile={sxFontBotaoMobile}
                              />
                            ))}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            * Valor das parcelas
                          </Typography>
                        </Paper>
                      </Box>
                    ) : // Revisao --------------
                      // Gerar Proposta --------------
                      proposta.etapa.id === 3 ? (
                        <Box sx={{ p: 3, width: '100%', maxWidth: 620, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                              <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="subtitle1" fontWeight={600}>Resumo da Proposta</Typography>
                            </Stack>
                            <Grid container spacing={1.5}>
                              {[
                                { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                                {
                                  label: 'Local Instalação',
                                  value: `${proposta.localinstalacao?.cidade.nome} - ${proposta.localinstalacao?.estado.sigla}`,
                                  Icone: LocationCityIcon,
                                },
                                {
                                  label: 'Data Instalação',
                                  value: new Date(proposta.datainstalacao).toLocaleDateString(),
                                  Icone: CalendarMonth,
                                },
                                {
                                  label: 'Valor Projeto',
                                  value: parseFloat(proposta.valorprojeto).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }),
                                  Icone: AttachMoneyIcon,
                                },
                              ].map((i) => (
                                <Grid item xs={6} sm={3} key={i.label}>
                                  <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                    <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                      <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                    </Box>
                                  </Stack>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                          <Stack direction={'row'} alignItems={'center'} justifyContent={'center'}>
                            <Button
                              variant="contained"
                              onClick={gerarPdfSeguro}
                              endIcon={<DownloadIcon />}
                              color={propostaGerada ? 'success' : 'primary'}
                            >
                              Gerar Proposta
                            </Button>
                          </Stack>
                        </Box>
                      ) : // Gerar Proposta --------------
                        // Finalizar Proposta --------------
                        proposta.etapa.id === 4 ? (
                          <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                            <Autocomplete
                              disablePortal
                              id="anos"
                              value={proposta.negociacao?.anos || null}
                              options={anos}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={{ ...styleTextField, marginBottom: 2 }}
                              renderInput={(params) => <TextField {...params} label="Anos" />}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({
                                  ...prevProposta,
                                  negociacao: { ...prevProposta.negociacao, anos: newValue },
                                }));
                              }}
                            />

                            <Autocomplete
                              disablePortal
                              id="parcelas"
                              value={proposta.negociacao?.parcelas || null}
                              options={parcelas}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={{ ...styleTextField, marginBottom: 2 }}
                              renderInput={(params) => <TextField {...params} label="Parcelas" />}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({
                                  ...prevProposta,
                                  negociacao: { ...prevProposta.negociacao, parcelas: newValue },
                                }));
                              }}
                            />

                            <TextField
                              disabled
                              id="valor"
                              label="Valor Total"
                              variant="outlined"
                              sx={styleTextField}
                              value={(proposta.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || ''}
                            />

                            <TextField
                              disabled
                              id="valor"
                              label="Valor Parcela"
                              variant="outlined"
                              sx={styleTextField}
                              value={
                                (proposta.valorParcela || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || ''
                              }
                            />

                            <Autocomplete
                              disablePortal
                              id="finalizado"
                              value={proposta.finalizado || null}
                              options={finalizado}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={{ ...styleTextField, marginBottom: 2 }}
                              renderInput={(params) => <TextField {...params} label="Selecione" />}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({ ...prevProposta, finalizado: newValue }));
                              }}
                            />
                            {proposta.finalizado?.nome === 'Rejeitado' && (
                              <>
                                <AutoCompleteMedium
                                  disablePortal
                                  id="motivoRejeicao"
                                  name="motivoRejeicao"
                                  value={proposta?.motivoRejeicao || null}
                                  onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                                  options={motivos}
                                  isOptionEqualToValue={(option, value) => option._id === value._id}
                                  getOptionLabel={(option) => `${option.nome}`}
                                  renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                                />
                                <TextField
                                  id="obs"
                                  label="Descrição"
                                  variant="outlined"
                                  value={proposta?.finalizado?.obs || ''}
                                  multiline
                                  rows={4}
                                  onChange={(event, newValue) => {
                                    setProposta((prevProposta) => ({
                                      ...prevProposta,
                                      finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                    }));
                                  }}
                                  sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                                />
                              </>
                            )}
                          </Stack>
                        ) : // Finalizar Proposta --------------
                          null
              }

              <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
                <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                  {' '}
                  Cancelar
                </Button>
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={proposta.etapa.id === 0}
                  variant="contained"
                  onClick={handleBack}
                  startIcon={<ArrowBackIosNewIcon />}
                >
                  Anterior
                </Button>
                {proposta.etapa.id + 1 === steps.length ? (
                  <Button
                    sx={sxFontBotaoMobile}
                    size={'small'}
                    disabled={
                      modalLoading ||
                      props.visualiza ||
                      (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)
                    }
                    variant="contained"
                    color="success"
                    onClick={handleConfirm}
                    endIcon={<BeenhereOutlinedIcon />}
                  >
                    Finalizar
                  </Button>
                ) : (
                  <Button
                    sx={sxFontBotaoMobile}
                    size={'small'}
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIosIcon />}
                  >
                    Próximo
                  </Button>
                )}
                <Button
                  sx={sxFontBotaoMobile}
                  size={'small'}
                  disabled={modalLoading || props.visualiza}
                  variant="outlined"
                  color="success"
                  onClick={handleConfirm}
                  startIcon={<SaveIcon />}
                >
                  Salvar
                </Button>
              </Stack>
            </Box>
          ) : // -------------------------------------------------------
            // Modal para seguro -------------------------------------
            // Modal para limpeza -------------------------
            proposta.produto.nome === 'Limpeza' ? (
              <Box sx={style}>
                <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
                  <Stepper alternativeLabel activeStep={proposta.etapa.id} connector={<QontoConnector />}>
                    {steps.map((step) => (
                      <Step key={step.id}>
                        <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Stack>
                {
                  // Cadastro --------------
                  proposta.etapa.id === 0 ? (
                    <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                      <IconButtonWrapper>
                        <Autocomplete
                          disablePortal
                          id="cliente"
                          value={proposta.cliente || null}
                          options={rowsClientes}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          getOptionLabel={(option) => `${option.nome} - ${option.cpf}`} // sem _id visualmente
                          renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                              {option.nome} - {option.cpf}
                            </li>
                          )}
                          renderInput={(params) => <TextField {...params} label="Cliente" />}
                          onChange={(event, newValue) => {
                            setProposta((prevProposta) => ({ ...prevProposta, cliente: newValue }));
                          }}
                          sx={styleTextField}
                        />

                        <IconButton aria-label="add" onClick={() => setModalAddCliente(true)}>
                          {
                            proposta.cliente ? (
                              <EditIcon />
                            ) : (
                              <AddCircleIcon />
                            )
                          }
                        </IconButton>
                        {proposta.cliente && !proposta.cliente.cpf && (
                          <Tooltip title="Complete o cadastro do cliente">
                            <WarningAmberIcon color="warning" />
                          </Tooltip>
                        )}
                      </IconButtonWrapper>

                      <Autocomplete
                        disablePortal
                        id="estadolocalizacao"
                        value={proposta.localinstalacao?.estado || null}
                        options={estados}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => `${option.nome} - ${option.sigla}`}
                        sx={styleTextField}
                        renderInput={(params) => <TextField {...params} label="Estado Instalação" />}
                        onChange={(event, newValue) => {
                          setProposta((prevProposta) => ({
                            ...prevProposta,
                            localinstalacao: { ...prevProposta.localinstalacao, estado: newValue },
                          }));
                        }}
                      />

                      <Autocomplete
                        disablePortal
                        id="cidadeinstalacao"
                        value={proposta.localinstalacao?.cidade || null}
                        options={cidades}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => `${option.nome}`}
                        sx={styleTextField}
                        renderInput={(params) => <TextField {...params} label="Cidade Instalação" />}
                        onChange={(event, newValue) => {
                          setProposta((prevProposta) => ({
                            ...prevProposta,
                            localinstalacao: { ...prevProposta.localinstalacao, cidade: newValue },
                          }));
                        }}
                      />

                      <TextField
                        id="quantidadepaineis"
                        label="Quantidade Painéis"
                        inputProps={{ type: 'number' }}
                        variant="outlined"
                        sx={styleTextField}
                        value={proposta.quantidadepaineis || ''}
                        onChange={(event) => {
                          setProposta((prevProposta) => ({
                            ...prevProposta,
                            quantidadepaineis: event.target.value === '' ? null : parseFloat(event.target.value),
                          }));
                        }}
                      />

                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptLocale}>
                        <DatePicker
                          open={openCalendar}
                          onOpen={handleCalendarOpen}
                          onClose={handleCalendarClose}
                          value={proposta.dataagendamento}
                          onChange={handleDateChange2}
                          renderInput={(params) => <TextField {...params} label="Data Agendamento" sx={styleTextField} />}
                        />
                      </LocalizationProvider>
                    </Stack>
                  ) : // Cadastro --------------
                    // Necessidade --------------
                    proposta.etapa.id === 1 ? (
                      <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                        <Stack alignItems={'center'} sx={{ width: '50%', height: '90%', border: 1, borderRadius: 2 }}>
                          <Stack alignItems={'center'} borderRadius={2} sx={{ backgroundColor: 'white', width: '100%' }}>
                            <b>Anexos</b>
                          </Stack>
                          <Button onClick={addAnexo} variant="contained" sx={{ mt: 2 }}>
                            Adicionar Anexo
                          </Button>
                          <Container>
                            <Stack sx={{ width: '100%', borderRadius: 2, mt: 2 }}>
                              {anexo?.map((anexod, index) => {
                                return (
                                  <Typography
                                    key={index}
                                    onClick={() => downloadAnexo(anexod)}
                                    variant="subtitle2"
                                    sx={{
                                      backgroundColor: 'white',
                                      ml: 1,
                                      mb: 1,
                                      '&:hover': { backgroundColor: 'lightgray', cursor: 'pointer' },
                                    }}
                                  >
                                    {`${index + 1} - ${anexod.nome}.${anexod.ext}`}
                                  </Typography>
                                );
                              })}
                            </Stack>
                          </Container>
                        </Stack>
                      </Stack>
                    ) : // Necessidade --------------
                      // Revisao --------------
                      proposta.etapa.id === 2 ? (
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                          <Stack gap={2}>
                            <TextField
                              size="small"
                              id="nomecliente"
                              label="Nome Cliente"
                              value={proposta.cliente?.nome}
                              variant="filled"
                              sx={styleTextField2}
                              inputProps={{ sx: sxFontBotaoMobile }}
                            />
                            <TextField
                              size="small"
                              id="localinstalacao"
                              label="Local Instalação"
                              value={`${proposta.localinstalacao?.cidade.nome} - ${proposta.localinstalacao?.estado.sigla}`}
                              variant="filled"
                              sx={styleTextField2}
                              inputProps={{ sx: sxFontBotaoMobile }}
                            />
                            <TextField
                              size="small"
                              id="quantidadepaineis"
                              label="Quantidade Paineis"
                              value={`${proposta.quantidadepaineis}`}
                              variant="filled"
                              sx={styleTextField2}
                            />
                            <TextField
                              size="small"
                              id="valormanutencao"
                              label="Valor Manutenção"
                              value={parseFloat(proposta.quantidadepaineis * 15).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                              variant="filled"
                              sx={styleTextField2}
                            />
                          </Stack>
                          <Stack gap={2}>
                            <TextField
                              size="small"
                              id="avista"
                              label="A Vista"
                              value={parseFloat(proposta.quantidadepaineis * 15 * 0.9).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                              variant="filled"
                              sx={styleTextField3}
                            />
                            <TextField
                              size="small"
                              id="dividido"
                              label="2 parcelas"
                              value={parseFloat((proposta.quantidadepaineis * 15) / 2).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                              variant="filled"
                              sx={styleTextField3}
                            />
                            <TextField
                              size="small"
                              id="dividido"
                              label="3 parcelas"
                              value={parseFloat((proposta.quantidadepaineis * 15) / 3).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                              variant="filled"
                              sx={styleTextField3}
                            />
                          </Stack>
                        </Stack>
                      ) : // Revisao --------------
                        // Gerar Proposta --------------
                        proposta.etapa.id === 3 ? (
                          <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                            <Button
                              variant="contained"
                              onClick={gerarPdfLimpeza}
                              endIcon={<DownloadIcon />}
                              color={propostaGerada ? 'success' : 'primary'}
                            >
                              Gerar Proposta
                            </Button>
                          </Stack>
                        ) : // Gerar Proposta --------------
                          // Finalizar Proposta --------------
                          proposta.etapa.id === 4 ? (
                            <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                              <Autocomplete
                                disablePortal
                                id="parcelas"
                                value={proposta.negociacao?.parcelas || null}
                                options={parcelasLimpeza}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => `${option.nome}`}
                                sx={{ ...styleTextField, marginBottom: 2 }}
                                renderInput={(params) => <TextField {...params} label="Parcelas" />}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({
                                    ...prevProposta,
                                    negociacao: { ...prevProposta.negociacao, parcelas: newValue },
                                  }));
                                }}
                              />

                              <TextField
                                disabled
                                id="valor"
                                label="Valor"
                                variant="outlined"
                                sx={styleTextField}
                                value={proposta.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || ''}
                              />

                              <Autocomplete
                                disablePortal
                                id="finalizado"
                                value={proposta.finalizado || null}
                                options={finalizado}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => `${option.nome}`}
                                sx={{ ...styleTextField, marginBottom: 2 }}
                                renderInput={(params) => <TextField {...params} label="Selecione" />}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({ ...prevProposta, finalizado: newValue }));
                                }}
                              />
                              {proposta.finalizado?.nome === 'Rejeitado' && (
                                <>
                                  <AutoCompleteMedium
                                    disablePortal
                                    id="motivoRejeicao"
                                    name="motivoRejeicao"
                                    value={proposta?.motivoRejeicao || null}
                                    onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                                    options={motivos}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    getOptionLabel={(option) => `${option.nome}`}
                                    renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                                  />
                                  <TextField
                                    id="obs"
                                    label="Descrição"
                                    variant="outlined"
                                    value={proposta?.finalizado?.obs || ''}
                                    multiline
                                    rows={4}
                                    onChange={(event, newValue) => {
                                      setProposta((prevProposta) => ({
                                        ...prevProposta,
                                        finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                      }));
                                    }}
                                    sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                                  />
                                </>
                              )}
                            </Stack>
                          ) : // Finalizar Proposta --------------
                            null
                }

                <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
                  <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                    {' '}
                    Cancelar
                  </Button>
                  <Button
                    sx={sxFontBotaoMobile}
                    size={'small'}
                    disabled={proposta.etapa.id === 0}
                    variant="contained"
                    onClick={handleBack}
                    startIcon={<ArrowBackIosNewIcon />}
                  >
                    Anterior
                  </Button>
                  {proposta.etapa.id + 1 === steps.length ? (
                    <Button
                      sx={sxFontBotaoMobile}
                      size={'small'}
                      disabled={
                        modalLoading ||
                        props.visualiza ||
                        (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)
                      }
                      variant="contained"
                      color="success"
                      onClick={handleConfirm}
                      endIcon={<BeenhereOutlinedIcon />}
                    >
                      Finalizar
                    </Button>
                  ) : (
                    <Button
                      sx={sxFontBotaoMobile}
                      size={'small'}
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForwardIosIcon />}
                    >
                      Próximo
                    </Button>
                  )}
                  <Button
                    sx={sxFontBotaoMobile}
                    size={'small'}
                    disabled={modalLoading || props.visualiza}
                    variant="outlined"
                    color="success"
                    onClick={handleConfirm}
                    startIcon={<SaveIcon />}
                  >
                    Salvar
                  </Button>
                </Stack>
              </Box>
            ) : // Modal para limpeza -------------------------
              // Modal para Proposta de Serviços -------------------------
              (proposta.produto.nome === 'Serviços' || proposta.produto.nome === 'Proposta de Serviços') ? (
                <Box sx={style}>
                  <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
                    <Stepper alternativeLabel activeStep={proposta.etapa.id} connector={<QontoConnector />}>
                      {steps.map((step) => (
                        <Step key={step.id}>
                          <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Stack>
                  {
                    // Cadastro --------------
                    proposta.etapa.id === 0 ? (
                      <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                        <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                          <CamposInstalacao
                            campos={['cliente', 'estado', 'cidade']}
                            proposta={proposta}
                            setProposta={setProposta}
                            rowsClientes={rowsClientes}
                            estados={estados}
                            cidades={cidades}
                            onAbrirModalCliente={() => setModalAddCliente(true)}
                          />

                          {proposta.localinstalacao?.cidade && proposta.localinstalacao?.cidade?.nome !== 'Araguari' && (
                            <TextField
                              size="small"
                              id="kmrodado"
                              label="KM Rodaddo"
                              value={proposta.kmrodado}
                              inputProps={{ type: 'number' }}
                              sx={styleTextField}
                              onChange={(e) => {
                                setProposta({ ...proposta, kmrodado: e.target.value });
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <ExploreIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}

                          <Autocomplete
                            disablePortal
                            id="origem"
                            value={proposta.origem || null}
                            options={origem}
                            isOptionEqualToValue={(option, value) => option.nome === value.nome}
                            getOptionLabel={(option) => `${option.nome}`}
                            sx={styleTextField}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Forma Captação"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FlagIcon fontSize="small" sx={{ color: cores.laranja }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                            onChange={(event, newValue) => {
                              setProposta((prevProposta) => ({ ...prevProposta, origem: newValue }));
                            }}
                          />
                          <Autocomplete
                            disablePortal
                            id="unidade"
                            value={proposta.unidade || null}
                            options={unidades}
                            sx={styleTextField}
                            isOptionEqualToValue={isSameUnidade}
                            getOptionLabel={(option) => `${option.nome}`}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Unidade"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <ApartmentIcon fontSize="small" sx={{ color: cores.laranja }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                            onChange={(event, newValue) => {
                              setProposta((prevProposta) => ({ ...prevProposta, unidade: newValue }));
                            }}
                          />
                        </Stack>

                        {proposta?.origem?.nome === 'Indicação' && (
                          <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                            <IconButtonWrapper>
                              <Autocomplete
                                disablePortal
                                id="Indicador"
                                value={proposta.indicador || null}
                                options={rowsIndicador}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => `${option.nome} - ${option.cpf}`}
                                key={(option) => option._id}
                                sx={styleTextField}
                                renderInput={(params) => <TextField {...params} label="Indicador" />}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({ ...prevProposta, indicador: newValue }));
                                }}
                              />
                              <IconButton aria-label="add" onClick={() => setModalAddIndicador(true)}>
                                <AddCircleIcon />
                              </IconButton>
                            </IconButtonWrapper>

                            <Autocomplete
                              disablePortal
                              id="tipoindicacao"
                              value={proposta.tipoindicacao || null}
                              options={tipoindicacao}
                              isOptionEqualToValue={(option, value) => option.nome === value.nome}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={styleTextField}
                              renderInput={(params) => <TextField {...params} label="Tipo Indicação" />}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({ ...prevProposta, tipoindicacao: newValue }));
                              }}
                            />
                            {proposta?.tipoindicacao?.nome === 'Indicador PJ' && (
                              <Autocomplete
                                disablePortal
                                id="percentual"
                                value={proposta.percentualindicacao || null}
                                options={percentualindicacao}
                                isOptionEqualToValue={(option, value) => option === value}
                                getOptionLabel={(option) => `${option}`}
                                sx={styleTextField}
                                renderInput={(params) => <TextField {...params} label="Percentual Indicação" />}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({ ...prevProposta, percentualindicacao: newValue }));
                                }}
                              />
                            )}
                          </Stack>
                        )}
                      </Stack>
                    ) : // Cadastro --------------
                      // Necessidade --------------
                      proposta.etapa.id === 1 ? (
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          alignItems={'flex-start'}
                          justifyContent={'center'}
                          flex={1}
                          gap={3}
                          p={2}
                          backgroundColor={'white'}
                        >
                          {/* Lado Esquerdo: Inserção de Itens */}
                          <Paper
                            variant="outlined"
                            sx={{ width: { xs: '100%', md: '50%' }, p: 3 }}
                          >
                            <Stack alignItems={'center'} gap={2}>
                              <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%', mb: 1 }}>
                                <BuildIcon fontSize="small" sx={{ color: cores.laranja }} />
                                <Typography variant="subtitle1" fontWeight={600}>Adicionar Serviços</Typography>
                              </Stack>

                              {/* NOVO (deployment Marcki): identifica qual unidade/contato do
                                  cliente final gerou esta proposta — útil para clientes
                                  recorrentes com múltiplas filiais/departamentos que pedem
                                  vários orçamentos ao longo do ano. Aparece também na lista de
                                  Propostas, sem precisar abrir cada uma. */}
                              <TextField
                                fullWidth
                                label="Referência/Unidade do Cliente"
                                placeholder="Ex.: Loja Centro, Filial Sul, Depto. Financeiro"
                                value={proposta.referenciaCliente || ''}
                                onChange={(e) => setProposta((prev) => ({ ...prev, referenciaCliente: e.target.value }))}
                              />

                              <Autocomplete
                                fullWidth
                                id="servico-select"
                                options={servicos}
                                getOptionLabel={(option) => option.nome}
                                value={servicoSelecionado}
                                onChange={(event, newValue) => {
                                  setServicoSelecionado(newValue);
                                  // Pré-preenche com o valor do catálogo como ponto de
                                  // partida — o usuário pode ajustar no campo abaixo.
                                  setValorUnitario(newValue ? (Number(newValue.valor) || 0) : 0);
                                }}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Selecione o Serviço"
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />

                              <Stack direction="row" gap={2} width="100%">
                                <TextField
                                  label={servicoSelecionado?.unidade ? `Quantidade (${servicoSelecionado.unidade})` : 'Quantidade'}
                                  type="number"
                                  value={quantidade}
                                  onChange={(e) => setQuantidade(Number(e.target.value))}
                                  InputProps={{
                                    inputProps: { min: 1 },
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <TuneIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{ width: '50%' }}
                                />
                                <TextField
                                  label="Valor Unitário (R$)"
                                  type="number"
                                  value={valorUnitario}
                                  onChange={(e) => setValorUnitario(Number(e.target.value))}
                                  InputProps={{
                                    inputProps: { min: 0, step: '0.01' },
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{ width: '50%' }}
                                />
                              </Stack>

                              {servicoSelecionado?.tipo === 'recorrente' && (
                                <TextField
                                  fullWidth
                                  label="Duração (nº de meses)"
                                  type="number"
                                  value={duracao}
                                  onChange={(e) => setDuracao(Number(e.target.value))}
                                  InputProps={{
                                    inputProps: { min: 1 },
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <CalendarMonth fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                  helperText={`Cobrança ${servicoSelecionado?.periodicidade || ''} — valor por período × duração`}
                                />
                              )}

                              <Button variant="contained" onClick={handleAdicionarServico} disabled={!servicoSelecionado}>
                                Adicionar à Proposta
                              </Button>
                            </Stack>
                          </Paper>

                          {/* Lado Direito: Itens Adicionados e Total */}
                          <Paper
                            variant="outlined"
                            sx={{ width: { xs: '100%', md: '50%' }, p: 3, minHeight: '250px' }}
                          >
                            <Stack direction="row" alignItems="center" gap={1} mb={1}>
                              <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                              <Typography variant="subtitle1" fontWeight={600}>Serviços na Proposta</Typography>
                            </Stack>
                            <List>
                              {proposta.servicos?.length > 0 ? (
                                proposta.servicos.map((servico, index) => {
                                  const subtotal = valorLinhaServico(servico);
                                  return (
                                    <ListItem
                                      key={index}
                                      disableGutters
                                      secondaryAction={
                                        <IconButton edge="end" aria-label="delete" onClick={() => handleRemoverServico(index)}>
                                          <DeleteIcon />
                                        </IconButton>
                                      }
                                    >
                                      <ListItemText
                                        primary={servico.nome}
                                        secondary={`Qtd: ${servico.quantidade}${servico.unidade ? ` ${servico.unidade}` : ''} | Valor Unit.: R$ ${(Number(servico.valor) || 0).toFixed(2)} | Subtotal: R$ ${subtotal.toFixed(2)}`}
                                      />
                                    </ListItem>
                                  );
                                })
                              ) : (
                                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                                  Nenhum serviço adicionado ainda.
                                </Typography>
                              )}
                            </List>

                            {proposta.servicos?.length > 0 && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                {/* ── Desconto / Acréscimo (serviços) ── */}
                                <Stack direction="row" alignItems="center" gap={1} mb={1}>
                                  <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  <Typography variant="subtitle2" fontWeight={600}>Ajuste de Valor</Typography>
                                </Stack>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                                  <TextField
                                    size="small"
                                    id="desconto-servico"
                                    label={`Desconto (${tipoDesconto})`}
                                    value={proposta.desconto || ''}
                                    inputProps={{ type: 'number' }}
                                    sx={{ width: '55%' }}
                                    onChange={onChangeDesconto}
                                  />
                                  <Stack direction="row" sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                                    <Button size="small" variant={tipoDesconto === '%' ? 'contained' : 'outlined'} onClick={() => setTipoDesconto('%')} sx={{ minWidth: 48, px: 1 }}>%</Button>
                                    <Button size="small" variant={tipoDesconto === 'R$' ? 'contained' : 'outlined'} onClick={() => setTipoDesconto('R$')} sx={{ minWidth: 48, px: 1 }}>R$</Button>
                                  </Stack>
                                </Stack>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                                  <Stack direction="row" alignItems="center" gap={1}>
                                    <Checkbox
                                      size="small"
                                      checked={termoAcrescimoAceito || proposta.acrescimoAceito}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          if (!proposta.acrescimo || proposta.acrescimo <= 0) {
                                            window.alert('Digite o valor do acréscimo antes de habilitar.');
                                            return;
                                          }
                                          setModalTermoAcrescimo(true);
                                        } else {
                                          setTermoAcrescimoAceito(false);
                                          setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                                        }
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                      Habilitar Acréscimo
                                    </Typography>
                                  </Stack>
                                  <TextField
                                    size="small"
                                    id="acrescimo-servico"
                                    label={`Acréscimo (${tipoAcrescimo})`}
                                    value={proposta.acrescimo || ''}
                                    inputProps={{ type: 'number' }}
                                    sx={{ width: '55%' }}
                                    onChange={onChangeAcrescimo}
                                  />
                                  <Stack direction="row" sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                                    <Button size="small" variant={tipoAcrescimo === '%' ? 'contained' : 'outlined'} onClick={() => setTipoAcrescimo('%')} sx={{ minWidth: 48, px: 1 }}>%</Button>
                                    <Button size="small" variant={tipoAcrescimo === 'R$' ? 'contained' : 'outlined'} onClick={() => setTipoAcrescimo('R$')} sx={{ minWidth: 48, px: 1 }}>R$</Button>
                                  </Stack>
                                </Stack>
                                <Divider sx={{ my: 2 }} />
                                <Box
                                  display="grid"
                                  // Define duas colunas: 1ª se ajusta ao conteúdo, 2ª ocupa o espaço restante
                                  gridTemplateColumns="max-content 1fr"
                                  // Espaçamento entre as linhas e colunas
                                  rowGap={1}
                                  columnGap={4}
                                >
                                  {/* Linha 1 */}
                                  <Typography sx={{ textAlign: 'right' }}>Serviços :</Typography>
                                  <Typography>
                                    {subTotalServicos.toLocaleString('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    })}
                                  </Typography>
                                  {valorDescontoServicos > 0 && (
                                    <>
                                      <Typography sx={{ textAlign: 'right' }}>Desconto :</Typography>
                                      <Typography sx={{ color: 'green' }}>
                                        - {valorDescontoServicos.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </Typography>
                                    </>
                                  )}
                                  {valorAcrescimoServicos > 0 && (
                                    <>
                                      <Typography sx={{ textAlign: 'right' }}>Acréscimo :</Typography>
                                      <Typography sx={{ color: '#e65100' }}>
                                        + {valorAcrescimoServicos.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </Typography>
                                    </>
                                  )}
                                  {valorDeslocamento > 0 && (
                                    <>
                                      <Typography sx={{ textAlign: 'right' }}>Deslocamento :</Typography>
                                      <Typography>
                                        {valorDeslocamento.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </Typography>
                                    </>
                                  )}
                                  {/* Linha 2 */}

                                  {/* Linha 3 */}
                                  {valorIndicacao > 0 && (
                                    <>
                                      <Typography sx={{ textAlign: 'right' }}>Indicação :</Typography>
                                      <Typography>
                                        {valorIndicacao.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </Typography>
                                    </>
                                  )}

                                  {/* Linha 4 */}
                                  <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>Total Proposta :</Typography>
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    {valorTotalProposta.toLocaleString('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    })}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Paper>
                        </Stack>
                      ) : // Necessidade --------------
                        // Revisao --------------
                        proposta.etapa.id === 2 ? (
                          <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                                <DescriptionIcon fontSize="small" sx={{ color: cores.laranja }} />
                                <Typography variant="subtitle1" fontWeight={600}>Resumo do Cadastro</Typography>
                              </Stack>
                              <Grid container spacing={1.5}>
                                {[
                                  { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                                  {
                                    label: 'Local Instalação',
                                    value: `${proposta.localinstalacao?.cidade.nome} - ${proposta.localinstalacao?.estado.sigla}`,
                                    Icone: LocationCityIcon,
                                  },
                                  // NOVO (deployment Marcki): só aparece quando preenchido — evita
                                  // linha vazia em propostas antigas ou sem essa referência.
                                  ...(proposta.referenciaCliente
                                    ? [{ label: 'Referência/Unidade', value: proposta.referenciaCliente, Icone: ApartmentIcon }]
                                    : []),
                                ].map((i) => (
                                  <Grid item xs={6} key={i.label}>
                                    <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                      <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                      </Box>
                                    </Stack>
                                  </Grid>
                                ))}
                              </Grid>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 3 }}>
                              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                                <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                                <Typography variant="subtitle1" fontWeight={600}>Resumo dos Serviços</Typography>
                              </Stack>
                              <List>
                                {proposta.servicos?.length > 0 ? (
                                  proposta.servicos.map((servico, index) => {
                                    const subtotal = valorLinhaServico(servico);
                                    return (
                                      <ListItem key={index} disableGutters>
                                        <ListItemText
                                          primary={servico.nome}
                                          secondary={`Qtd: ${servico.quantidade}${servico.unidade ? ` ${servico.unidade}` : ''} | Valor Unit.: R$ ${(Number(servico.valor) || 0).toFixed(2)} | Subtotal: R$ ${subtotal.toFixed(2)}`}
                                        />
                                      </ListItem>
                                    );
                                  })
                                ) : (
                                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                                    Nenhum serviço adicionado ainda.
                                  </Typography>
                                )}
                              </List>

                              {proposta.servicos?.length > 0 && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <Box
                                    display="grid"
                                    // Define duas colunas: 1ª se ajusta ao conteúdo, 2ª ocupa o espaço restante
                                    gridTemplateColumns="max-content 1fr"
                                    // Espaçamento entre as linhas e colunas
                                    rowGap={1}
                                    columnGap={4}
                                  >
                                    {/* Linha 1 */}
                                    <Typography sx={{ textAlign: 'right' }}>Serviços :</Typography>
                                    <Typography>
                                      {subTotalServicos.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>

                                    {/* Linha 2 */}
                                    <Typography sx={{ textAlign: 'right' }}>Deslocamento :</Typography>
                                    <Typography>
                                      {valorDeslocamento.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>

                                    {/* Linha 3 */}
                                    <Typography sx={{ textAlign: 'right' }}>Indicação :</Typography>
                                    <Typography>
                                      {valorIndicacao.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>

                                    {/* Linha 4 */}
                                    <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>Total Proposta :</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                      {valorTotalProposta.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </Paper>
                          </Box>
                        ) : // Revisao --------------
                          // Gerar Proposta --------------
                          proposta.etapa.id === 3 ? (
                            <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                              <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                                <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                                  <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  <Typography variant="subtitle1" fontWeight={600}>Resumo da Proposta</Typography>
                                </Stack>
                                <Grid container spacing={1.5}>
                                  {[
                                    { label: 'Cliente', value: proposta.cliente?.nome, Icone: PersonIcon },
                                    {
                                      label: 'Local Instalação',
                                      value: `${proposta.localinstalacao?.cidade.nome} - ${proposta.localinstalacao?.estado.sigla}`,
                                      Icone: LocationCityIcon,
                                    },
                                    { label: 'Nº de Serviços', value: proposta.servicos?.length || 0, Icone: BuildIcon },
                                    {
                                      label: 'Total Proposta',
                                      value: valorTotalProposta.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
                                      Icone: AttachMoneyIcon,
                                    },
                                  ].map((i) => (
                                    <Grid item xs={6} sm={3} key={i.label}>
                                      <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                        <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                          <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                        </Box>
                                      </Stack>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Paper>

                              <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} gap={4}>
                                <TextField
                                  multiline
                                  rows={6}
                                  id="obs"
                                  label="Observação"
                                  variant="outlined"
                                  value={proposta.obs}
                                  onChange={(e) => {
                                    setProposta({ ...proposta, obs: e.target.value });
                                  }}
                                  sx={{ backgroundColor: 'white', width: '100%' }}
                                />
                                <Button
                                  variant="contained"
                                  onClick={gerarPdfServicos}
                                  endIcon={<DownloadIcon />}
                                  color={propostaGerada ? 'success' : 'primary'}
                                >
                                  Gerar Proposta
                                </Button>
                              </Stack>
                            </Box>
                          ) : // Gerar Proposta --------------
                            // Finalizar Proposta --------------
                            proposta.etapa.id === 4 ? (
                              <Stack direction={'column'} alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                                <TextField
                                  disabled
                                  id="valor"
                                  label="Valor"
                                  variant="outlined"
                                  sx={styleTextField}
                                  value={proposta.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || ''}
                                />

                                <Autocomplete
                                  disablePortal
                                  id="Forma de Pagamento"
                                  value={proposta.formapagamento}
                                  onChange={(event, newValue) => {
                                    setProposta((prev) => ({ ...prev, formapagamento: newValue, parcelas: 1 }));
                                  }}
                                  options={formas}
                                  isOptionEqualToValue={(option, value) => option._id === value._id}
                                  getOptionLabel={(option) => `${option.nome}`}
                                  sx={styleTextField}
                                  renderInput={(params) => <TextField {...params} label="Forma Pagamento" />}
                                />

                                {proposta.formapagamento?.nome === 'Crédito Parcelado' && (
                                  <TextField
                                    disabled={proposta.valor <= 100}
                                    id="parcelas"
                                    label="Parcelas"
                                    variant="outlined"
                                    sx={styleTextField}
                                    inputProps={{ type: 'number' }}
                                    value={proposta.parcelas || ''}
                                    onChange={(e) =>
                                      setProposta((prev) => ({ ...prev, parcelas: e.target.value > 12 ? 12 : e.target.value }))
                                    }
                                  />
                                )}

                                <Autocomplete
                                  disablePortal
                                  id="finalizado"
                                  value={proposta.finalizado || null}
                                  options={finalizado}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  getOptionLabel={(option) => `${option.nome}`}
                                  sx={{ ...styleTextField, marginBottom: 2 }}
                                  renderInput={(params) => <TextField {...params} label="Status" />}
                                  onChange={(event, newValue) => {
                                    setProposta((prevProposta) => ({ ...prevProposta, finalizado: newValue }));
                                  }}
                                />
                                {proposta.finalizado?.nome === 'Rejeitado' && (
                                  <>
                                    <AutoCompleteMedium
                                      disablePortal
                                      id="motivoRejeicao"
                                      name="motivoRejeicao"
                                      value={proposta?.motivoRejeicao || null}
                                      onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                                      options={motivos}
                                      isOptionEqualToValue={(option, value) => option._id === value._id}
                                      getOptionLabel={(option) => `${option.nome}`}
                                      renderInput={(params) => <TextField {...params} placeholder="Motivo" />}
                                    />
                                    <TextField
                                      id="obs"
                                      label="Descrição"
                                      variant="outlined"
                                      value={proposta?.finalizado?.obs || ''}
                                      multiline
                                      rows={4}
                                      onChange={(event, newValue) => {
                                        setProposta((prevProposta) => ({
                                          ...prevProposta,
                                          finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                        }));
                                      }}
                                      sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                                    />
                                  </>
                                )}
                              </Stack>
                            ) : // Finalizar Proposta --------------
                              null
                  }

                  <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
                    <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                      {' '}
                      Cancelar
                    </Button>
                    <Button
                      sx={sxFontBotaoMobile}
                      size={'small'}
                      disabled={proposta.etapa.id === 0}
                      variant="contained"
                      onClick={handleBack}
                      startIcon={<ArrowBackIosNewIcon />}
                    >
                      Anterior
                    </Button>
                    {proposta.etapa.id + 1 === steps.length ? (
                      <Button
                        sx={sxFontBotaoMobile}
                        size={'small'}
                        disabled={
                          modalLoading ||
                          props.visualiza ||
                          (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)
                        }
                        variant="contained"
                        color="success"
                        onClick={handleConfirm}
                        endIcon={<BeenhereOutlinedIcon />}
                      >
                        Finalizar
                      </Button>
                    ) : (
                      <Button
                        sx={sxFontBotaoMobile}
                        size={'small'}
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<ArrowForwardIosIcon />}
                      >
                        Próximo
                      </Button>
                    )}
                    <Button
                      sx={sxFontBotaoMobile}
                      size={'small'}
                      disabled={modalLoading || props.visualiza}
                      variant="outlined"
                      color="success"
                      onClick={handleConfirm}
                      startIcon={<SaveIcon />}
                    >
                      Salvar
                    </Button>
                  </Stack>
                </Box>
              ) : // Modal para Proposta de Serviços -------------------------
                // Modal para Personalizado -------------------------
                // -------------------------------------------------------
                proposta.produto.nome === 'Personalizado' ? (
                  <Box sx={style}>
                    <Stack sx={{ width: '100%', backgroundColor: 'white' }} spacing={4}>
                      <Stepper alternativeLabel activeStep={proposta.etapa?.id} connector={<QontoConnector />}>
                        {steps.map((step) => (
                          <Step key={step.id}>
                            <StepLabel StepIconComponent={QontoStepIcon}>{step.nome}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Stack>
                    {
                      // Cadastro --------------
                      proposta.etapa?.id === 0 ? (
                        <Stack alignItems={'center'} justifyContent={'center'} flexDirection={'row'} flex={1} gap={2}>
                          <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                            <CamposInstalacao
                              campos={['cliente', 'estado', 'cidade', 'disjuntor', 'origem', 'unidade']}
                              proposta={proposta}
                              setProposta={setProposta}
                              rowsClientes={rowsClientes}
                              estados={estados}
                              cidades={cidades}
                              tiposDisjuntor={tipodisjuntor}
                              potenciasDisjuntor={potenciadisjuntor}
                              origens={origem}
                              unidades={unidades}
                              isSameUnidade={isSameUnidade}
                              onAbrirModalCliente={() => setModalAddCliente(true)}
                            />

                            {/* Orientação fica de fora do CamposInstalacao porque, só no Personalizado,
                                fica desabilitada quando o motivo é "Usina de Solo". */}
                            <Autocomplete
                              disablePortal
                              id="orientacaotelhado"
                              value={proposta.orientacaotelhado || null}
                              options={orientacaotelhado}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => `${option.nome}`}
                              sx={styleTextField}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Orientação Telhado"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <ExploreIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                              onChange={(event, newValue) => {
                                setProposta((prevProposta) => ({ ...prevProposta, orientacaotelhado: newValue }));
                              }}
                              disabled={proposta.motivoPersonalizado === 'Usina de Solo'}
                            />
                          </Stack>
                          {proposta?.origem?.nome === 'Indicação' && (
                            <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                              <IconButtonWrapper>
                                <Autocomplete
                                  disablePortal
                                  id="Indicador"
                                  value={proposta.indicador || null}
                                  options={rowsIndicador}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  getOptionLabel={(option) => `${option.nome} - ${option.cpf}`}
                                  key={(option) => option._id}
                                  sx={styleTextField}
                                  renderInput={(params) => <TextField {...params} label="Indicador" />}
                                  onChange={(event, newValue) => {
                                    setProposta((prevProposta) => ({ ...prevProposta, indicador: newValue }));
                                  }}
                                />
                                <IconButton aria-label="add" onClick={() => setModalAddIndicador(true)}>
                                  <AddCircleIcon />
                                </IconButton>
                              </IconButtonWrapper>

                              <Autocomplete
                                disablePortal
                                id="tipoindicacao"
                                value={proposta.tipoindicacao || null}
                                options={tipoindicacao}
                                isOptionEqualToValue={(option, value) => option.nome === value.nome}
                                getOptionLabel={(option) => `${option.nome}`}
                                sx={styleTextField}
                                renderInput={(params) => <TextField {...params} label="Tipo Indicação" />}
                                onChange={(event, newValue) => {
                                  setProposta((prevProposta) => ({ ...prevProposta, tipoindicacao: newValue }));
                                }}
                              />
                              {proposta?.tipoindicacao?.nome === 'Indicador PJ' && (
                                <Autocomplete
                                  disablePortal
                                  id="percentual"
                                  value={proposta.percentualindicacao || null}
                                  options={percentualindicacao}
                                  isOptionEqualToValue={(option, value) => option === value}
                                  getOptionLabel={(option) => `${option}`}
                                  sx={styleTextField}
                                  renderInput={(params) => <TextField {...params} label="Percentual Indicação" />}
                                  onChange={(event, newValue) => {
                                    setProposta((prevProposta) => ({ ...prevProposta, percentualindicacao: newValue }));
                                  }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      ) : // Cadastro --------------
                        // Necessidade --------------
                        proposta.etapa?.id === 1 ? (
                          <Box sx={{ p: 3, width: '100%', maxWidth: 960, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: cores.textoClaro }}>
                              Consumo de Energia
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                              <Typography variant="subtitle1" fontWeight={600} mb={2}>Faturas</Typography>
                              <Stack direction={'row'} flexWrap="wrap" gap={2}>
                                {proposta.faturas.map((fatura, index1) => (
                                  <Paper key={index1} variant="outlined" sx={{ p: 2, minWidth: 220 }}>
                                    <Stack alignItems={'center'} gap={1.5}>
                                      <Button variant="contained" onClick={() => handleAddFatura(index1)} endIcon={<AddCircleIcon />} size="small">
                                        Adicionar Fatura
                                      </Button>
                                      {fatura.map((fat, index2) => (
                                        <Stack key={index2} direction={'row'} alignItems={'center'} gap={0.5} width="100%">
                                          <TextField
                                            size="small"
                                            id="fatura"
                                            label="Valor Fatura"
                                            inputProps={{ type: 'number' }}
                                            sx={{ ...styleTextField2, width: '100%' }}
                                            value={fat.valor}
                                            onChange={(e) => handleChangeFatura(e, index1, index2)}
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                          <IconButton aria-label="remover fatura" size="small" onClick={(e) => removeFatura(e, index1, index2)}>
                                            <RemoveCircleIcon fontSize="small" />
                                          </IconButton>
                                        </Stack>
                                      ))}
                                      {fatura.length === 0 ? (
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          onClick={() => handleRemoveTipoFatura(index1)}
                                          endIcon={<RemoveCircleIcon />}
                                        >
                                          Remover Grupo
                                        </Button>
                                      ) : null}
                                    </Stack>
                                  </Paper>
                                ))}
                                <Stack width={150} alignItems={'center'} justifyContent={'center'}>
                                  <Button
                                    variant="outlined"
                                    onClick={handleAddTipoFatura}
                                    endIcon={<AddCircleIcon />}
                                    sx={{ color: cores.laranja, borderColor: cores.laranja }}
                                  >
                                    Adicionar Grupo
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                              <Typography variant="subtitle1" fontWeight={600} mb={2}>Tarifa e Distribuidora</Typography>
                              <Stack direction={'row'} gap={2} flexWrap="wrap">
                                <TextField
                                  size="small"
                                  id="tarifaEnergia"
                                  label="Tarifa de Energia (R$/kWh)"
                                  inputProps={{ type: 'number', step: '0.01' }}
                                  sx={{ ...styleTextField3, minWidth: 190 }}
                                  value={proposta.tarifaEnergia || ''}
                                  onChange={(e) => {
                                    setProposta((prev) => ({ ...prev, tarifaEnergia: e.target.value }));
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <Autocomplete
                                  disablePortal
                                  id="distribuidora"
                                  value={proposta.distribuidora || null}
                                  options={distribuidoras}
                                  isOptionEqualToValue={(option, value) => option === value}
                                  getOptionLabel={(option) => option}
                                  sx={{ ...styleTextField3, minWidth: 190 }}
                                  size="small"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Distribuidora"
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <BusinessIcon fontSize="small" sx={{ color: cores.laranja }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                  onChange={(event, newValue) => {
                                    setProposta((prev) => ({ ...prev, distribuidora: newValue }));
                                  }}
                                />
                              </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5 }}>
                              <Typography variant="subtitle1" fontWeight={600} mb={2}>Instalação e Consumo</Typography>
                              <Stack direction={'row'} gap={2} flexWrap="wrap">
                                <Autocomplete
                                  size={'small'}
                                  disablePortal
                                  disabled
                                  id="tipoinstalacao"
                                  value={proposta.disjuntorcliente.tipodisjuntor || null}
                                  options={tipodisjuntor}
                                  getOptionLabel={(option) => `${option.nome}`}
                                  isOptionEqualToValue={(option, value) => option._id === value._id}
                                  sx={{ ...styleTextField3, minWidth: 190 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Tipo Instalação"
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <FlashOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />

                                <TextField
                                  disabled
                                  size="small"
                                  id="mediaconsumo"
                                  label="Média Consumo"
                                  inputProps={{ type: 'number' }}
                                  sx={{ ...styleTextField3, minWidth: 170 }}
                                  value={proposta.mediaconsumo}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SpeedIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <TextField
                                  disabled
                                  size="small"
                                  id="mediaconsumoabatida"
                                  label="Média Consumo Abatida"
                                  inputProps={{ type: 'number' }}
                                  sx={{ ...styleTextField3, minWidth: 170 }}
                                  value={proposta.mediaconsumoabatida}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SpeedIcon fontSize="small" sx={{ color: cores.laranja }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Stack>
                            </Paper>
                                {erro?.tipo === 2 && (
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      mt: 2,
                                      borderColor: 'warning.main',
                                      bgcolor: '#fff8e1',
                                    }}
                                  >
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                      <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />
                                      <Box>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                          Kit disponível insuficiente
                                        </Typography>
                                        <Typography variant="body2">{erro.msg}</Typography>
                                      </Box>
                                    </Stack>
                                  </Paper>
                                )}

                          </Box>
                        ) : // Necessidade --------------
                          // Revisao --------------
                          proposta.etapa?.id === 2 ? (
                            <Box sx={{ p: 3, width: '100%', maxWidth: 720, mx: 'auto', overflowY: 'auto', flex: 1 }}>
                              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Módulo</Typography>
                                <Autocomplete
                                  disablePortal
                                  id="modulo"
                                  value={proposta.modulo}
                                  options={modulos}
                                  getOptionLabel={(option) => `Módulo ${option.potencia} ${option.marca}`}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  sx={styleTextField}
                                  size={'small'}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Módulo"
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <GridOnIcon fontSize="small" sx={{ color: cores.laranja }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                  onChange={(option, value) => setProposta({ ...proposta, modulo: value })}
                                />
                              </Paper>

                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" alignItems="center" gap={1} mb={1}>
                                  <DescriptionIcon fontSize="small" sx={{ color: cores.laranja }} />
                                  <Typography variant="subtitle1" fontWeight={600}>Resumo do Cadastro</Typography>
                                </Stack>
                                <Grid container spacing={1.5}>
                                  {[
                                    { label: 'Cliente', value: proposta.cliente.nome, Icone: PersonIcon },
                                    {
                                      label: 'Local Instalação',
                                      value: `${proposta.localinstalacao.cidade.nome} - ${proposta.localinstalacao.estado.sigla}`,
                                      Icone: LocationCityIcon,
                                    },
                                    { label: 'Média Consumo', value: proposta.mediaconsumoabatida, Icone: SpeedIcon },
                                    { label: 'Orientação Telhado', value: proposta.orientacaotelhado.nome, Icone: ExploreIcon },
                                    { label: 'Tipo Disjuntor', value: proposta.disjuntorcliente.tipodisjuntor.nome, Icone: FlashOnIcon },
                                    { label: 'Potência Disjuntor', value: proposta.disjuntorcliente.potenciadisjuntor.valor, Icone: FlashOnIcon },
                                  ].map((i) => (
                                    <Grid item xs={6} sm={4} key={i.label}>
                                      <Stack direction="row" alignItems="flex-start" gap={0.75}>
                                        <i.Icone fontSize="small" sx={{ color: cores.laranja, mt: '2px' }} />
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">{i.label}</Typography>
                                          <Typography variant="body2" fontWeight={600}>{i.value || '–'}</Typography>
                                        </Box>
                                      </Stack>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Paper>
                            </Box>
                          ) : // Revisao --------------
                            // Gerar Proposta --------------
                            proposta.etapa?.id === 3 ? (
                              <Box sx={{ p: 3, width: '100%', overflowY: 'auto', flex: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={5}>
                                    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                      <Stack direction="row" alignItems="center" gap={1} mb={2}>
                                        <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                                        <Typography variant="subtitle1" fontWeight={600}>Dados do Kit</Typography>
                                      </Stack>
                                      <Stack gap={2}>
                                        <TextField
                                          id="valor"
                                          label="Valor"
                                          inputProps={{ type: 'number' }}
                                          sx={styleTextField2}
                                          value={proposta.valor || ''}
                                          onChange={(event) => {
                                            setProposta((prevProposta) => ({ ...prevProposta, valor: event.target.value }));
                                          }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                                              </InputAdornment>
                                            ),
                                          }}
                                        />

                                        <TextField
                                          id="potenciacc"
                                          label="Potencia Kit kWp"
                                          inputProps={{ type: 'number' }}
                                          sx={styleTextField2}
                                          value={proposta.kit?.potenciacc || ''}
                                          onChange={(event) => {
                                            setProposta((prevProposta) => ({
                                              ...prevProposta,
                                              kit: { potenciacc: parseFloat(event.target.value) },
                                            }));
                                          }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <PowerIcon fontSize="small" sx={{ color: cores.laranja }} />
                                              </InputAdornment>
                                            ),
                                          }}
                                        />

                                        <Button
                                          onClick={() => setModalPersonalizado2(true)}
                                          variant="outlined"
                                          startIcon={<VisibilityIcon />}
                                          sx={{ color: cores.laranja, borderColor: cores.laranja, mt: 1 }}
                                        >
                                          Ver Espeficações
                                        </Button>

                                        {['Agendamento', 'Lead', 'Indicação'].includes(proposta.origem.nome) ? (
                                          <Autocomplete
                                            disablePortal
                                            id="finalizado"
                                            value={proposta.user || null}
                                            options={usuarios}
                                            isOptionEqualToValue={(option, value) => option._id === value._id}
                                            getOptionLabel={(option) => `${option.nome}`}
                                            sx={{ ...styleTextField2, marginBottom: 2 }}
                                            renderInput={(params) => <TextField {...params} label="Vendedor Externo" />}
                                            onChange={(event, newValue) => {
                                              setProposta((prevProposta) => ({ ...prevProposta, user: newValue, user2: prevProposta.user }));
                                            }}
                                          />
                                        ) : null}
                                      </Stack>
                                    </Paper>
                                  </Grid>

                                  <Grid item xs={12} md={7}>
                                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                          <AttachFileIcon fontSize="small" sx={{ color: cores.laranja }} />
                                          <Typography variant="subtitle1" fontWeight={600}>Anexos</Typography>
                                        </Stack>
                                        <Button
                                          onClick={addAnexo}
                                          variant="outlined"
                                          size="small"
                                          startIcon={<AddCircleIcon />}
                                          sx={{ color: cores.laranja, borderColor: cores.laranja }}
                                        >
                                          Adicionar Anexo
                                        </Button>
                                      </Stack>
                                      <Stack gap={1}>
                                        {anexo?.length ? anexo.map((anexod, index) => (
                                          <Stack
                                            key={index}
                                            direction="row"
                                            alignItems="center"
                                            gap={1}
                                            onClick={() => downloadAnexo(anexod)}
                                            sx={{
                                              p: 1, borderRadius: 1, border: '1px solid #e0e0e0',
                                              '&:hover': { backgroundColor: '#FFF3E0', cursor: 'pointer' },
                                            }}
                                          >
                                            <InsertDriveFileIcon fontSize="small" sx={{ color: cores.laranja }} />
                                            <Typography variant="body2">
                                              {`${anexod.nome}.${anexod.ext}`}
                                            </Typography>
                                          </Stack>
                                        )) : (
                                          <Typography variant="body2" color="text.secondary">Nenhum anexo adicionado.</Typography>
                                        )}
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                </Grid>
                              </Box>
                            ) : // Gerar Proposta --------------
                              // Finalizar Proposta --------------
                              proposta.etapa?.id === 4 ? (
                                <Stack alignItems={'center'} justifyContent={'center'} flex={1} gap={2}>
                                  {proposta.user2 ? (
                                    <Autocomplete
                                      disablePortal
                                      id="vendedor abertura"
                                      disabled
                                      value={proposta.user2 || null}
                                      options={usuarios}
                                      isOptionEqualToValue={(option, value) => option._id === value._id}
                                      getOptionLabel={(option) => `${option.nome}`}
                                      sx={{ ...styleTextField, marginBottom: 2 }}
                                      renderInput={(params) => <TextField {...params} label="Vendedor Abertura" />}
                                    />
                                  ) : null}

                                  <Autocomplete
                                    disablePortal
                                    id="finalizado"
                                    value={proposta.finalizado || null}
                                    options={finalizado}
                                    isOptionEqualToValue={(option, value) => option.nome === value.nome}
                                    getOptionLabel={(option) => `${option.nome}`}
                                    sx={{ ...styleTextField, marginBottom: 2 }}
                                    renderInput={(params) => <TextField {...params} label="Selecione" />}
                                    onChange={(event, newValue) => {
                                      setProposta((prevProposta) => ({ ...prevProposta, finalizado: newValue }));
                                    }}
                                  />
                                  {proposta.finalizado?.nome === 'Rejeitado' && (
                                    <>
                                      <AutoCompleteMedium
                                        disablePortal
                                        id="motivoRejeicao"
                                        name="motivoRejeicao"
                                        value={proposta?.motivoRejeicao || null}
                                        onChange={(_, value) => handleAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value)}
                                        options={motivos}
                                        isOptionEqualToValue={(option, value) => option._id === value._id}
                                        getOptionLabel={(option) => `${option.nome}`}
                                        renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                                      />
                                      <TextField
                                        id="obs"
                                        label="Descrição"
                                        variant="outlined"
                                        value={proposta?.finalizado?.obs || ''}
                                        multiline
                                        rows={4}
                                        onChange={(event, newValue) => {
                                          setProposta((prevProposta) => ({
                                            ...prevProposta,
                                            finalizado: { ...prevProposta?.finalizado, obs: event.target.value },
                                          }));
                                        }}
                                        sx={{ width: '50%', backgroundColor: 'white', mt: 1 }}
                                      />
                                    </>
                                  )}
                                </Stack>
                              ) : // Finalizar Proposta --------------
                                null
                    }

                    <Stack direction={'row'} gap={2} justifyContent={'space-between'} sx={{ bottom: 0, left: 0, right: 0, p: 2 }}>
                      <Button sx={sxFontBotaoMobile} size={'small'} variant="outlined" onClick={() => onClose()} color="error">
                        {' '}
                        Cancelar
                      </Button>
                      <Button
                        sx={sxFontBotaoMobile}
                        size={'small'}
                        disabled={proposta.etapa.id === 0}
                        variant="contained"
                        onClick={handleBack}
                        startIcon={<ArrowBackIosNewIcon />}
                      >
                        Anterior
                      </Button>
                      {proposta.etapa.id + 1 === steps.length ? (
                        <Button
                          sx={sxFontBotaoMobile}
                          size={'small'}
                          disabled={
                            modalLoading ||
                            props.visualiza ||
                            (proposta.finalizado?.nome === 'Rejeitado' && !proposta.finalizado?.obs)
                          }
                          variant="contained"
                          color="success"
                          onClick={handleConfirm}
                          endIcon={<BeenhereOutlinedIcon />}
                        >
                          Finalizar
                        </Button>
                      ) : (
                        <Button
                          sx={sxFontBotaoMobile}
                          size={'small'}
                          variant="contained"
                          onClick={handleNext}
                          endIcon={<ArrowForwardIosIcon />}
                        >
                          Próximo
                        </Button>
                      )}
                      <Button
                        sx={sxFontBotaoMobile}
                        size={'small'}
                        disabled={modalLoading || props.visualiza}
                        variant="outlined"
                        color="success"
                        onClick={handleConfirm}
                        startIcon={<SaveIcon />}
                      >
                        Salvar
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  // -------------------------------------------------------
                  // Modal Solo -------------------------
                  <Box>Erro</Box>
                )}
      {/* Modal Termo de Ciência — Acréscimo */}
      {modalTermoAcrescimo && (
        <>
          <Box
            sx={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998,
            }}
          />
          <Box
            sx={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white', borderRadius: 2, p: 3,
              width: 500, maxWidth: '90vw', zIndex: 9999,
              boxShadow: 24,
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} mb={2}>
              <WarningAmberIcon sx={{ color: '#e65100', fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                Termo de Ciência
              </Typography>
            </Stack>
            <Box sx={{ backgroundColor: '#fff3e0', border: '1.5px solid #e65100', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }} gutterBottom>
                ATENÇÃO — Acréscimo aplicado ao valor do kit
              </Typography>
              <Typography variant="body2" gutterBottom>
                Você está aplicando um acréscimo de <strong>{proposta.acrescimo} {tipoAcrescimo}</strong> sobre
                o valor do kit, elevando o valor de venda para{' '}
                <strong>{parseFloat(proposta.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>.
              </Typography>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                Estou ciente de que o aumento inadequado do valor de venda deste orçamento pode
                ocasionar na perda do negócio, podendo afetar meus resultados e os resultados da empresa.
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Em caso de recusa, a proposta seguirá com o valor original sem acréscimo.
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setModalTermoAcrescimo(false);
                  setTermoAcrescimoAceito(false);
                  setProposta((prev) => ({ ...prev, acrescimo: 0, acrescimoAceito: false }));
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setTermoAcrescimoAceito(true);
                  setProposta((prev) => ({ ...prev, acrescimoAceito: true }));
                  setModalTermoAcrescimo(false);
                  window.alert('Acréscimo habilitado! Por favor, salve a proposta antes de continuar.');
                }}
                sx={{ backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}
              >
                Estou ciente, prosseguir
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {/* Modal Atividade */}
      {
        modalAddCliente && (
          <ModalAddCliente produto={proposta.cliente ? proposta.cliente : null} open={modalAddCliente} onClose={closeModalAddCliente} onConfirm={confirmCliente} />
        )
      }


      {/* Modal Atividade */}
      {modalAddIndicador && (
        <ModalAddIndicador
          open={modalAddIndicador}
          onClose={() => setModalAddIndicador(false)}
          onConfirm={confirmIndicador}
        />
      )}

      {/* Cadastro contextual de kit a partir do dimensionamento */}
      {tipoKitContextual === 'ongrid' && (
        <AddKit
          open={modalKitContextual}
          produto={null}
          contextoDimensionamento={contextoKitContextual}
          onClose={fecharCadastroKitContextual}
          handleClose={fecharCadastroKitContextual}
          onConfirm={confirmarKitContextual}
        />
      )}
      {tipoKitContextual === 'bombeamento' && (
        <AddKitBombeamento
          open={modalKitContextual}
          produto={null}
          contextoDimensionamento={contextoKitContextual}
          onClose={fecharCadastroKitContextual}
          onConfirm={confirmarKitContextual}
        />
      )}
      {tipoKitContextual === 'offgrid' && (
        <AddKitOffGrid
          open={modalKitContextual}
          produto={null}
          contextoDimensionamento={contextoKitContextual}
          onClose={fecharCadastroKitContextual}
          onConfirm={async () => {
            fecharCadastroKitContextual();
            await recarregarKitsOffGrid();
          }}
        />
      )}

      {/* Modal Mensagem */}
      <ModalMensagem open={modalMensagem} onClose={closeModalMensagem} mensagem={mensagem} destino={destino} />

      {/* Modal Loading */}
      {modalLoading && <ModalLoading open={modalLoading} />}
      {modalAtividade && (
        <ModalAtividadade open={modalAtividade} onClose={closeModalAtividade} onConfirm={handleSalvarAtividade} />
      )}
      {modalAddAgenda && (
        <ModalAddAgenda
          open={modalAddAgenda}
          onClose={() => setModalAddAgenda(false)}
          proposta={proposta}
          onConfirmAgenda={handleAddAgenda}
        />
      )}
      {modalPersonalizado && (
        <ModalPropostaPersonalizada
          open={modalPersonalizado}
          onClose={() => setModalPersonalizado(false)}
          proposta={proposta}
          onConfirm={confirmModalPersonalizado}
          onAutoCompleteChange={handleAutoCompleteChange}
        />
      )}
      {modalPersonalizado2 && (
        <ModalPropostaPersonalizada
          open={modalPersonalizado2}
          onClose={() => setModalPersonalizado2(false)}
          proposta={proposta}
          onConfirm={() => setModalPersonalizado2(false)}
          onAutoCompleteChange={handleAutoCompleteChange}
        />
      )}
    </ThemeProvider>
  );
});

EditProposta.propTypes = {
  produto: PropTypes.object,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
};

export default EditProposta;
